class SearchSpringCommon {
    static CART_SVG = `
        <svg
            focusable="false"
            width="24"
            height="24"
            class="icon icon--quick-buy-tote-bag"
            fill="none"
            viewBox="0 0 24 24"
        >
            <path d="M14 20H4L5 9H19L19.4545 14" stroke="currentColor" stroke-width="1.7"></path>
            <path d="M20 16V24M16 20H24" stroke="currentColor" stroke-width="1.7"></path>
            <path d="M15 6V6C15 4.34315 13.6569 3 12 3V3C10.3431 3 9 4.34315 9 6V6" stroke="currentColor" stroke-width="1.7"></path>
        </svg>`;
        
        
    static PRODUCT_IMAGE_ASPECT_RATIO = 0.6671428571428571;

    static marketToPriceFieldMap = {
        'gb': 'ss_price',
        'ie': 'ie_price',
        'nether': 'nl_price',
        'nl': 'nl_price',
        'spain': 'es_price',
        'france': 'fr_price',
        'italy': 'it_price',
        'portugal': 'pt_price',
        'germany': 'de_price',
        'austria': 'at_price',
        'denmark': 'dk_price',
        'belgium': 'be_price',
        'finland': 'fi_price',
        'isle-of-man': 'im_price',
        'poland': 'pl_price'
    };

    static marketToCountryNameMap = {
        'gb': 'united_kingdom',
        'ie': 'ireland',
        'nether': 'netherlands',
        'spain': 'spain',
        'france': 'france',
        'italy': 'italy',
        'portugal': 'portugal',
        'germany': 'germany',
        'austria': 'austria',
        'denmark': 'denmark',
        'belgium': 'belgium',
        'finland': 'finland',
        'isle-of-man': 'isle_of_man',
        'poland': 'poland'
    };

    static marketToCodeMap = {
        'austria': 'at',
        'belgium': 'be',
        'denmark': 'dk',
        'finland': 'fi',
        'france': 'fr',
        'germany': 'de',
        'hong-kong': 'hk',
        'ie': 'ie',
        'italy': 'it',
        'nether': 'nl',
        'poland': 'pl',
        'portugal': 'pt',
        'singapore': 'sg',
        'spain': 'es',
        'sweden': 'se',
    };

    static getCountryCodeFromMarket(market) {
        return this.marketToCodeMap[market] ?? null;
    }

    static getSearchSpringMarketFilter() {
        if (!window.themeVariables?.settings?.searchSpringIncludeMarketFilter) return null;

        const currentMarket = window.themeVariables?.settings?.currentMarket || 'gb';
        if (!currentMarket) return null;

        const countryCode = SearchSpringCommon.getCountryCodeFromMarket(currentMarket);
        if (!countryCode) return null;

        return `${countryCode}_included`;
    }

    static getCountryNameFromMarket(market) {
        return SearchSpringCommon.marketToCountryNameMap[market] || null;
    }

    static getPriceFieldForMarket(market) {
        return SearchSpringCommon.marketToPriceFieldMap[market] || 'ss_price';
    }

    static getPublishedBgFilter() {
        const name = window.themeVariables?.settings?.searchSpringCustomFilterName;
        const value = window.themeVariables?.settings?.searchSpringCustomFilterValue;
        if (!name || !value) return null;
        return { name, value };
    }

    static toNumericPrice(value) {
        if (value == null || value === '') return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        const s = String(value).trim().replace(/,/g, '');
        if (!s) return 0;
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
        return this.marketToCountryNameMap[market] ?? null;
    }

    static getCookie(name) {
        const nameEQ = name + '=';
        for (let c of document.cookie.split(';')) {
            c = c.trimStart();
            if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
        }
        return null;
    }

    static async fetchShopifyPricesForHandles(handles) {
        const priceMap = new Map();
        if (!handles?.length) return priceMap;

        const token = window.themeVariables?.settings?.storefrontAccessToken;
        if (token) {
            try {
                return await SearchSpringCommon.fetchShopifyPricesBatched(handles, token);
            } catch (e) {
                console.warn('[SS] Batched price fetch failed, falling back to per-product requests', e);
            }
        }

        return SearchSpringCommon.fetchShopifyPricesLegacy(handles);
    }

    static async fetchShopifyPricesBatched(handles, token) {
        const priceMap = new Map();
        const root = window.themeVariables?.routes?.rootUrlWithoutSlash ?? '';
        const country = (window.themeVariables?.settings?.currentCountryCode ?? 'gb').toUpperCase();

        const handleQuery = handles
            .map((handle, i) => `p${i}: product(handle: ${JSON.stringify(handle)}) {
                priceRange {
                  minVariantPrice { amount }
                  maxVariantPrice { amount }
                }
                compareAtPriceRange {
                  minVariantPrice { amount }
                  maxVariantPrice { amount }
                }
            }`)
            .join('\n ');

        const priceResponse = await fetch(`${root}/api/2026-04/graphql.json`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({ query: `query @inContext(country: ${country}) { ${handleQuery} }` }),
        });

        if (!priceResponse.ok) throw new Error(`Storefront API ${priceResponse.status}`);
        const priceJSON = await priceResponse.json();
        if (priceJSON.errors?.length) throw new Error(priceJSON.errors[0].message);

        const toCents = (str) => {
            if (str == null) return null;
            return Math.round(parseFloat(str || '0') * 100);
        };

        handles.forEach((handle, i) => {
            const productIndex = priceJSON.data?.[`p${i}`];
            if (!productIndex) return;
            const priceMin = toCents(productIndex.priceRange.minVariantPrice.amount);
            const priceMax = toCents(productIndex.priceRange.maxVariantPrice.amount);
            const compareMin = toCents(productIndex.compareAtPriceRange.minVariantPrice.amount);
            const compareMax = toCents(productIndex.compareAtPriceRange.maxVariantPrice.amount);
            priceMap.set(handle, {
                price: priceMin,
                price_min: priceMin,
                price_max: priceMax,
                compare_at_price: compareMin,
                compare_at_price_min: compareMin,
                compare_at_price_max: compareMax,
                price_varies: priceMin !== priceMax,
            });
        });

        return priceMap;
    }

    static async fetchShopifyPricesLegacy(handles) {
        const root = window.themeVariables?.routes?.rootUrlWithoutSlash ?? '';
        const priceMap = new Map();
        const cacheBust = `?_=${Date.now()}`;

        const results = await Promise.allSettled(
            handles.map((handle) =>
                fetch(`${root}/products/${encodeURIComponent(handle)}.js${cacheBust}`).then((r) => (r.ok ? r.json() : null))
            )
        );

        handles.forEach((handle, i) => {
            const res = results[i];
            if (res?.status === 'fulfilled' && res.value) {
                const p = res.value;
                priceMap.set(handle, {
                    price: p.price ?? p.price_min ?? 0,
                    price_min: p.price_min ?? p.price ?? 0,
                    price_max: p.price_max ?? p.price ?? 0,
                    compare_at_price: p.compare_at_price ?? p.compare_at_price_min ?? 0,
                    compare_at_price_min: p.compare_at_price_min ?? 0,
                    compare_at_price_max: p.compare_at_price_max ?? 0,
                    price_varies: p.price_varies ?? false,
                });
            }
        });

        return priceMap;
    }

    static formatPrice(val, isFromStorefront) {
        const cents = isFromStorefront
            ? Math.round(parseFloat(val) || 0)
            : Math.round((parseFloat(val) || 0) * 100);

        const moneyFormat = window.themeVariables?.settings?.currencyCodeEnabled
            ? window.themeVariables?.settings?.moneyWithCurrencyFormat
            : window.themeVariables?.settings?.moneyFormat;

        if (typeof window.formatMoney === 'function' && moneyFormat) {
            return window.formatMoney(cents, moneyFormat);
        }
        if (typeof window.formatPriceWithCurrency === 'function') {
            return window.formatPriceWithCurrency(val);
        }
        return String(val ?? '');
    }

    static isDevMode() {
        return window.Shopify?.theme?.role !== 'main';
    }

    static generateUUID() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    static setCookie(name, value, days) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/';
    }

    static deleteCookie(name) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }

    static getSessionId() {
        return SearchSpringCommon.getCookie('ssSessionId') || '';
    }

    static buildProductUrl(url, handle) {
        const root = window.themeVariables?.routes?.rootUrlWithoutSlash ?? '';
        if (!url) return `${window.Shopify?.routes?.root || '/'}products/${handle}`;
        if (url.startsWith('http')) return url;
        if (root && !url.startsWith(root)) return root + (url.startsWith('/') ? url : '/' + url);
        return url;
    }

    static resolvePricing(product, shopifyPrice, countryCode = null) {
        let price, compare_at_price, price_varies, price_min;
        if (shopifyPrice) {
            price = shopifyPrice.price;
            compare_at_price = shopifyPrice.compare_at_price || shopifyPrice.compare_at_price_min || 0;
            price_varies = shopifyPrice.price_varies;
            price_min = shopifyPrice.price_min;
        } else {
            price = product.price || 0;
            if (countryCode && countryCode !== 'en' && product[`${countryCode}_price`]) {
                price = product[`${countryCode}_price`];
            }
            compare_at_price = product.msrp || 0;
            if (countryCode && countryCode !== 'en' && product[`${countryCode}_compare_at_price`]) {
                compare_at_price = product[`${countryCode}_compare_at_price`];
            }
            price_varies = product.price_varies ?? false;
            price_min = product.price_min ?? price;
        }
        return { price, compare_at_price, price_varies, price_min, fromStorefront: !!shopifyPrice };
    }

    static buildPriceHTML(pricing, t, { usePlaceholder = false } = {}) {
        const { price, compare_at_price, price_varies, price_min, fromStorefront } = pricing;
        if (usePlaceholder) {
            return '<span class="price price--placeholder" aria-hidden="true">&nbsp;</span>';
        }
        const priceFloat = parseFloat(price) || 0;
        const compareFloat = parseFloat(compare_at_price) || 0;
        const isOnSale = compareFloat > 0 && compareFloat > priceFloat;
        if (price_varies && price_min != null) {
            const minFormatted = SearchSpringCommon.formatPrice(price_min, fromStorefront);
            const fromStr = t.fromPriceHtml || 'From PRICE_PLACEHOLDER';
            return `<span class="price">${fromStr.replace('PRICE_PLACEHOLDER', minFormatted)}</span>`;
        }
        const formattedPrice = SearchSpringCommon.formatPrice(price, fromStorefront);
        const formattedCompare = compare_at_price ? SearchSpringCommon.formatPrice(compare_at_price, fromStorefront) : '';
        const saleSpan = `<span class="price price--highlight">${formattedPrice}</span>`;
        const regularSpan = `<span class="price">${formattedPrice}</span>`;
        const compareSpan = `<span class="price price--compare">${formattedCompare}</span>`;
        return isOnSale ? `${saleSpan} ${compareSpan}` : regularSpan;
    }

    static buildProductLabelHTML(tags, available, pricing, t) {
        const { price, compare_at_price, fromStorefront } = pricing;
        const priceFloat = parseFloat(price) || 0;
        const compareFloat = parseFloat(compare_at_price) || 0;
        const isOnSale = compareFloat > 0 && compareFloat > priceFloat;
        return [
            ...(tags || [])
                .filter(tag => tag.includes('__label'))
                .map(tag => {
                    const isSecond = tag.includes('__label2');
                    let labelText = tag.split(':').pop();
                    if (t.labels?.[labelText]) labelText = t.labels[labelText];
                    return `<span class="label label--custom${isSecond ? '2' : ''}">${labelText}</span>`;
                }),
            !available ? `<span class="label label--subdued">${t.soldOut || 'Sold Out'}</span>` : '',
            isOnSale ? (() => {
                const mode = window.themeVariables?.settings?.discountMode;
                let savings;
                if (mode === 'percentage') {
                    savings = `${Math.round((compareFloat - priceFloat) / compareFloat * 100)}%`;
                } else {
                    savings = SearchSpringCommon.formatPrice(compareFloat - priceFloat, fromStorefront);
                }
                const text = t.discountHtml ? t.discountHtml.replace('{{ savings }}', savings) : savings;
                return `<span class="label label--highlight">${text}</span>`;
            })() : ''
        ].join('');
    }

    static buildCtaHTML({ available, variants, productId, drawerId, popoverId, t }) {
        const firstAvailable = variants.length === 1 ? variants[0] : variants.find(v => v.available);
        const variantId = firstAvailable ? firstAvailable.id || firstAvailable.sku : productId;
        if (available && variants.length === 1 && firstAvailable) {
            return `
        <form method="post" action="/cart/add" is="product-form" class="product-item__quick-form">
          <input type="hidden" name="quantity" value="1">
          <input type="hidden" name="id" value="${variantId}">
          <button is="loader-button" type="submit" class="button button--outline button--text button--full hidden-touch">${t.addToCart || 'Add to cart'}</button>
          <button type="submit" class="product-item__quick-buy-button hidden-no-touch">
            <span class="visually-hidden">${t.addToCart || 'Add to cart'}</span>
            ${SearchSpringCommon.CART_SVG}
          </button>
        </form>`;
        }
        return `
        <div class="product-item__quick-form">
          <button type="button" is="toggle-button" loader data-ss-beacon="quick-view" aria-controls="${drawerId}" aria-expanded="false" class="button button--outline button--text button--full hidden-touch hidden-phone">${t.quickView || 'Quick view'}</button>
          <button type="button" is="toggle-button" loader data-ss-beacon="quick-view" aria-controls="${drawerId}" aria-expanded="false" class="product-item__quick-buy-button hidden-no-touch hidden-phone">
            <span class="visually-hidden">${t.quickView || 'Quick view'}</span>
            ${SearchSpringCommon.CART_SVG}
          </button>
          <button type="button" is="toggle-button" loader data-ss-beacon="quick-view" aria-controls="${popoverId}" aria-expanded="false" class="product-item__quick-buy-button hidden-tablet-and-up">
            <span class="visually-hidden">${t.quickView || 'Quick view'}</span>
            ${SearchSpringCommon.CART_SVG}
          </button>
        </div>`;
    }

    // Feed uses language-based fields (nl_title, fr_title, etc.), not country-based (be_title)
    static getLocalizedTitle(item, fallbackTitle, language) {
        const langCode = (language || '').split('-')[0];
        if (langCode && langCode !== 'en' && item?.[`${langCode}_title`]) {
            return item[`${langCode}_title`];
        }
        return fallbackTitle;
    }

    // Decode HTML entities/encoding issues and optionally strip a trailing
    // " - colour" segment from a product title, per the disable_colours_in_title setting
    static getDisplayTitle(title) {
        let displayTitle = decodeHtmlEntities(title || '');

        if (window.themeVariables?.settings?.disable_colours_in_title && displayTitle) {
            const parts = displayTitle.split(/\s+[-–—]\s+/);
            displayTitle = (parts[0] || displayTitle).trim();
        }

        return displayTitle;
    }

    static PLP_IMAGE_WIDTHS = [300, 400, 500, 600, 700, 800];
    static PLP_IMAGE_SIZES = '(max-width: 740px) calc(50vw - 24px), (max-width: 1199px) calc(33vw - 32px), calc((min(100vw - 80px, 1520px) - 305px) / 4 - 18px)';
    static REC_IMAGE_SIZES = '(max-width: 740px) 75vw, min(25vw, 380px)';
    static PREDICTIVE_IMAGE_WIDTHS = [80, 114, 160, 228];
    static SHOPIFY_SIZE_SUFFIX_RE = /_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*)(?=\.(?:jpe?g|png|gif|webp|heic)$)/i;

    static getFullImageUrl(product) {
        if (!product) return '';
        const images = product.images;
        let fromImages = '';
        if (typeof images === 'string' && images) {
            fromImages = images.split('|')[0] || '';
        } else if (Array.isArray(images) && images.length) {
            fromImages = images[0] || '';
        }
        return product.imageUrl || product.image || fromImages || '';
    }

    static shopifyCdnUrl(src, width) {
        if (!src) return '';
        try {
            const url = new URL(src, window.location.origin);
            url.pathname = url.pathname.replace(SearchSpringCommon.SHOPIFY_SIZE_SUFFIX_RE, '');
            url.searchParams.delete('width');
            url.searchParams.delete('height');
            url.searchParams.delete('crop');
            if (width) url.searchParams.set('width', String(width));
            return url.href;
        } catch {
            return src;
        }
    }

    static buildImageSrcset(src, widths = SearchSpringCommon.PLP_IMAGE_WIDTHS) {
        if (!src) return '';
        return widths.map((w) => `${SearchSpringCommon.shopifyCdnUrl(src, w)} ${w}w`).join(', ');
    }

    static getPlpImageSizes(container) {
        const fromDom = container?.dataset?.imageSizes;
        return (fromDom && fromDom.trim()) || SearchSpringCommon.PLP_IMAGE_SIZES;
    }

    static buildResponsiveImageHTML({
        src,
        alt = '',
        className = '',
        lazyload = true,
        sizes = SearchSpringCommon.PLP_IMAGE_SIZES,
        widths = SearchSpringCommon.PLP_IMAGE_WIDTHS
    } = {}) {
        if (!src) return '';
        const fallbackWidth = widths.includes(600) ? 600 : widths[Math.floor(widths.length / 2)];
        const srcAttr = SearchSpringCommon.shopifyCdnUrl(src, fallbackWidth);
        const srcset = SearchSpringCommon.buildImageSrcset(src, widths);
        const loading = lazyload ? ' loading="lazy"' : '';
        const width = fallbackWidth;
        const height = Math.round(width / SearchSpringCommon.PRODUCT_IMAGE_ASPECT_RATIO);
        const escapedAlt = String(alt).replace(/"/g, '&quot;');
        return `<img class="${className}" src="${srcAttr}" srcset="${srcset}" sizes="${sizes}" alt="${escapedAlt}" width="${width}" height="${height}"${loading}>`;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Configuration and Cookie/API Utility Functions
// ─────────────────────────────────────────────────────────────────────────────
function getSearchSpringParams(siteId) {
    return {
        siteId: siteId,
        userId: SearchSpringCommon.getCookie('ssUserId'),
        sessionId: SearchSpringCommon.getSessionId(),
        shopper: SearchSpringCommon.getCookie('ssShopperId') || '',
        cart: SearchSpringCommon.getCookie('ssCartProducts') || '',
        lastViewed: SearchSpringCommon.getCookie('ssViewedProducts') || ''
    };
}



// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

function isPriceFacetField(field) {
    return field && typeof field === 'string' && field.endsWith('_price');
}

/**
 * Fix encoding issues with special characters
 * Handles cases where UTF-8 characters are misinterpreted as Latin-1
 * Example: "–" (en dash) appearing as "â" due to encoding issues
 * Only applies fixes if corruption patterns are detected
 */
function decodeHtmlEntities(text) {
    if (!text || typeof text !== 'string') return text || '';
    
    try {
        // First decode HTML entities if present
        let decoded = text;
        if (typeof document !== 'undefined' && document.createElement) {
            try {
                const textarea = document.createElement('textarea');
                if (textarea) {
                    textarea.innerHTML = text;
                    decoded = textarea.value || text;
                }
            } catch (e) {
                console.warn('SearchSpring: textarea decode failed', e);
                decoded = text;
            }
        }
        
        // Try to fix UTF-8 misinterpretation using TextDecoder
        // Convert string to bytes, then decode as UTF-8
        try {
            const bytes = new Uint8Array(decoded.split('').map(c => c.charCodeAt(0) & 0xFF));
            const utf8Decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
            // Only use if it produces a different result and doesn't contain replacement chars
            if (utf8Decoded !== decoded && !utf8Decoded.includes('�')) {
                decoded = utf8Decoded;
            }
        } catch (e) {
            console.warn('SearchSpring: TextDecoder failed', e);
        }
        
        // Fix UTF-8 characters that were misinterpreted as Latin-1
        // Common pattern: UTF-8 byte sequences read as Latin-1 characters
        
        // Fix accented characters (C3 XX pattern) - most common UTF-8 corruption
        // These are 2-byte UTF-8 sequences interpreted as 2 Latin-1 characters
        decoded = decoded
            .replace(/\u00C3\u00A4/g, '\u00E4')  // "Ã¤" → "ä" (U+00E4, UTF-8: C3 A4)
            .replace(/\u00C3\u00BC/g, '\u00FC')  // "Ã¼" → "ü" (U+00FC, UTF-8: C3 BC)
            .replace(/\u00C3\u00B6/g, '\u00F6')  // "Ã¶" → "ö" (U+00F6, UTF-8: C3 B6)
            .replace(/\u00C3\u00A9/g, '\u00E9')  // "Ã©" → "é" (U+00E9, UTF-8: C3 A9)
            .replace(/\u00C3\u00A8/g, '\u00E8')  // "Ã¨" → "è" (U+00E8, UTF-8: C3 A8)
            .replace(/\u00C3\u00A0/g, '\u00E0')  // "Ã " → "à" (U+00E0, UTF-8: C3 A0)
            .replace(/\u00C3\u00A1/g, '\u00E1')  // "Ã¡" → "á" (U+00E1, UTF-8: C3 A1)
            .replace(/\u00C3\u00A2/g, '\u00E2')  // "Ã¢" → "â" (U+00E2, UTF-8: C3 A2)
            .replace(/\u00C3\u00A3/g, '\u00E3')  // "Ã£" → "ã" (U+00E3, UTF-8: C3 A3)
            .replace(/\u00C3\u00A5/g, '\u00E5')  // "Ã¥" → "å" (U+00E5, UTF-8: C3 A5)
            .replace(/\u00C3\u00A7/g, '\u00E7')  // "Ã§" → "ç" (U+00E7, UTF-8: C3 A7)
            .replace(/\u00C3\u00AD/g, '\u00ED')  // "Ã­" → "í" (U+00ED, UTF-8: C3 AD)
            .replace(/\u00C3\u00AC/g, '\u00EC')  // "Ã¬" → "ì" (U+00EC, UTF-8: C3 AC)
            .replace(/\u00C3\u00AE/g, '\u00EE')  // "Ã®" → "î" (U+00EE, UTF-8: C3 AE)
            .replace(/\u00C3\u00AF/g, '\u00EF')  // "Ã¯" → "ï" (U+00EF, UTF-8: C3 AF)
            .replace(/\u00C3\u00B1/g, '\u00F1')  // "Ã±" → "ñ" (U+00F1, UTF-8: C3 B1)
            .replace(/\u00C3\u00B2/g, '\u00F2')  // "Ã²" → "ò" (U+00F2, UTF-8: C3 B2)
            .replace(/\u00C3\u00B3/g, '\u00F3')  // "Ã³" → "ó" (U+00F3, UTF-8: C3 B3)
            .replace(/\u00C3\u00B4/g, '\u00F4')  // "Ã´" → "ô" (U+00F4, UTF-8: C3 B4)
            .replace(/\u00C3\u00B5/g, '\u00F5')  // "Ãµ" → "õ" (U+00F5, UTF-8: C3 B5)
            .replace(/\u00C3\u00B9/g, '\u00F9')  // "Ã¹" → "ù" (U+00F9, UTF-8: C3 B9)
            .replace(/\u00C3\u00BA/g, '\u00FA')  // "Ãº" → "ú" (U+00FA, UTF-8: C3 BA)
            .replace(/\u00C3\u00BB/g, '\u00FB')  // "Ã»" → "û" (U+00FB, UTF-8: C3 BB)
            .replace(/\u00C3\u00BF/g, '\u00FF')  // "Ã¿" → "ÿ" (U+00FF, UTF-8: C3 BF)
            // Uppercase versions
            .replace(/\u00C3\u0084/g, '\u00C4')  // "Ã„" → "Ä" (U+00C4, UTF-8: C3 84)
            .replace(/\u00C3\u009C/g, '\u00DC')  // "Ãœ" → "Ü" (U+00DC, UTF-8: C3 9C)
            .replace(/\u00C3\u0096/g, '\u00D6')  // "Ã–" → "Ö" (U+00D6, UTF-8: C3 96)
            .replace(/\u00C3\u0089/g, '\u00C9')  // "Ã‰" → "É" (U+00C9, UTF-8: C3 89)
            .replace(/\u00C3\u0088/g, '\u00C8')  // "Ãˆ" → "È" (U+00C8, UTF-8: C3 88)
            .replace(/\u00C3\u0080/g, '\u00C0')  // "Ã€" → "À" (U+00C0, UTF-8: C3 80)
            .replace(/\u00C3\u0081/g, '\u00C1')  // "Ã" → "Á" (U+00C1, UTF-8: C3 81)
            .replace(/\u00C3\u0082/g, '\u00C2')  // "Ã‚" → "Â" (U+00C2, UTF-8: C3 82)
            .replace(/\u00C3\u0083/g, '\u00C3')  // "Ãƒ" → "Ã" (U+00C3, UTF-8: C3 83)
            .replace(/\u00C3\u0085/g, '\u00C5')  // "Ã…" → "Å" (U+00C5, UTF-8: C3 85)
            .replace(/\u00C3\u0087/g, '\u00C7')  // "Ã‡" → "Ç" (U+00C7, UTF-8: C3 87)
            .replace(/\u00C3\u008D/g, '\u00CD')  // "Ã" → "Í" (U+00CD, UTF-8: C3 8D)
            .replace(/\u00C3\u008C/g, '\u00CC')  // "ÃŒ" → "Ì" (U+00CC, UTF-8: C3 8C)
            .replace(/\u00C3\u008E/g, '\u00CE')  // "ÃŽ" → "Î" (U+00CE, UTF-8: C3 8E)
            .replace(/\u00C3\u008F/g, '\u00CF')  // "Ã" → "Ï" (U+00CF, UTF-8: C3 8F)
            .replace(/\u00C3\u0091/g, '\u00D1')  // "Ã'" → "Ñ" (U+00D1, UTF-8: C3 91)
            .replace(/\u00C3\u0092/g, '\u00D2')  // "Ã'" → "Ò" (U+00D2, UTF-8: C3 92)
            .replace(/\u00C3\u0093/g, '\u00D3')  // "Ã'" → "Ó" (U+00D3, UTF-8: C3 93)
            .replace(/\u00C3\u0094/g, '\u00D4')  // "Ã'" → "Ô" (U+00D4, UTF-8: C3 94)
            .replace(/\u00C3\u0095/g, '\u00D5')  // "Ã•" → "Õ" (U+00D5, UTF-8: C3 95)
            .replace(/\u00C3\u0099/g, '\u00D9')  // "Ã™" → "Ù" (U+00D9, UTF-8: C3 99)
            .replace(/\u00C3\u009A/g, '\u00DA')  // "Ãš" → "Ú" (U+00DA, UTF-8: C3 9A)
            .replace(/\u00C3\u009B/g, '\u00DB')  // "Ã›" → "Û" (U+00DB, UTF-8: C3 9B)
            .replace(/\u00C3\u0178/g, '\u00DF'); // "ÃŸ" → "ß" (U+00DF, UTF-8: C3 9F)
        
        // Fix punctuation marks (E2 80 XX pattern) - 3-byte UTF-8 sequences
        // First decode HTML entities for dashes
        decoded = decoded
            .replace(/&ndash;|&#8211;|&#x2013;/gi, '\u2013')  // HTML entity for en dash
            .replace(/&mdash;|&#8212;|&#x2014;/gi, '\u2014'); // HTML entity for em dash
        
        decoded = decoded
            // Handle "â –" or "â —" pattern (corruption + space + dash)
            // Match any character in the corruption range followed by optional whitespace and dash
            .replace(/[\u00C0-\u00FF]\s*[\u2013\u2014]/g, function(match) {
                // Replace corruption char + space + dash with just the dash
                return match.includes('\u2014') ? '\u2014' : '\u2013';
            })
            // Also handle literal "â" character variations
            .replace(/[\u00E2â]\s*[\u2013\u2014]/g, function(match) {
                return match.includes('\u2014') ? '\u2014' : '\u2013';
            })
            // Fix full UTF-8 sequences that were misinterpreted
            .replace(/\u00E2\u20AC\u0094/g, '\u2014')      // em dash (U+2014) E2 80 94
            .replace(/\u00E2\u20AC\u0093/g, '\u2013')       // en dash (U+2013) E2 80 93  
            .replace(/\u00E2\u20AC\u0099/g, '\u2019')        // right single quotation (U+2019) E2 80 99
            .replace(/\u00E2\u20AC\u0098/g, '\u2018')        // left single quotation (U+2018) E2 80 98
            .replace(/\u00E2\u20AC\u009C/g, '\u201C')       // left double quotation (U+201C) E2 80 9C
            .replace(/\u00E2\u20AC\u009D/g, '\u201D')       // right double quotation (U+201D) E2 80 9D
            .replace(/\u00E2\u20AC\u00A6/g, '\u2026')       // ellipsis (U+2026) E2 80 A6
            // Handle partial corruption patterns - "â€" sequences
            .replace(/\u00E2\u20AC\s/g, '\u2013 ')     // "â€ " → "– "
            .replace(/\u00E2\u20AC\u2013/g, '\u2013')  // "â€–" → "–" (double corruption)
            .replace(/\u00E2\u20AC\u2014/g, '\u2014')  // "â€—" → "—" (double corruption)
            // Handle standalone "â" character (common corruption)
            .replace(/\u00E2\s/g, '\u2013 ')     // "â " → "– "
            .replace(/\u00E2$/g, '\u2013')        // "â" at end → "–"
            .replace(/\s\u00E2\s/g, ' \u2013 ')  // " â " → " – "
            .replace(/\s\u00E2$/g, ' \u2013')     // " â" at end → " –"
            // Also check for literal "â" character (in case encoding is different)
            .replace(/â\s/g, '\u2013 ')           // "â " → "– " (literal â character)
            .replace(/\sâ\s/g, ' \u2013 ')         // " â " → " – " (literal â character)
            .replace(/\sâ$/g, ' \u2013');          // " â" at end → " –" (literal â character)
        
        return decoded;
    } catch (e) {
        console.error('SearchSpring: decodeHtmlEntities failed', e);
        return text;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring API Call Functionality
// ─────────────────────────────────────────────────────────────────────────────

function getSiteId() {
    const indexes = window.themeVariables?.settings?.searchSpringIndexes || {};
    const market = window.themeVariables?.settings?.currentMarket || 'gb';
    const language = window.themeVariables?.settings?.currentLanguage || 'en';

    const siteId = indexes['DEFAULT'];
    return siteId;
}

// part of future scope
// function getSearchSpringIndexKey(market, language) {
//     if (market === 'gb') return 'UK-EN';
//     if (market === 'ie') return 'IE-EN';
//     if (market === 'nether') return language === 'nl' ? 'NL-NL' : 'NL-EN';
//
//     switch (market) {
//         case 'spain': return language === 'es' ? 'ROW-ES' : 'ROW-EN';
//         case 'france': return language === 'fr' ? 'ROW-FR' : 'ROW-EN';
//         case 'italy': return language === 'it' ? 'ROW-IT' : 'ROW-EN';
//         case 'portugal': return language === 'pt-PT' ? 'ROW-PT' : 'ROW-EN';
//         case 'belgium':
//             switch (language) {
//                 case 'nl': return 'NL-NL';
//                 case 'fr': return 'ROW-FR';
//                 default: return 'ROW-EN';
//             }
//         case 'germany':
//         case 'austria':
//             return language === 'de' ? 'ROW-DE' : 'ROW-EN';
//         default:
//             switch (language) {
//                 case 'es': return 'ROW-ES';
//                 case 'fr': return 'ROW-FR';
//                 case 'it': return 'ROW-IT';
//                 case 'pt': return 'ROW-PT';
//                 case 'de': return 'ROW-DE';
//                 default: return 'ROW-EN';
//             }
//     }
// }

async function searchSpringApiCall(endpoint, params = {}, filterParams = []) {
    let queryString = new URLSearchParams(params).toString();
    
    // Add filter parameters that allow duplicates
    if (filterParams && filterParams.length > 0) {
        const filterQuery = filterParams.map(([key, value]) => 
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        ).join('&');
        
        queryString = queryString ? `${queryString}&${filterQuery}` : filterQuery;
    }

    const finalParams = new URLSearchParams(queryString);
    if (window.themeVariables?.settings?.searchSpringIncludeIsPublishedFilter) {
        const isPublishedBgFilter = SearchSpringCommon.getPublishedBgFilter();
        if (isPublishedBgFilter) {
            finalParams.set(`bgfilter.${isPublishedBgFilter.name}`, isPublishedBgFilter.value);
        }
    }
    const url = `${endpoint}?${finalParams.toString()}`;
    const response = await fetch(url);
    return response.json();
}

async function fetchSearchSpringResults(siteId, config = {}) {
    const endpoint = `https://${siteId}.a.searchspring.io/api/search/${config.type == 'category' ? 'category' : 'search'}`

    const params = {
        ...getSearchSpringParams(siteId),
        pageLoadId: window.pageLoadId,
        domain: window.location.hostname,
        resultsFormat: 'json',
        ...(config.beacon !== false && { beacon: 'true' }),
        page: config.page || 1,
        resultsPerPage: config.resultsPerPage || 24
    };

    // Handle Search Query
    if (config.query) {
        params.q = config.query;
    }

    // Handle Sorting
    // Checks for specific sort object OR legacy sort keys in the config root
    if (config.sort?.field && config.sort.field !== 'relevance') {
         params[`sort.${config.sort.field}`] = config.sort.direction || 'asc';
    }
    // Legacy support: copy any key starting with 'sort.' from config to params
    Object.keys(config).forEach(key => {
        if (key.startsWith('sort.')) params[key] = config[key];
    });

    // Construct Filter Parameters (Array of arrays for URLSearchParams)
    const filterParams = [];

    // Country Background Filter
    const marketFilter = SearchSpringCommon.getSearchSpringMarketFilter();
    if (marketFilter) {
        filterParams.push([`bgfilter.${marketFilter}`, 'TRUE']);
    }
    
    // Collection Background Filter - prefer collection_id (locale-agnostic), else collection_handle
    if (config.collectionId) {
        filterParams.push(['bgfilter.collection_id', String(config.collectionId)]);
    } else if (config.collectionHandle) {
        filterParams.push(['bgfilter.collection_handle', config.collectionHandle]);
    }

    // Custom Background Filters
    if (config.bgFilters) {
        Object.entries(config.bgFilters).forEach(([field, values]) => {
            const arr = Array.isArray(values) ? values : [values];
            arr.forEach(val => filterParams.push([`bgfilter.${field}`, val]));
        });
    }

    // User Selected Facets (Standard Filters)
    if (config.filters) {
        Object.entries(config.filters).forEach(([field, values]) => {
            const valuesArray = Array.isArray(values) ? values : [values];

            if (isPriceFacetField(field)) {
                valuesArray.forEach(val => {
                    if (val.includes('-')) {
                        const [low, high] = val.split('-');
                        filterParams.push([`filter.${field}.low`, low]);
                        filterParams.push([`filter.${field}.high`, high]);
                    } else {
                        filterParams.push([`filter.${field}`, val]);
                    }
                });
            } else {
                // Standard Filters
                valuesArray.forEach(val => filterParams.push([`filter.${field}`, val]));
            }
        });
    }

    // Construct Final URL
    // We use URLSearchParams to handle encoding cleanly
    const urlParams = new URLSearchParams(params);

    // Append repeatable filters
    filterParams.forEach(([key, value]) => urlParams.append(key, value));

    if (window.themeVariables?.settings?.searchSpringIncludeIsPublishedFilter) {
        const isPublishedBgFilter = SearchSpringCommon.getPublishedBgFilter();
        if (isPublishedBgFilter) {
            urlParams.set(`bgfilter.${isPublishedBgFilter.name}`, isPublishedBgFilter.value);
        }
    }

    // Limit returned facets to current market (avoids 25-facet cap); only when meta filters define the set
    if (config.includedFacets?.length) {
        config.includedFacets.forEach(f => urlParams.append('includedFacets', f));
    }

    const url = `${endpoint}?${urlParams.toString()}`;

    // Execute Request
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`SearchSpring API Error: ${response.status}`);
        // Ensure UTF-8 encoding is properly handled
        const data = await response.json();
        return data;
    } catch (err) {
        return { results: [], facets: [], pagination: {} }; // Return empty structure on fail
    }
}

function searchProducts(siteId, query, options = {}) {
  return fetchSearchSpringResults(siteId, { ...options, query });
}

//MJ: WIP - confirm if we can remove
// function getLanguageParameters(language = 'en') {
//     if (language == 'en') {
//         return {"includedFacets": [
//             `title`, 
//             `description`
//         ]};
//     }
//     return {"includedFacets": [
//         `${language}_title`, 
//         `${language}_description`
//     ]};
// }

function getSuggestions(siteId, query, options = {}) {
    const endpoint = `https://${siteId}.a.searchspring.io/api/suggest/query`;
    const params = {
        ...getSearchSpringParams(siteId),
        query: query,
        suggestionCount: options.suggestionCount || 5,
        productCount: options.productCount || 10,
        ...options
    };
    return searchSpringApiCall(endpoint, params);
}

function getAutocompleteSuggestions(siteId, query, options = {}) {
    const currentLanguage = window.themeVariables?.settings?.currentLanguage || 'en';
    const endpoint = `https://${siteId}.a.searchspring.io/api/search/autocomplete`;
    const params = {
        ...getSearchSpringParams(siteId),
        beacon: 'true',
        q: query,
        ...options
    };
    return searchSpringApiCall(endpoint, params);
}

// Trending items list
function getTrending(siteId, options = {}) {
    const endpoint = `https://${siteId}.a.searchspring.io/api/suggest/trending`;
    const params = {
        ...getSearchSpringParams(siteId),
        ...options
    };
    return searchSpringApiCall(endpoint, params);
}


// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Initialization and Event Examples
// ─────────────────────────────────────────────────────────────────────────────

function initializeSearchSpringCookies() {
    // Required cookies
    let userId = SearchSpringCommon.getCookie('_isuid');
    let ssUserId = SearchSpringCommon.getCookie('ssUserId');
    
    if (!userId || !ssUserId || userId !== ssUserId) {
        const newUserId = SearchSpringCommon.generateUUID();
        SearchSpringCommon.setCookie('_isuid', newUserId, 365);
        SearchSpringCommon.setCookie('ssUserId', newUserId, 365);
    }

    let sessionId = SearchSpringCommon.getCookie('ssSessionId');
    if (!sessionId) {
        sessionId = SearchSpringCommon.getCookie('ssSessionIdNamespace') || SearchSpringCommon.generateUUID();
        SearchSpringCommon.setCookie('ssSessionId', sessionId);
    }

    if (SearchSpringCommon.getCookie('ssSessionIdNamespace')) {
        SearchSpringCommon.deleteCookie('ssSessionIdNamespace');
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Initialization Logic
// ─────────────────────────────────────────────────────────────────────────────

// 1. Wrap your main logic in a named function
const initSearchSpringApp = async () => {
    // Wait for themeVariables (from js-variables in head) - can be delayed on direct collection/search visits
    if (!window.themeVariables) {
        const maxRetries = 20;
        for (let i = 0; i < maxRetries; i++) {
            await new Promise((r) => setTimeout(r, 50));
            if (window.themeVariables) break;
        }
        if (!window.themeVariables) {
            return;
        }
    }

    window.pageLoadId = SearchSpringCommon.generateUUID();

    const lang = window.themeVariables?.settings?.currentLanguage || 'en';
    const market = window.themeVariables?.settings?.currentMarket || 'gb';
    const marketCodeMap = { 'gb': 'GB', 'ie': 'IE', 'nether': 'NL', 'spain': 'ES', 'france': 'FR', 'italy': 'IT', 'portugal': 'PT', 'germany': 'DE', 'austria': 'AT', 'denmark': 'DK', 'belgium':'BE', 'finland': 'FI', 'isle-of-man': 'IM', 'poland': 'PL' };

    const countryCode = marketCodeMap[market] || 'GB';
    let locale;
    if (lang.includes('-')) {
        locale = lang;
    } else {
        locale = `${lang}-${countryCode}`;
    }

    // For EUR, always use comma decimal separator regardless of locale
    const currency = window.searchSpringTranslations?.currency || { symbol: '£', code: 'GBP' };
    const isEUR = currency.code === 'EUR';
    // Use a European locale that uses comma decimal for EUR (es-ES, de-DE, fr-FR all work)
    // This ensures EUR always shows comma decimal even with English locales like en-IE
    const formatLocale = isEUR && locale.includes('en-') ? 'es-ES' : locale;
    
    window.priceNumberFormatter = new Intl.NumberFormat(formatLocale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    window.formatPriceWithCurrency = (price) => {
      const currenciesRequiringSuffix = ['SEK', 'DKK', 'CHF']
      const currency = window.searchSpringTranslations?.currency || { symbol: '£', code: 'GBP' };
      const showCurrencyCode = !!window.themeVariables?.settings?.currencyCodeEnabled;

      let formattedPrice = window.priceNumberFormatter.format(price);
      // Ensure EUR always uses comma decimal (fallback if locale didn't apply correctly)
      if (currency.code === 'EUR' && formattedPrice.includes('.')) {
        formattedPrice = formattedPrice.replace('.', ',');
      }

      if (currenciesRequiringSuffix.includes(currency.code)) {
        return `${formattedPrice} ${currency.symbol}${showCurrencyCode ? ` ${currency.code}` : ''}`;
      }

      return `${currency.symbol}${formattedPrice}${showCurrencyCode ? ` ${currency.code}` : ''}`;
    }
    initializeSearchSpringCookies();

    window.colorSwatchMap = {};
    const configString = window.themeVariables?.settings?.colorSwatchConfig;

    if (configString) {
        configString.split('\n').forEach(line => {
        if (line.includes(':')) {
            const parts = line.split(':');
            const colorName = parts[0].trim().toLowerCase();
            const colorValue = parts[1].trim();
            window.colorSwatchMap[colorName] = colorValue;
        }
        });
    }

    const englishColorHexMap = {
        'black': '#000000', 'blue': '#0000ff', 'brown': '#a52a2a',
        'green': '#008000', 'grey': '#808080', 'orange': '#ffa500',
        'pink': '#ffc0cb', 'purple': '#800080', 'white': '#ffffff',
        'red': '#ff0000', 'burgundy': '#800020', 'yellow': '#ffff00',
        'indigo': '#4b0082'
    };

    window.hexToEnglishColorMap = {};
    for (const [name, hex] of Object.entries(englishColorHexMap)) {
        window.hexToEnglishColorMap[hex.toLowerCase()] = name;
    }
  
    const allSearchForms = document.querySelectorAll('form[action$="/search"]');
  
    async function handleSearchRedirect(event) {
      const form = event.target;
      const searchInput = form.querySelector('input[name="q"]');
      const query = searchInput?.value?.trim();
  
      if (!query) {
        return; 
      }
  
      try {
        const redirectCheck = await searchProducts(getSiteId(), query, { resultsPerPage: 0 });

        if (redirectCheck?.merchandising?.redirect) {
            event.preventDefault();
            const redirectPath = redirectCheck.merchandising.redirect;

            if (redirectPath.startsWith('http')) {
                window.location.href = redirectPath;
                return;
            }

            const marketPrefixMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
            const marketPrefix = marketPrefixMatch ? marketPrefixMatch[0] : '';
            const finalRedirectUrl = `${marketPrefix}${redirectPath}`;
            window.location.href = finalRedirectUrl;
            return;
        }
        
      } catch (err) {}
    }
  
    allSearchForms.forEach(form => {
      form.addEventListener('submit', handleSearchRedirect);
    });
  
    // Trending Now is rendered in snippets/predictive-search.liquid from the
    // header's three trending term settings (or the quick links menu). Do not
    // fill .ss_predictive-search__menu-list from Searchspring trending data.


    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="new-search"]')) {
        const searchInput = document.querySelector('.main-search__input');
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
        }
      }
    });
};

// 2. The Universal Loader Pattern
// This prevents the race condition where DOMContentLoaded might have already fired,
// but also waits if the script is parsed before the Body is ready (which is your current case).

if (document.readyState === 'loading') {
    // Document is still parsing, wait for it to be ready
    document.addEventListener('DOMContentLoaded', initSearchSpringApp);
} else {
    // Document is already ready (interactive or complete), run immediately
    initSearchSpringApp();
}

if ((typeof Shopify) === 'undefined') { window.Shopify = {}; }
if ((typeof Shopify.getCart) === 'undefined') {
    Shopify.getCart = function(callback, cart) {
        if(!cart){
            return fetch('/cart.js').then(res => res.json()).then(cart => {
                if ((typeof callback) === 'function') {
                    callback(cart);
                }
                else if (typeof Shopify.onCartUpdate === "function") {
                    Shopify.onCartUpdate(cart);
                }
            });
        }else{
            if ((typeof callback) === 'function') {
            callback(cart);
        }else if(typeof Shopify.onCartUpdate ==='function') {
            Shopify.onCartUpdate(cart);
            }
        }
    };
}

(()=>{
    var a_ = Object.defineProperty
      , a0 = (a,b,c)=>b in a ? a_(a, b, {
        enumerable: !0,
        configurable: !0,
        writable: !0,
        value: c
    }) : a[b] = c
      , b = (c,a,b)=>(a0(c, "symbol" != typeof a ? a + "" : a, b),
    b);
    function a(b) {
        this.listenerMap = [{}, {}],
        b && this.root(b),
        this.handle = a.prototype.handle.bind(this),
        this._removedListeners = []
    }
    function a1(a, b) {
        return a.toLowerCase() === b.tagName.toLowerCase()
    }
    function a2(b, a) {
        return this.rootElement === window ? a === document || a === document.documentElement || a === window : this.rootElement === a
    }
    function a3(a, b) {
        return a === b.id
    }
    a.prototype.root = function(c) {
        let b = this.listenerMap, a;
        if (this.rootElement) {
            for (a in b[1])
                b[1].hasOwnProperty(a) && this.rootElement.removeEventListener(a, this.handle, !0);
            for (a in b[0])
                b[0].hasOwnProperty(a) && this.rootElement.removeEventListener(a, this.handle, !1)
        }
        if (!c || !c.addEventListener)
            return this.rootElement && delete this.rootElement,
            this;
        for (a in this.rootElement = c,
        b[1])
            b[1].hasOwnProperty(a) && this.rootElement.addEventListener(a, this.handle, !0);
        for (a in b[0])
            b[0].hasOwnProperty(a) && this.rootElement.addEventListener(a, this.handle, !1);
        return this
    }
    ,
    a.prototype.captureForType = function(a) {
        return -1 !== ["blur", "error", "focus", "load", "resize", "scroll"].indexOf(a)
    }
    ,
    a.prototype.on = function(b, a, f, c) {
        let g, h, d, e;
        if (!b)
            throw new TypeError("Invalid event type: " + b);
        if ("function" == typeof a && (c = f,
        f = a,
        a = null),
        void 0 === c && (c = this.captureForType(b)),
        "function" != typeof f)
            throw new TypeError("Handler must be a type of Function");
        return g = this.rootElement,
        (h = this.listenerMap[c ? 1 : 0])[b] || (g && g.addEventListener(b, this.handle, c),
        h[b] = []),
        a ? /^[a-z]+$/i.test(a) ? (e = a,
        d = a1) : /^#[a-z0-9\-_]+$/i.test(a) ? (e = a.slice(1),
        d = a3) : (e = a,
        d = Element.prototype.matches) : (e = null,
        d = a2.bind(this)),
        h[b].push({
            selector: a,
            handler: f,
            matcher: d,
            matcherParam: e
        }),
        this
    }
    ,
    a.prototype.off = function(c, a, b, g) {
        let e, h, f, d, i;
        if ("function" == typeof a && (g = b,
        b = a,
        a = null),
        void 0 === g)
            return this.off(c, a, b, !0),
            this.off(c, a, b, !1),
            this;
        if (f = this.listenerMap[g ? 1 : 0],
        !c) {
            for (i in f)
                f.hasOwnProperty(i) && this.off(i, a, b);
            return this
        }
        if (!(d = f[c]) || !d.length)
            return this;
        for (e = d.length - 1; e >= 0; e--)
            h = d[e],
            a && a !== h.selector || b && b !== h.handler || (this._removedListeners.push(h),
            d.splice(e, 1));
        return !d.length && (delete f[c],
        this.rootElement && this.rootElement.removeEventListener(c, this.handle, g)),
        this
    }
    ,
    a.prototype.handle = function(d) {
        let b, g, f = d.type, i, l, h, c = [], a, j = "ftLabsDelegateIgnore";
        if (!0 === d[j])
            return;
        switch (3 === (a = d.target).nodeType && (a = a.parentNode),
        a.correspondingUseElement && (a = a.correspondingUseElement),
        i = this.rootElement,
        d.eventPhase || (d.target !== d.currentTarget ? 3 : 2)) {
        case 1:
            c = this.listenerMap[1][f];
            break;
        case 2:
            this.listenerMap[0] && this.listenerMap[0][f] && (c = c.concat(this.listenerMap[0][f])),
            this.listenerMap[1] && this.listenerMap[1][f] && (c = c.concat(this.listenerMap[1][f]));
            break;
        case 3:
            c = this.listenerMap[0][f]
        }
        let e = [];
        for (g = c.length; a && g; ) {
            for (b = 0; b < g && (h = c[b]); b++)
                a.tagName && ["button", "input", "select", "textarea"].indexOf(a.tagName.toLowerCase()) > -1 && a.hasAttribute("disabled") ? e = [] : h.matcher.call(a, h.matcherParam, a) && e.push([d, a, h]);
            if (a === i || (g = c.length,
            (a = a.parentElement || a.parentNode)instanceof HTMLDocument))
                break
        }
        let k;
        for (b = 0; b < e.length; b++)
            if (!(this._removedListeners.indexOf(e[b][2]) > -1) && !1 === this.fire.apply(this, e[b])) {
                e[b][0][j] = !0,
                e[b][0].preventDefault(),
                k = !1;
                break
            }
        return k
    }
    ,
    a.prototype.fire = function(b, a, c) {
        return c.handler.call(a, b, a)
    }
    ,
    a.prototype.destroy = function() {
        this.off(),
        this.root()
    }
    ;
    var m = a
      , n = class {
        constructor() {
            this.delegateElement = new m(document.body),
            this.delegateElement.on("change", "[data-bind-value]", this._onValueChanged.bind(this))
        }
        _onValueChanged(c, a) {
            let b = document.getElementById(a.getAttribute("data-bind-value"));
            b && ("SELECT" === a.tagName && (a = a.options[a.selectedIndex]),
            b.innerHTML = a.hasAttribute("title") ? a.getAttribute("title") : a.value)
        }
    }
    ;
    function a4(a, b, c={}) {
        a.dispatchEvent(new CustomEvent(b,{
            bubbles: !0,
            detail: c
        }))
    }
    function a5(a, b, c={}) {
        a.dispatchEvent(new CustomEvent(b,{
            bubbles: !1,
            detail: c
        }))
    }
    var a6 = class extends HTMLElement {
        constructor() {
            super(),
            this._hasSectionReloaded = !1,
            Shopify.designMode && this.rootDelegate.on("shopify:section:select", a=>{
                let b = this.closest(".shopify-section");
                a.target === b && a.detail.load && (this._hasSectionReloaded = !0)
            }
            )
        }
        get rootDelegate() {
            return this._rootDelegate = this._rootDelegate || new m(document.documentElement)
        }
        get delegate() {
            return this._delegate = this._delegate || new m(this)
        }
        showLoadingBar() {
            a4(document.documentElement, "theme:loading:start")
        }
        hideLoadingBar() {
            a4(document.documentElement, "theme:loading:end")
        }
        untilVisible(a={
            rootMargin: "30px 0px",
            threshold: 0
        }) {
            let b = ()=>{
                this.classList.add("became-visible"),
                this.style.opacity = "1"
            }
            ;
            return new Promise(c=>{
                window.IntersectionObserver ? (this.intersectionObserver = new IntersectionObserver(a=>{
                    a[0].isIntersecting && (this.intersectionObserver.disconnect(),
                    requestAnimationFrame(()=>{
                        c(),
                        b()
                    }
                    ))
                }
                ,a),
                this.intersectionObserver.observe(this)) : (c(),
                b())
            }
            )
        }
        disconnectedCallback() {
            var a;
            this.delegate.destroy(),
            this.rootDelegate.destroy(),
            null == (a = this.intersectionObserver) || a.disconnect(),
            delete this._delegate,
            delete this._rootDelegate
        }
    }
      , d = ["input", "select", "textarea", "a[href]", "button", "[tabindex]", "audio[controls]", "video[controls]", '[contenteditable]:not([contenteditable="false"])', "details>summary:first-of-type", "details"]
      , a7 = d.join(",")
      , a8 = "undefined" == typeof Element ? function() {}
    : Element.prototype.matches || Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector
      , a9 = function(a, c, d) {
        var b = Array.prototype.slice.apply(a.querySelectorAll(a7));
        return c && a8.call(a, a7) && b.unshift(a),
        b = b.filter(d)
    }
      , ba = function(a) {
        return "true" === a.contentEditable
    }
      , bb = function(a) {
        var b = parseInt(a.getAttribute("tabindex"), 10);
        return isNaN(b) ? ba(a) || ("AUDIO" === a.nodeName || "VIDEO" === a.nodeName || "DETAILS" === a.nodeName) && null === a.getAttribute("tabindex") ? 0 : a.tabIndex : b
    }
      , bc = function(a, b) {
        return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex
    }
      , bd = function(a) {
        return "INPUT" === a.tagName
    }
      , be = function(a) {
        return bd(a) && "hidden" === a.type
    }
      , bf = function(a) {
        return "DETAILS" === a.tagName && Array.prototype.slice.apply(a.children).some(function(a) {
            return "SUMMARY" === a.tagName
        })
    }
      , bg = function(b, c) {
        for (var a = 0; a < b.length; a++)
            if (b[a].checked && b[a].form === c)
                return b[a]
    }
      , bh = function(a) {
        if (!a.name)
            return !0;
        var b, f = a.form || a.ownerDocument, c = function(a) {
            return f.querySelectorAll('input[type="radio"][name="' + a + '"]')
        };
        if ("undefined" != typeof window && void 0 !== window.CSS && "function" == typeof window.CSS.escape)
            b = c(window.CSS.escape(a.name));
        else
            try {
                b = c(a.name)
            } catch (e) {
                return !1
            }
        var d = bg(b, a.form);
        return !d || d === a
    }
      , bi = function(a) {
        return bd(a) && "radio" === a.type
    }
      , bj = function(a) {
        return bi(a) && !bh(a)
    }
      , bk = function(a, b) {
        if ("hidden" === getComputedStyle(a).visibility)
            return !0;
        var d = a8.call(a, "details>summary:first-of-type") ? a.parentElement : a;
        if (a8.call(d, "details:not([open]) *"))
            return !0;
        if (b && "full" !== b) {
            if ("non-zero-area" === b) {
                var c = a.getBoundingClientRect()
                  , e = c.width
                  , f = c.height;
                return 0 === e && 0 === f
            }
        } else
            for (; a; ) {
                if ("none" === getComputedStyle(a).display)
                    return !0;
                a = a.parentElement
            }
        return !1
    }
      , bl = function(b) {
        if (bd(b) || "SELECT" === b.tagName || "TEXTAREA" === b.tagName || "BUTTON" === b.tagName)
            for (var a = b.parentElement; a; ) {
                if ("FIELDSET" === a.tagName && a.disabled) {
                    for (var c = 0; c < a.children.length; c++) {
                        var d = a.children.item(c);
                        if ("LEGEND" === d.tagName) {
                            if (d.contains(b))
                                return !1;
                            break
                        }
                    }
                    return !0
                }
                a = a.parentElement
            }
        return !1
    }
      , bm = function(b, a) {
        return !(a.disabled || be(a) || bk(a, b.displayCheck) || bf(a) || bl(a))
    }
      , bn = function(b, a) {
        return !(!bm(b, a) || bj(a) || 0 > bb(a))
    }
      , bo = function(b, a) {
        var c = []
          , d = [];
        return a9(b, (a = a || {}).includeContainer, bn.bind(null, a)).forEach(function(a, e) {
            var b = bb(a);
            0 === b ? c.push(a) : d.push({
                documentOrder: e,
                tabIndex: b,
                node: a
            })
        }),
        d.sort(bc).map(function(a) {
            return a.node
        }).concat(c)
    }
      , bp = d.concat("iframe").join(",")
      , bq = function(a, b) {
        if (b = b || {},
        !a)
            throw new Error("No node provided");
        return !1 !== a8.call(a, bp) && bm(b, a)
    };
    function br(c, d) {
        var a = Object.keys(c);
        if (Object.getOwnPropertySymbols) {
            var b = Object.getOwnPropertySymbols(c);
            d && (b = b.filter(function(a) {
                return Object.getOwnPropertyDescriptor(c, a).enumerable
            })),
            a.push.apply(a, b)
        }
        return a
    }
    function bs(c) {
        for (var a = 1; a < arguments.length; a++) {
            var b = null != arguments[a] ? arguments[a] : {};
            a % 2 ? br(Object(b), !0).forEach(function(a) {
                bt(c, a, b[a])
            }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(c, Object.getOwnPropertyDescriptors(b)) : br(Object(b)).forEach(function(a) {
                Object.defineProperty(c, a, Object.getOwnPropertyDescriptor(b, a))
            })
        }
        return c
    }
    function bt(a, b, c) {
        return b in a ? Object.defineProperty(a, b, {
            value: c,
            enumerable: !0,
            configurable: !0,
            writable: !0
        }) : a[b] = c,
        a
    }
    var bu = function() {
        var a = [];
        return {
            activateTrap: function(b) {
                if (a.length > 0) {
                    var c = a[a.length - 1];
                    c !== b && c.pause()
                }
                var d = a.indexOf(b);
                -1 === d || a.splice(d, 1),
                a.push(b)
            },
            deactivateTrap: function(c) {
                var b = a.indexOf(c);
                -1 !== b && a.splice(b, 1),
                a.length > 0 && a[a.length - 1].unpause()
            }
        }
    }()
      , bv = function(a) {
        return a.tagName && "input" === a.tagName.toLowerCase() && "function" == typeof a.select
    }
      , bw = function(a) {
        return "Escape" === a.key || "Esc" === a.key || 27 === a.keyCode
    }
      , bx = function(a) {
        return "Tab" === a.key || 9 === a.keyCode
    }
      , by = function(a) {
        return setTimeout(a, 0)
    }
      , bz = function(a, c) {
        var b = -1;
        return a.every(function(a, d) {
            return !c(a) || (b = d,
            !1)
        }),
        b
    }
      , bA = function(b) {
        for (var c = arguments.length, d = new Array(c > 1 ? c - 1 : 0), a = 1; a < c; a++)
            d[a - 1] = arguments[a];
        return "function" == typeof b ? b.apply(void 0, d) : b
    }
      , bB = function(a) {
        return a.target.shadowRoot && "function" == typeof a.composedPath ? a.composedPath()[0] : a.target
    }
      , bC = function(c, a) {
        var b, d = (null == a ? void 0 : a.document) || document, e = bs({
            returnFocusOnDeactivate: !0,
            escapeDeactivates: !0,
            delayInitialFocus: !0
        }, a), f = {
            containers: [],
            tabbableGroups: [],
            nodeFocusedBeforeActivation: null,
            mostRecentlyFocusedNode: null,
            active: !1,
            paused: !1,
            delayInitialFocusTimer: void 0
        }, g = function(a, b, c) {
            return a && void 0 !== a[b] ? a[b] : e[c || b]
        }, h = function(a) {
            return !!(a && f.containers.some(function(b) {
                return b.contains(a)
            }))
        }, i = function(c) {
            var a = e[c];
            if ("function" == typeof a) {
                for (var f = arguments.length, g = new Array(f > 1 ? f - 1 : 0), b = 1; b < f; b++)
                    g[b - 1] = arguments[b];
                a = a.apply(void 0, g)
            }
            if (!a) {
                if (void 0 === a || !1 === a)
                    return a;
                throw new Error("`".concat(c, "` was specified but was not a node, or did not return a node"))
            }
            var h = a;
            if ("string" == typeof a && !(h = d.querySelector(a)))
                throw new Error("`".concat(c, "` as selector refers to no known node"));
            return h
        }, j = function() {
            var a = i("initialFocus");
            if (!1 === a)
                return !1;
            if (void 0 === a) {
                if (h(d.activeElement))
                    a = d.activeElement;
                else {
                    var b = f.tabbableGroups[0];
                    a = b && b.firstTabbableNode || i("fallbackFocus")
                }
            }
            if (!a)
                throw new Error("Your focus-trap needs to have at least one focusable element");
            return a
        }, k = function() {
            if (f.tabbableGroups = f.containers.map(function(b) {
                var a = bo(b);
                if (a.length > 0)
                    return {
                        container: b,
                        firstTabbableNode: a[0],
                        lastTabbableNode: a[a.length - 1]
                    }
            }).filter(function(a) {
                return !!a
            }),
            f.tabbableGroups.length <= 0 && !i("fallbackFocus"))
                throw new Error("Your focus-trap must have at least one container with at least one tabbable node in it at all times")
        }, l = function b(a) {
            if (!1 !== a && a !== d.activeElement) {
                if (!a || !a.focus) {
                    b(j());
                    return
                }
                a.focus({
                    preventScroll: !!e.preventScroll
                }),
                f.mostRecentlyFocusedNode = a,
                bv(a) && a.select()
            }
        }, m = function(a) {
            var b = i("setReturnFocus", a);
            return b || !1 !== b && a
        }, n = function(a) {
            var c = bB(a);
            if (!h(c)) {
                if (bA(e.clickOutsideDeactivates, a)) {
                    b.deactivate({
                        returnFocus: e.returnFocusOnDeactivate && !bq(c)
                    });
                    return
                }
                bA(e.allowOutsideClick, a) || a.preventDefault()
            }
        }, o = function(b) {
            var a = bB(b)
              , c = h(a);
            c || a instanceof Document ? c && (f.mostRecentlyFocusedNode = a) : (b.stopImmediatePropagation(),
            l(f.mostRecentlyFocusedNode || j()))
        }, p = function(e) {
            var g = bB(e);
            k();
            var a = null;
            if (f.tabbableGroups.length > 0) {
                var b = bz(f.tabbableGroups, function(a) {
                    return a.container.contains(g)
                });
                if (b < 0)
                    a = e.shiftKey ? f.tabbableGroups[f.tabbableGroups.length - 1].lastTabbableNode : f.tabbableGroups[0].firstTabbableNode;
                else if (e.shiftKey) {
                    var c = bz(f.tabbableGroups, function(a) {
                        return g === a.firstTabbableNode
                    });
                    if (c < 0 && f.tabbableGroups[b].container === g && (c = b),
                    c >= 0) {
                        var h = 0 === c ? f.tabbableGroups.length - 1 : c - 1
                          , j = f.tabbableGroups[h];
                        a = j.lastTabbableNode
                    }
                } else {
                    var d = bz(f.tabbableGroups, function(a) {
                        return g === a.lastTabbableNode
                    });
                    if (d < 0 && f.tabbableGroups[b].container === g && (d = b),
                    d >= 0) {
                        var m = d === f.tabbableGroups.length - 1 ? 0 : d + 1
                          , n = f.tabbableGroups[m];
                        a = n.firstTabbableNode
                    }
                }
            } else
                a = i("fallbackFocus");
            a && (e.preventDefault(),
            l(a))
        }, q = function(a) {
            if (bw(a) && !1 !== bA(e.escapeDeactivates, a)) {
                a.preventDefault(),
                b.deactivate();
                return
            }
            if (bx(a)) {
                p(a);
                return
            }
        }, r = function(a) {
            if (!bA(e.clickOutsideDeactivates, a)) {
                var b = bB(a);
                h(b) || bA(e.allowOutsideClick, a) || (a.preventDefault(),
                a.stopImmediatePropagation())
            }
        }, s = function() {
            if (f.active)
                return bu.activateTrap(b),
                f.delayInitialFocusTimer = e.delayInitialFocus ? by(function() {
                    l(j())
                }) : l(j()),
                d.addEventListener("focusin", o, !0),
                d.addEventListener("mousedown", n, {
                    capture: !0,
                    passive: !1
                }),
                d.addEventListener("touchstart", n, {
                    capture: !0,
                    passive: !1
                }),
                d.addEventListener("click", r, {
                    capture: !0,
                    passive: !1
                }),
                d.addEventListener("keydown", q, {
                    capture: !0,
                    passive: !1
                }),
                b
        }, t = function() {
            if (f.active)
                return d.removeEventListener("focusin", o, !0),
                d.removeEventListener("mousedown", n, !0),
                d.removeEventListener("touchstart", n, !0),
                d.removeEventListener("click", r, !0),
                d.removeEventListener("keydown", q, !0),
                b
        };
        return (b = {
            activate: function(a) {
                if (f.active)
                    return this;
                var e = g(a, "onActivate")
                  , h = g(a, "onPostActivate")
                  , b = g(a, "checkCanFocusTrap");
                b || k(),
                f.active = !0,
                f.paused = !1,
                f.nodeFocusedBeforeActivation = d.activeElement,
                e && e();
                var c = function() {
                    b && k(),
                    s(),
                    h && h()
                };
                return b ? (b(f.containers.concat()).then(c, c),
                this) : (c(),
                this)
            },
            deactivate: function(a) {
                if (!f.active)
                    return this;
                clearTimeout(f.delayInitialFocusTimer),
                f.delayInitialFocusTimer = void 0,
                t(),
                f.active = !1,
                f.paused = !1,
                bu.deactivateTrap(b);
                var d = g(a, "onDeactivate")
                  , i = g(a, "onPostDeactivate")
                  , e = g(a, "checkCanReturnFocus");
                d && d();
                var h = g(a, "returnFocus", "returnFocusOnDeactivate")
                  , c = function() {
                    by(function() {
                        h && l(m(f.nodeFocusedBeforeActivation)),
                        i && i()
                    })
                };
                return h && e ? (e(m(f.nodeFocusedBeforeActivation)).then(c, c),
                this) : (c(),
                this)
            },
            pause: function() {
                return f.paused || !f.active || (f.paused = !0,
                t()),
                this
            },
            unpause: function() {
                return f.paused && f.active && (f.paused = !1,
                k(),
                s()),
                this
            },
            updateContainerElements: function(a) {
                var b = [].concat(a).filter(Boolean);
                return f.containers = b.map(function(a) {
                    return "string" == typeof a ? d.querySelector(a) : a
                }),
                f.active && k(),
                this
            }
        }).updateContainerElements(c),
        b
    };
    function bD(a, b, d) {
        let c = !1;
        a.type.includes("shopify:section") ? b.hasAttribute("section") && b.getAttribute("section") === a.detail.sectionId && (c = !0) : a.type.includes("shopify:block") && a.target === b && (c = !0),
        c && d(a)
    }
    var o = class extends a6 {
        static get observedAttributes() {
            return ["open"]
        }
        constructor() {
            if (super(),
            Shopify.designMode && (this.rootDelegate.on("shopify:section:select", a=>bD(a, this, ()=>this.open = !0)),
            this.rootDelegate.on("shopify:section:deselect", a=>bD(a, this, ()=>this.open = !1))),
            this.hasAttribute("append-body")) {
                let a = document.getElementById(this.id);
                this.removeAttribute("append-body"),
                a && a !== this ? (a.replaceWith(this.cloneNode(!0)),
                this.remove()) : document.body.appendChild(this)
            }
        }
        connectedCallback() {
            this.delegate.on("click", ".openable__overlay", ()=>this.open = !1),
            this.delegate.on("click", '[data-action="close"]', a=>{
                a.stopPropagation(),
                this.open = !1
            }
            )
        }
        get requiresLoading() {
            return this.hasAttribute("href")
        }
        get open() {
            return this.hasAttribute("open")
        }
        set open(a) {
            a ? (async()=>{
                await this._load(),
                this.clientWidth,
                this.setAttribute("open", "")
            }
            )() : this.removeAttribute("open")
        }
        get shouldTrapFocus() {
            return !0
        }
        get returnFocusOnDeactivate() {
            return !this.hasAttribute("return-focus") || "true" === this.getAttribute("return-focus")
        }
        get focusTrap() {
            return this._focusTrap = this._focusTrap || bC(this, {
                fallbackFocus: this,
                initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
                clickOutsideDeactivates: a=>!(a.target.hasAttribute("aria-controls") && a.target.getAttribute("aria-controls") === this.id),
                allowOutsideClick: a=>a.target.hasAttribute("aria-controls") && a.target.getAttribute("aria-controls") === this.id,
                returnFocusOnDeactivate: this.returnFocusOnDeactivate,
                onDeactivate: ()=>this.open = !1,
                preventScroll: !0
            })
        }
        attributeChangedCallback(b, c, a) {
            "open" === b && (null === c && "" === a ? (this.shouldTrapFocus && setTimeout(()=>this.focusTrap.activate(), 150),
            a4(this, "openable-element:open")) : null === a && (this.shouldTrapFocus && this.focusTrap.deactivate(),
            a4(this, "openable-element:close")))
        }
        async _load() {
            if (!this.requiresLoading)
                return;
            a5(this, "openable-element:load:start");
            let b = await fetch(this.getAttribute("href"))
              , a = document.createElement("div");
            a.innerHTML = await b.text(),
            this.innerHTML = a.querySelector(this.tagName.toLowerCase()).innerHTML,
            this.removeAttribute("href"),
            a5(this, "openable-element:load:end")
        }
    }
    ;
    window.customElements.define("openable-element", o);
    var p = class extends o {
        constructor() {
            super(),
            this.ignoreNextTransition = this.open,
            this.addEventListener("shopify:block:select", ()=>this.open = !0),
            this.addEventListener("shopify:block:deselect", ()=>this.open = !1)
        }
        get animateItems() {
            return this.hasAttribute("animate-items")
        }
        attributeChangedCallback(b) {
            if (this.ignoreNextTransition)
                return this.ignoreNextTransition = !1;
            if ("open" === b) {
                this.style.overflow = "hidden";
                let a = {
                    height: ["0px", `${this.scrollHeight}px`],
                    visibility: ["hidden", "visible"]
                };
                this.animateItems && (a.opacity = this.open ? [0, 0] : [0, 1]),
                this.animate(a, {
                    duration: 500,
                    direction: this.open ? "normal" : "reverse",
                    easing: "cubic-bezier(0.75, 0, 0.175, 1)"
                }).onfinish = ()=>{
                    this.style.overflow = this.open ? "visible" : "hidden"
                }
                ,
                this.animateItems && this.open && this.animate({
                    opacity: [0, 1],
                    transform: ["translateY(10px)", "translateY(0)"]
                }, {
                    duration: 250,
                    delay: 250,
                    easing: "cubic-bezier(0.75, 0, 0.175, 1)"
                }),
                a4(this, this.open ? "openable-element:open" : "openable-element:close")
            }
        }
    }
    ;
    window.customElements.define("collapsible-content", p);
    var q = class extends HTMLButtonElement {
        connectedCallback() {
            this.addEventListener("click", a=>{
                window.confirm(this.getAttribute("data-message") || "Are you sure you wish to do this?") || a.preventDefault()
            }
            )
        }
    }
    ;
    window.customElements.define("confirm-button", q, {
        extends: "button"
    });
    var e = {
        _prepareButton() {
            this.originalContent = this.innerHTML,
            this._startTransitionPromise = null,
            this.innerHTML = `
      <span class="loader-button__text">${this.innerHTML}</span>
      <span class="loader-button__loader" hidden>
        <div class="spinner">
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
        </div>
      </span>
    `,
            this.textElement = this.firstElementChild,
            this.spinnerElement = this.lastElementChild,
            window.addEventListener("pagehide", ()=>this.removeAttribute("aria-busy"))
        },
        _startTransition() {
            let a = this.textElement.animate({
                opacity: [1, 0],
                transform: ["translateY(0)", "translateY(-10px)"]
            }, {
                duration: 75,
                easing: "ease",
                fill: "forwards"
            });
            this.spinnerElement.hidden = !1;
            let b = this.spinnerElement.animate({
                opacity: [0, 1],
                transform: ["translate(-50%, 0%)", "translate(-50%, -50%)"]
            }, {
                duration: 75,
                delay: 75,
                easing: "ease",
                fill: "forwards"
            });
            this._startTransitionPromise = Promise.all([new Promise(b=>a.onfinish = ()=>b()), new Promise(a=>b.onfinish = ()=>a())])
        },
        async _endTransition() {
            this._startTransitionPromise && (await this._startTransitionPromise,
            this.spinnerElement.animate({
                opacity: [1, 0],
                transform: ["translate(-50%, -50%)", "translate(-50%, -100%)"]
            }, {
                duration: 75,
                delay: 100,
                easing: "ease",
                fill: "forwards"
            }).onfinish = ()=>this.spinnerElement.hidden = !0,
            this.textElement.animate({
                opacity: [0, 1],
                transform: ["translateY(10px)", "translateY(0)"]
            }, {
                duration: 75,
                delay: 175,
                easing: "ease",
                fill: "forwards"
            }),
            this._startTransitionPromise = null)
        }
    }
      , f = class extends HTMLButtonElement {
        static get observedAttributes() {
            return ["aria-busy"]
        }
        constructor() {
            super(),
            this.addEventListener("click", a=>{
                "submit" === this.type && this.form && this.form.checkValidity() && !this.form.hasAttribute("is") && (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? (a.preventDefault(),
                this.setAttribute("aria-busy", "true"),
                setTimeout(()=>this.form.submit(), 250)) : this.setAttribute("aria-busy", "true"))
            }
            )
        }
        connectedCallback() {
            this._prepareButton()
        }
        disconnectedCallback() {
            this.innerHTML = this.originalContent
        }
        attributeChangedCallback(a, c, b) {
            "aria-busy" === a && ("true" === b ? this._startTransition() : this._endTransition())
        }
    }
    ;
    Object.assign(f.prototype, e),
    window.customElements.define("loader-button", f, {
        extends: "button"
    });
    var r = class extends a6 {
        connectedCallback() {
            this.hasAttribute("ajax") && this.delegate.on("click", "a", this._onLinkClicked.bind(this))
        }
        _onLinkClicked(b, c) {
            // b.preventDefault();
            // let a = new URL(window.location.href);
            // a.searchParams.set("page", c.getAttribute("data-page")),
            // a4(this, "pagination:page-changed", {
            //     url: a.toString()
            // });
            renderSearchSpring(c.getAttribute("data-page")); 
        }
    }
    ;
    window.customElements.define("page-pagination", r);
    var g = class extends HTMLButtonElement {
        static get observedAttributes() {
            return ["aria-expanded", "aria-busy"]
        }
        constructor() {
            super(),
            this.hasAttribute("loader") && this._prepareButton(),
            this.addEventListener("click", this._onButtonClick.bind(this)),
            this.rootDelegate = new m(document.documentElement);
            this.isSizeBtnClicked = !1;
        }
        _onButtonClick(e) {
            const target = e.target.querySelector(".mobile-toolbar__item-label")?.textContent ?? e.target.textContent;
            const isSizeFilter = e.target.hasAttribute('data-size-filter');
            const controlledElementId = this.getAttribute("aria-controls");
            if (isSizeFilter && controlledElementId === "facet-filters") {
                const filterPanel = this.controlledElement;
        
                if (filterPanel) {
                    if (filterPanel.classList.contains('searchspring-facet-filters')) {
                        const sizeFilterButton = filterPanel
                            .querySelector('button[aria-controls*="size"], button[aria-controls*="Size"]');
                        if (sizeFilterButton) {
                            sizeFilterButton.setAttribute("aria-expanded", "true");
                            this.isSizeBtnClicked = true;
                        }
                    } else {
                        const sizeFilterButton = filterPanel
                            .querySelector('button[aria-controls="facet-filter-filter.v.option.size"]');
                        if (sizeFilterButton) {
                            sizeFilterButton.setAttribute("aria-expanded", "true");
                            this.isSizeBtnClicked = true;
                        }
                    }
                }
            }
        
            this.isExpanded = !this.isExpanded;
        }
        connectedCallback() {
            document.addEventListener("openable-element:close", a=>{
                const controlledElementId = this.getAttribute("aria-controls");
                
                if (this.controlledElement === a.target && 
                    (controlledElementId === "facet-filters" || controlledElementId === "searchspring-facet-filters") && 
                    this.isSizeBtnClicked) {
                    
                    // Handle regular Shopify filters
                    if (controlledElementId === "facet-filters") {
                        this.controlledElement.querySelector('button[aria-controls="facet-filter-filter.v.option.size"]').setAttribute("aria-expanded", "false");
                    }
                    // Handle SearchSpring filters
                    else if (controlledElementId === "searchspring-facet-filters") {
                        const sizeFilterButton = this.controlledElement.querySelector('button[aria-controls*="size"], button[aria-controls*="Size"]');
                        if (sizeFilterButton) {
                            sizeFilterButton.setAttribute("aria-expanded", "false");
                        }
                    }
                    
                    this.isSizeBtnClicked = false;
                }
        
                this.controlledElement === a.target && (this.isExpanded = !1,
                a.stopPropagation())
            }),
            document.addEventListener("openable-element:open", a=>{
                this.controlledElement === a.target && (this.isExpanded = !0,
                a.stopPropagation())
            }),
            this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, ()=>{
                this.classList.contains("button") ? this.setAttribute("aria-busy", "true") : null !== this.offsetParent && a4(document.documentElement, "theme:loading:start")
            }, !0),
            this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, ()=>{
                this.classList.contains("button") ? this.removeAttribute("aria-busy") : null !== this.offsetParent && a4(document.documentElement, "theme:loading:end")
            }, !0)
        }
        // _onButtonClick(e) {
        //     const target = e.target.querySelector(".mobile-toolbar__item-label")?.textContent ?? e.target.textContent;
        //     const isSizeFilter = e.target.hasAttribute('data-size-filter');
        //     if (isSizeFilter && this.getAttribute("aria-controls") == "facet-filters") {
        //         this.controlledElement
        //             .querySelector('button[aria-controls="facet-filter-filter.v.option.size"]')
        //             .setAttribute("aria-expanded", "true");
        //         this.isSizeBtnClicked = true;
        //     }
        
        //     this.isExpanded = !this.isExpanded;
        // }
        // connectedCallback() {
        //     document.addEventListener("openable-element:close", a=>{
        //         if (this.controlledElement === a.target && this.getAttribute("aria-controls") == "facet-filters" && this.isSizeBtnClicked) {
        //             this.controlledElement.querySelector('button[aria-controls="facet-filter-filter.v.option.size"]').setAttribute("aria-expanded", "false");
        //             this.isSizeBtnClicked = false;
        //         }

        //         this.controlledElement === a.target && (this.isExpanded = !1,
        //         a.stopPropagation())
        //     }
        //     ),
        //     document.addEventListener("openable-element:open", a=>{
        //         this.controlledElement === a.target && (this.isExpanded = !0,
        //         a.stopPropagation())
        //     }
        //     ),
        //     this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, ()=>{
        //         this.classList.contains("button") ? this.setAttribute("aria-busy", "true") : null !== this.offsetParent && a4(document.documentElement, "theme:loading:start")
        //     }
        //     , !0),
        //     this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, ()=>{
        //         this.classList.contains("button") ? this.removeAttribute("aria-busy") : null !== this.offsetParent && a4(document.documentElement, "theme:loading:end")
        //     }
        //     , !0)
        // }
        disconnectedCallback() {
            this.rootDelegate.destroy()
        }
        get isExpanded() {
            return "true" === this.getAttribute("aria-expanded")
        }
        set isExpanded(a) {
            this.setAttribute("aria-expanded", a ? "true" : "false")
        }
        get controlledElement() {
            return document.getElementById(this.getAttribute("aria-controls"))
        }
        attributeChangedCallback(c, b, a) {
            switch (c) {
            case "aria-expanded":
                "false" === b && "true" === a ? this.controlledElement.open = !0 : "true" === b && "false" === a && (this.controlledElement.open = !1);
                break;
            case "aria-busy":
                this.hasAttribute("loader") && ("true" === a ? this._startTransition() : this._endTransition())
            }
        }
    }
    ;
    Object.assign(g.prototype, e),
    window.customElements.define("toggle-button", g, {
        extends: "button"
    });
    var s = class extends HTMLAnchorElement {
        static get observedAttributes() {
            return ["aria-expanded"]
        }
        constructor() {
            super(),
            this.addEventListener("click", a=>{
                a.preventDefault(),
                this.isExpanded = !this.isExpanded
            }
            ),
            this.rootDelegate = new m(document.documentElement)
        }
        connectedCallback() {
            this.rootDelegate.on("openable-element:close", `#${this.getAttribute("aria-controls")}`, a=>{
                this.controlledElement === a.target && (this.isExpanded = !1)
            }
            , !0),
            this.rootDelegate.on("openable-element:open", `#${this.getAttribute("aria-controls")}`, a=>{
                this.controlledElement === a.target && (this.isExpanded = !0)
            }
            , !0)
        }
        disconnectedCallback() {
            this.rootDelegate.destroy()
        }
        get isExpanded() {
            return "true" === this.getAttribute("aria-expanded")
        }
        set isExpanded(a) {
            this.setAttribute("aria-expanded", a ? "true" : "false")
        }
        get controlledElement() {
            return document.querySelector(`#${this.getAttribute("aria-controls")}`)
        }
        attributeChangedCallback(c, a, b) {
            "aria-expanded" === c && ("false" === a && "true" === b ? this.controlledElement.open = !0 : "true" === a && "false" === b && (this.controlledElement.open = !1))
        }
    }
    ;
    window.customElements.define("toggle-link", s, {
        extends: "a"
    });
    var t = class extends a6 {
        connectedCallback() {
            this.buttons = Array.from(this.querySelectorAll("button")),
            this.delegate.on("click", "button", (b,a)=>{
                this._dispatchEvent(this.buttons.indexOf(a))
            }
            ),
            this.hasAttribute("animation-timer") && this.delegate.on("animationend", a=>{
                a.elapsedTime > 0 && this._dispatchEvent((this.selectedIndex + 1 + this.buttons.length) % this.buttons.length)
            }
            )
        }
        get selectedIndex() {
            return this.buttons.findIndex(a=>"true" === a.getAttribute("aria-current"))
        }
        set selectedIndex(c) {
            if (this.buttons.forEach((a,b)=>a.setAttribute("aria-current", c === b ? "true" : "false")),
            this.hasAttribute("align-selected")) {
                let d = this.buttons[c]
                  , e = window.innerWidth / 2
                  , b = d.getBoundingClientRect()
                  , a = this._findFirstScrollableElement(this.parentElement);
                a && a.scrollTo({
                    behavior: "smooth",
                    left: a.scrollLeft + (b.left - e) + b.width / 2
                })
            }
        }
        _dispatchEvent(a) {
            a !== this.selectedIndex && this.dispatchEvent(new CustomEvent("page-dots:changed",{
                bubbles: !0,
                detail: {
                    index: a
                }
            }))
        }
        _findFirstScrollableElement(a, b=0) {
            return null === a || b > 3 ? null : a.scrollWidth > a.clientWidth ? a : this._findFirstScrollableElement(a.parentElement, b + 1)
        }
    }
    ;
    window.customElements.define("page-dots", t);
    var u = class extends HTMLElement {
        connectedCallback() {
            this.prevButton = this.querySelector("button:first-of-type"),
            this.nextButton = this.querySelector("button:last-of-type"),
            this.prevButton.addEventListener("click", ()=>this.prevButton.dispatchEvent(new CustomEvent("prev-next:prev",{
                bubbles: !0
            }))),
            this.nextButton.addEventListener("click", ()=>this.nextButton.dispatchEvent(new CustomEvent("prev-next:next",{
                bubbles: !0
            })))
        }
        set isPrevDisabled(a) {
            this.prevButton.disabled = a
        }
        set isNextDisabled(a) {
            this.nextButton.disabled = a
        }
    }
      , v = class extends HTMLButtonElement {
        connectedCallback() {
            this.addEventListener("click", ()=>this.dispatchEvent(new CustomEvent("prev-next:prev",{
                bubbles: !0
            })))
        }
    }
      , w = class extends HTMLButtonElement {
        connectedCallback() {
            this.addEventListener("click", ()=>this.dispatchEvent(new CustomEvent("prev-next:next",{
                bubbles: !0
            })))
        }
    }
    ;
    function bE() {
        let a = getComputedStyle(document.documentElement);
        return parseInt(a.getPropertyValue("--header-height") || 0) * parseInt(a.getPropertyValue("--enable-sticky-header") || 0) + parseInt(a.getPropertyValue("--announcement-bar-height") || 0) * parseInt(a.getPropertyValue("--enable-sticky-announcement-bar") || 0)
    }
    window.customElements.define("prev-next-buttons", u),
    window.customElements.define("prev-button", v, {
        extends: "button"
    }),
    window.customElements.define("next-button", w, {
        extends: "button"
    });
    var x = class extends HTMLElement {
        connectedCallback() {
            this.lastKnownY = window.scrollY,
            this.currentTop = 0,
            this.hasPendingRaf = !1,
            window.addEventListener("scroll", this._checkPosition.bind(this))
        }
        get initialTopOffset() {
            return bE() + (parseInt(this.getAttribute("offset")) || 0)
        }
        _checkPosition() {
            this.hasPendingRaf || (this.hasPendingRaf = !0,
            requestAnimationFrame(()=>{
                let a = this.getBoundingClientRect().top + window.scrollY - this.offsetTop + this.initialTopOffset
                  , b = this.clientHeight - window.innerHeight;
                window.scrollY < this.lastKnownY ? this.currentTop -= window.scrollY - this.lastKnownY : this.currentTop += this.lastKnownY - window.scrollY,
                this.currentTop = Math.min(Math.max(this.currentTop, -b), a, this.initialTopOffset),
                this.lastKnownY = window.scrollY,
                this.style.top = `${this.currentTop}px`,
                this.hasPendingRaf = !1
            }
            ))
        }
    }
    ;
    function bF(b, c=15) {
        let d = null
          , e = null
          , a = f=>{
            e = f,
            d || (b(e),
            e = null,
            d = setTimeout(()=>{
                d = null,
                e && a(e)
            }
            , c))
        }
        ;
        return a
    }
    window.customElements.define("safe-sticky", x);
    var y = class extends HTMLElement {
        connectedCallback() {
            this._createSvg(),
            this.elementsToObserve = Array.from(this.querySelectorAll("a")).map(a=>document.querySelector(a.getAttribute("href"))),
            this.navListItems = Array.from(this.querySelectorAll("li")),
            this.navItems = this.navListItems.map(b=>{
                let a = b.firstElementChild
                  , c = a && a.getAttribute("href").slice(1)
                  , d = document.getElementById(c);
                return {
                    listItem: b,
                    anchor: a,
                    target: d
                }
            }
            ).filter(a=>a.target),
            this.drawPath(),
            window.addEventListener("scroll", bF(this.markVisibleSection.bind(this), 25)),
            window.addEventListener("orientationchange", ()=>{
                window.addEventListener("resize", ()=>{
                    this.drawPath(),
                    this.markVisibleSection()
                }
                , {
                    once: !0
                })
            }
            ),
            this.markVisibleSection()
        }
        _createSvg() {
            this.navPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
            let a = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            a.insertAdjacentElement("beforeend", this.navPath),
            this.insertAdjacentElement("beforeend", a),
            this.lastPathStart = this.lastPathEnd = null
        }
        drawPath() {
            let a = [], b;
            this.navItems.forEach((c,g)=>{
                let d = c.anchor.offsetLeft - 5
                  , e = c.anchor.offsetTop
                  , f = c.anchor.offsetHeight;
                0 === g ? (a.push("M", d, e, "L", d, e + f),
                c.pathStart = 0) : (b !== d && a.push("L", b, e),
                a.push("L", d, e),
                this.navPath.setAttribute("d", a.join(" ")),
                c.pathStart = this.navPath.getTotalLength() || 0,
                a.push("L", d, e + f)),
                b = d,
                this.navPath.setAttribute("d", a.join(" ")),
                c.pathEnd = this.navPath.getTotalLength()
            }
            )
        }
        syncPath() {
            let e = a=>a.classList.contains("is-visible")
              , c = this.navPath.getTotalLength()
              , a = c
              , b = 0;
            if (this.navItems.forEach(c=>{
                e(c.listItem) && (a = Math.min(c.pathStart, a),
                b = Math.max(c.pathEnd, b))
            }
            ),
            this.querySelectorAll(".is-visible").length > 0 && a < b) {
                if (a !== this.lastPathStart || b !== this.lastPathEnd) {
                    let d = `1 ${a} ${b - a} ${c}`;
                    this.navPath.style.setProperty("stroke-dashoffset", "1"),
                    this.navPath.style.setProperty("stroke-dasharray", d),
                    this.navPath.style.setProperty("opacity", "1")
                }
            } else
                this.navPath.style.setProperty("opacity", "0");
            this.lastPathStart = a,
            this.lastPathEnd = b
        }
        markVisibleSection() {
            for (let[b,a] of (this.navListItems.forEach(a=>a.classList.remove("is-visible")),
            this.elementsToObserve.entries())) {
                let c = a.getBoundingClientRect();
                if (c.top > bE() || b === this.elementsToObserve.length - 1) {
                    this.querySelector(`a[href="#${a.id}"]`).parentElement.classList.add("is-visible");
                    break
                }
            }
            this.syncPath()
        }
    }
    ;
    window.customElements.define("scroll-spy", y);
    var bG, bH, z = class extends HTMLElement {
        constructor() {
            super(),
            this.attachShadow({
                mode: "open"
            }).innerHTML = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(0,0,0,.2), rgba(0,0,0,0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
`,
            this.updater = new class {
                constructor(a) {
                    this.scheduleUpdate = bF(()=>this.update(a, getComputedStyle(a))),
                    this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this))
                }
                start(a) {
                    this.element && this.stop(),
                    a && (a.addEventListener("scroll", this.scheduleUpdate),
                    this.resizeObserver.observe(a),
                    this.element = a)
                }
                stop() {
                    this.element && (this.element.removeEventListener("scroll", this.scheduleUpdate),
                    this.resizeObserver.unobserve(this.element),
                    this.element = null)
                }
                update(b, a) {
                    if (!this.element)
                        return;
                    let c = a.getPropertyValue("--scroll-shadow-size") ? parseInt(a.getPropertyValue("--scroll-shadow-size")) : 0
                      , d = {
                        top: Math.max(this.element.scrollTop, 0),
                        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
                        left: Math.max(this.element.scrollLeft, 0),
                        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
                    };
                    requestAnimationFrame(()=>{
                        for (let a of ["top", "bottom", "left", "right"])
                            b.style.setProperty(`--${a}`, `${d[a] > c ? c : d[a]}px`)
                    }
                    )
                }
            }
            (this.shadowRoot.lastElementChild)
        }
        connectedCallback() {
            this.shadowRoot.querySelector("slot").addEventListener("slotchange", ()=>this.start()),
            this.start()
        }
        disconnectedCallback() {
            this.updater.stop()
        }
        start() {
            this.updater.start(this.firstElementChild)
        }
    }
    ;
    "ResizeObserver"in window && window.customElements.define("scroll-shadow", z);
    var A = class extends g {
        _onButtonClick() {
            window.matchMedia(window.themeVariables.breakpoints.phone).matches && navigator.share ? navigator.share({
                title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
                url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
            }) : super._onButtonClick()
        }
    }
    ;
    window.customElements.define("share-toggle-button", A, {
        extends: "button"
    });
    var B = class extends a6 {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll("native-carousel-item")),
            this.pageDotsElements = Array.from(this.querySelectorAll("page-dots")),
            this.prevNextButtonsElements = Array.from(this.querySelectorAll("prev-next-buttons")),
            this.items.length > 1 && (this.addEventListener("prev-next:prev", this.prev.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            this.addEventListener("page-dots:changed", a=>this.select(a.detail.index, !0)),
            Shopify.designMode && this.addEventListener("shopify:block:select", a=>this.select(a.target.index, !a.detail.load)));
            let a = this.items[0].parentElement;
            this.intersectionObserver = new IntersectionObserver(this._onVisibilityChanged.bind(this),{
                root: a,
                rootMargin: `${a.clientHeight}px 0px`,
                threshold: .8
            }),
            this.items.forEach(a=>this.intersectionObserver.observe(a))
        }
        disconnectedCallback() {
            super.disconnectedCallback(),
            this.intersectionObserver.disconnect()
        }
        get selectedIndex() {
            return this.items.findIndex(a=>a.selected)
        }
        prev(a=!0) {
            this.select(Math.max(this.selectedIndex - 1, 0), a)
        }
        next(a=!0) {
            this.select(Math.min(this.selectedIndex + 1, this.items.length - 1), a)
        }
        select(d, b=!0) {
            let c = Math.max(0, Math.min(d, this.items.length))
              , a = this.items[c];
            this._adjustNavigationForElement(a),
            b && (this.items.forEach(a=>this.intersectionObserver.unobserve(a)),
            setInterval(()=>{
                this.items.forEach(a=>this.intersectionObserver.observe(a))
            }
            , 800)),
            this.items.forEach((a,b)=>a.selected = b === c);
            let e = "ltr" === window.themeVariables.settings.direction ? 1 : -1;
            a.parentElement.scrollTo({
                left: e * (a.clientWidth * c),
                behavior: b ? "smooth" : "auto"
            })
        }
        _adjustNavigationForElement(a) {
            this.items.forEach(b=>b.selected = a === b),
            this.pageDotsElements.forEach(b=>b.selectedIndex = a.index),
            this.prevNextButtonsElements.forEach(b=>{
                b.isPrevDisabled = 0 === a.index,
                b.isNextDisabled = a.index === this.items.length - 1
            }
            )
        }
        _onVisibilityChanged(b) {
            for (let a of b)
                if (a.isIntersecting) {
                    this._adjustNavigationForElement(a.target);
                    break
                }
        }
    }
      , C = class extends a6 {
        static get observedAttributes() {
            return ["hidden"]
        }
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        set selected(a) {
            this.hidden = !a
        }
    }
    ;
    window.customElements.define("native-carousel-item", C),
    window.customElements.define("native-carousel", B);
    var D = class extends HTMLElement {
        connectedCallback() {
            this.scrollableElement = this.parentElement,
            this.scrollableElement.addEventListener("mouseenter", this._onMouseEnter.bind(this)),
            this.scrollableElement.addEventListener("mousemove", this._onMouseMove.bind(this)),
            this.scrollableElement.addEventListener("mouseleave", this._onMouseLeave.bind(this)),
            this.innerHTML = `
      <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
        <path d="M0 60C0 26.863 26.863 0 60 0s60 26.863 60 60-26.863 60-60 60S0 93.137 0 60z" fill="rgb(var(--text-color))"/>
        <path d="M46 50L36 60l10 10M74 50l10 10-10 10" stroke="rgb(var(--section-background))" stroke-width="4"/>
      </svg>
    `
        }
        _onMouseEnter(a) {
            this.removeAttribute("hidden"),
            this._positionCursor(a)
        }
        _onMouseLeave() {
            this.setAttribute("hidden", "")
        }
        _onMouseMove(a) {
            this.toggleAttribute("hidden", "BUTTON" === a.target.tagName || "A" === a.target.tagName),
            this._positionCursor(a)
        }
        _positionCursor(a) {
            let b = this.scrollableElement.getBoundingClientRect()
              , c = a.clientX - b.x
              , d = a.clientY - b.y;
            this.style.transform = `translate(${c - this.clientWidth / 2}px, ${d - this.clientHeight / 2}px)`
        }
    }
    ;
    window.customElements.define("drag-cursor", D);
    var E = class extends a6 {
        connectedCallback() {
            this.draggable && this._setupDraggability(),
            this._checkScrollability(),
            window.addEventListener("resize", this._checkScrollability.bind(this)),
            this.addEventListener("scroll", bF(this._calculateProgress.bind(this), 15))
        }
        get draggable() {
            return this.hasAttribute("draggable")
        }
        _setupDraggability() {
            this.insertAdjacentHTML("afterend", '<drag-cursor hidden class="custom-drag-cursor"></drag-cursor>');
            let a = matchMedia("(hover: none)");
            a.addListener(this._onMediaChanges.bind(this)),
            a.matches || this._attachDraggableListeners()
        }
        _attachDraggableListeners() {
            this.delegate.on("mousedown", this._onMouseDown.bind(this)),
            this.delegate.on("mousemove", this._onMouseMove.bind(this)),
            this.delegate.on("mouseup", this._onMouseUp.bind(this))
        }
        _removeDraggableListeners() {
            this.delegate.off("mousedown"),
            this.delegate.off("mousemove"),
            this.delegate.off("mouseup")
        }
        _checkScrollability() {
            this.classList.toggle("is-scrollable", this.scrollWidth > this.offsetWidth)
        }
        _calculateProgress() {
            let a = this.scrollLeft * ("ltr" === window.themeVariables.settings.direction ? 1 : -1)
              , b = 100 * Math.max(0, Math.min(1, a / (this.scrollWidth - this.clientWidth)));
            a4(this, "scrollable-content:progress", {
                progress: b
            })
        }
        _onMediaChanges(a) {
            a.matches ? this._removeDraggableListeners() : this._attachDraggableListeners()
        }
        _onMouseDown(a) {
            a.target && "IMG" === a.target.nodeName && a.preventDefault(),
            this.startX = a.clientX + this.scrollLeft,
            this.diffX = 0,
            this.drag = !0
        }
        _onMouseMove(a) {
            this.drag && (this.diffX = this.startX - (a.clientX + this.scrollLeft),
            this.scrollLeft += this.diffX)
        }
        _onMouseUp() {
            this.drag = !1;
            let b = 1
              , a = ()=>{
                let c = Math.sinh(b);
                c <= 0 ? window.cancelAnimationFrame(a) : (this.scrollLeft += this.diffX * c,
                b -= .03,
                window.requestAnimationFrame(a))
            }
            ;
            a()
        }
    }
    ;
    window.customElements.define("scrollable-content", E);
    var F = class extends a6 {
        constructor() {
            super(),
            this.rootDelegate.on("theme:loading:start", this.show.bind(this)),
            this.rootDelegate.on("theme:loading:end", this.hide.bind(this)),
            this.delegate.on("transitionend", this._onTransitionEnd.bind(this))
        }
        show() {
            this.classList.add("is-visible"),
            this.style.transform = "scaleX(0.4)"
        }
        hide() {
            this.style.transform = "scaleX(1)",
            this.classList.add("is-finished")
        }
        _onTransitionEnd(a) {
            "transform" === a.propertyName && this.classList.contains("is-finished") && (this.classList.remove("is-visible"),
            this.classList.remove("is-finished"),
            this.style.transform = "scaleX(0)")
        }
    }
    ;
    window.customElements.define("loading-bar", F);
    var G = class extends HTMLElement {
        connectedCallback() {
            this.originalContent = this.textContent,
            this.lastWidth = window.innerWidth,
            this.hasBeenSplitted = !1,
            window.addEventListener("resize", this._onResize.bind(this))
        }
        [Symbol.asyncIterator]() {
            return {
                splitPromise: this.split.bind(this),
                index: 0,
                async next() {
                    let a = await this.splitPromise();
                    return this.index !== a.length ? {
                        done: !1,
                        value: a[this.index++]
                    } : {
                        done: !0
                    }
                }
            }
        }
        split(a=!1) {
            return this.childElementCount > 0 && !a ? Promise.resolve(Array.from(this.children)) : (this.hasBeenSplitted = !0,
            new Promise(b=>{
                requestAnimationFrame(()=>{
                    this.innerHTML = this.originalContent.replace(/./g, "<span>$&</span>").replace(/\s/g, " ");
                    let c = {};
                    Array.from(this.children).forEach(a=>{
                        let b = parseInt(a.getBoundingClientRect().top);
                        c[b] = (c[b] || "") + a.textContent
                    }
                    ),
                    this.innerHTML = Object.values(c).map(b=>`<span ${this.hasAttribute("reveal") && !a ? "reveal" : ""} ${this.hasAttribute("reveal-visibility") && !a ? "reveal-visibility" : ""} style="display: block">${b.trim()}</span>`).join(""),
                    this.style.opacity = this.hasAttribute("reveal") ? 1 : null,
                    this.style.visibility = this.hasAttribute("reveal-visibility") ? "visible" : null,
                    b(Array.from(this.children))
                }
                )
            }
            ))
        }
        async _onResize() {
            this.lastWidth !== window.innerWidth && this.hasBeenSplitted && (await this.split(!0),
            this.dispatchEvent(new CustomEvent("split-lines:re-split",{
                bubbles: !0
            })),
            this.lastWidth = window.innerWidth)
        }
    }
    ;
    window.customElements.define("split-lines", G);
    var H = class extends o {
        connectedCallback() {
            super.connectedCallback(),
            this.delegate.on("click", ".popover__overlay", ()=>this.open = !1)
        }
        attributeChangedCallback(a, b, c) {
            super.attributeChangedCallback(a, b, c),
            "open" === a && document.documentElement.classList.toggle("lock-mobile", this.open)
        }
    }
    ;
    window.customElements.define("popover-content", H);
    var I = class extends HTMLElement {
        connectedCallback() {
            this.buttons = Array.from(this.querySelectorAll("button[aria-controls]")),
            this.scrollerElement = this.querySelector(".tabs-nav__scroller"),
            this.buttons.forEach(a=>a.addEventListener("click", ()=>this.selectButton(a))),
            this.addEventListener("shopify:block:select", a=>this.selectButton(a.target, !a.detail.load)),
            this.positionElement = document.createElement("span"),
            this.positionElement.classList.add("tabs-nav__position"),
            this.buttons[0].parentElement.insertAdjacentElement("afterend", this.positionElement),
            window.addEventListener("resize", this._onWindowResized.bind(this)),
            this._adjustNavigationPosition(),
            this.hasArrows && this._handleArrows()
        }
        get hasArrows() {
            return this.hasAttribute("arrows")
        }
        get selectedTabIndex() {
            return this.buttons.findIndex(a=>"true" === a.getAttribute("aria-expanded"))
        }
        get selectedButton() {
            return this.buttons.find(a=>"true" === a.getAttribute("aria-expanded"))
        }
        selectButton(a, d=!0) {
            if (!this.buttons.includes(a) || this.selectedButton === a)
                return;
            let b = document.getElementById(this.selectedButton.getAttribute("aria-controls"))
              , c = document.getElementById(a.getAttribute("aria-controls"));
            d ? this._transitionContent(b, c) : (b.hidden = !0,
            c.hidden = !1),
            this.selectedButton.setAttribute("aria-expanded", "false"),
            a.setAttribute("aria-expanded", "true"),
            a4(this, "tabs-nav:changed", {
                button: a
            }),
            this._adjustNavigationPosition()
        }
        addButton(a) {
            a.addEventListener("click", ()=>this.selectButton(a)),
            a.setAttribute("aria-expanded", "false"),
            this.buttons[this.buttons.length - 1].insertAdjacentElement("afterend", a),
            this.buttons.push(a),
            this._adjustNavigationPosition(!1)
        }
        _transitionContent(a, b) {
            a.animate({
                opacity: [1, 0]
            }, {
                duration: 250,
                easing: "ease"
            }).onfinish = ()=>{
                a.hidden = !0,
                b.hidden = !1,
                b.animate({
                    opacity: [0, 1]
                }, {
                    duration: 250,
                    easing: "ease"
                })
            }
        }
        _onWindowResized() {
            this._adjustNavigationPosition()
        }
        _adjustNavigationPosition(a=!0) {
            let b = this.selectedButton.clientWidth / this.positionElement.parentElement.clientWidth
              , c = this.selectedButton.offsetLeft / this.positionElement.parentElement.clientWidth / b
              , d = this.scrollerElement.clientWidth / 2;
            this.scrollerElement.scrollTo({
                behavior: a ? "smooth" : "auto",
                left: this.selectedButton.offsetLeft - d + this.selectedButton.clientWidth / 2
            }),
            a || (this.positionElement.style.transition = "none"),
            this.positionElement.style.setProperty("--scale", b),
            this.positionElement.style.setProperty("--translate", `${100 * c}%`),
            this.positionElement.clientWidth,
            requestAnimationFrame(()=>{
                this.positionElement.classList.add("is-initialized"),
                this.positionElement.style.transition = null
            }
            )
        }
        _handleArrows() {
            let a = this.querySelector(".tabs-nav__arrows");
            a.firstElementChild.addEventListener("click", ()=>{
                this.selectButton(this.buttons[Math.max(this.selectedTabIndex - 1, 0)])
            }
            ),
            a.lastElementChild.addEventListener("click", ()=>{
                this.selectButton(this.buttons[Math.min(this.selectedTabIndex + 1, this.buttons.length - 1)])
            }
            )
        }
    }
    ;
    window.customElements.define("tabs-nav", I);
    var J = class {
        static load(d) {
            let c = "requested"
              , e = "loaded"
              , a = this.libraries[d];
            if (!a)
                return;
            if (a.status === c)
                return a.promise;
            if (a.status === e)
                return Promise.resolve();
            let b;
            return b = new Promise("script" === a.type ? (d,c)=>{
                let b = document.createElement("script");
                b.id = a.tagId,
                b.src = a.src,
                b.onerror = c,
                b.onload = ()=>{
                    a.status = e,
                    d()
                }
                ,
                document.body.appendChild(b)
            }
            : (d,c)=>{
                let b = document.createElement("link");
                b.id = a.tagId,
                b.href = a.src,
                b.rel = "stylesheet",
                b.type = "text/css",
                b.onerror = c,
                b.onload = ()=>{
                    a.status = e,
                    d()
                }
                ,
                document.body.appendChild(b)
            }
            ),
            a.promise = b,
            a.status = c,
            b
        }
    }
    ;
    b(J, "libraries", {
        flickity: {
            tagId: "flickity",
            src: window.themeVariables.libs.flickity,
            type: "script"
        },
        photoswipe: {
            tagId: "photoswipe",
            src: window.themeVariables.libs.photoswipe,
            type: "script"
        },
        qrCode: {
            tagId: "qrCode",
            src: window.themeVariables.libs.qrCode,
            type: "script"
        },
        modelViewerUiStyles: {
            tagId: "shopify-model-viewer-ui-styles",
            src: "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
            type: "link"
        }
    });
    var K = class extends HTMLElement {
        async connectedCallback() {
            await J.load("qrCode"),
            new window.QRCode(this,{
                text: this.getAttribute("identifier"),
                width: 200,
                height: 200
            })
        }
    }
    ;
    window.customElements.define("qr-code", K);
    var L = class extends HTMLSelectElement {
        connectedCallback() {
            if (this.provinceElement = document.getElementById(this.getAttribute("aria-owns")),
            this.addEventListener("change", this._updateProvinceVisibility.bind(this)),
            this.hasAttribute("data-default")) {
                for (let b = 0; b !== this.options.length; ++b)
                    if (this.options[b].text === this.getAttribute("data-default")) {
                        this.selectedIndex = b;
                        break
                    }
            }
            this._updateProvinceVisibility();
            let a = "SELECT" === this.provinceElement.tagName ? this.provinceElement : this.provinceElement.querySelector("select");
            if (a.hasAttribute("data-default")) {
                for (let c = 0; c !== a.options.length; ++c)
                    if (a.options[c].text === a.getAttribute("data-default")) {
                        a.selectedIndex = c;
                        break
                    }
            }
        }
        _updateProvinceVisibility() {
            let a = this.options[this.selectedIndex];
            if (!a)
                return;
            let b = JSON.parse(a.getAttribute("data-provinces") || "[]")
              , c = "SELECT" === this.provinceElement.tagName ? this.provinceElement : this.provinceElement.querySelector("select");
            if (c.innerHTML = "",
            0 === b.length) {
                this.provinceElement.hidden = !0;
                return
            }
            b.forEach(a=>{
                c.options.add(new Option(a[1],a[0]))
            }
            ),
            this.provinceElement.hidden = !1
        }
    }
    ;
    window.customElements.define("country-selector", L, {
        extends: "select"
    });
    var M = class extends o {
        connectedCallback() {
            super.connectedCallback(),
            this.appearAfterDelay && !(this.onlyOnce && this.hasAppearedOnce) && setTimeout(()=>this.open = !0, this.apparitionDelay),
            this.delegate.on("click", ".modal__overlay", ()=>this.open = !1)
        }
        get appearAfterDelay() {
            return this.hasAttribute("apparition-delay")
        }
        get apparitionDelay() {
            return 1e3 * parseInt(this.getAttribute("apparition-delay") || 0)
        }
        get onlyOnce() {
            return this.hasAttribute("only-once")
        }
        get hasAppearedOnce() {
            return null !== localStorage.getItem("theme:popup-appeared")
        }
        attributeChangedCallback(a, b, c) {
            super.attributeChangedCallback(a, b, c),
            "open" === a && (document.documentElement.classList.toggle("lock-all", this.open),
            this.open && localStorage.setItem("theme:popup-appeared", !0))
        }
    }
    ;
    window.customElements.define("modal-content", M);
    var N = class extends HTMLElement {
        connectedCallback() {
            if (this.id === 'ss-price-slider-container') return;
            this.rangeLowerBound = this.querySelector(".price-range__range-group input:first-child"),
            this.rangeHigherBound = this.querySelector(".price-range__range-group input:last-child"),
            this.textInputLowerBound = this.querySelector(".price-range__input:first-child input"),
            this.textInputHigherBound = this.querySelector(".price-range__input:last-child input"),
            this.textInputLowerBound.addEventListener("focus", ()=>this.textInputLowerBound.select()),
            this.textInputHigherBound.addEventListener("focus", ()=>this.textInputHigherBound.select()),
            this.textInputLowerBound.addEventListener("change", a=>{
                a.target.value = Math.max(Math.min(parseInt(a.target.value), parseInt(this.textInputHigherBound.value || a.target.max) - 1), a.target.min),
                this.rangeLowerBound.value = a.target.value,
                this.rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max) * 100}%`)
            }
            ),
            this.textInputHigherBound.addEventListener("change", a=>{
                a.target.value = Math.min(Math.max(parseInt(a.target.value), parseInt(this.textInputLowerBound.value || a.target.min) + 1), a.target.max),
                this.rangeHigherBound.value = a.target.value,
                this.rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max) * 100}%`)
            }
            ),
            this.rangeLowerBound.addEventListener("change", a=>{
                this.textInputLowerBound.value = a.target.value,
                this.textInputLowerBound.dispatchEvent(new Event("change",{
                    bubbles: !0
                }))
            }
            ),
            this.rangeHigherBound.addEventListener("change", a=>{
                this.textInputHigherBound.value = a.target.value,
                this.textInputHigherBound.dispatchEvent(new Event("change",{
                    bubbles: !0
                }))
            }
            ),
            this.rangeLowerBound.addEventListener("input", a=>{
                a4(this, "facet:abort-loading"),
                a.target.value = Math.min(parseInt(a.target.value), parseInt(this.textInputHigherBound.value || a.target.max) - 1),
                a.target.parentElement.style.setProperty("--range-min", `${parseInt(a.target.value) / parseInt(a.target.max) * 100}%`),
                this.textInputLowerBound.value = a.target.value
            }
            ),
            this.rangeHigherBound.addEventListener("input", a=>{
                a4(this, "facet:abort-loading"),
                a.target.value = Math.max(parseInt(a.target.value), parseInt(this.textInputLowerBound.value || a.target.min) + 1),
                a.target.parentElement.style.setProperty("--range-max", `${parseInt(a.target.value) / parseInt(a.target.max) * 100}%`),
                this.textInputHigherBound.value = a.target.value
            }
            )
        }
    }
    ;
    window.customElements.define("price-range", N);
    var O = class extends HTMLElement {
        connectedCallback() {
            let a = this.querySelector(".link-bar__link-item--selected");
            a && requestAnimationFrame(()=>{
                a.style.scrollSnapAlign = "none"
            }
            )
        }
    }
    ;
    window.customElements.define("link-bar", O);
    var bI = class {
        static prefersReducedMotion() {
            return window.matchMedia("(prefers-reduced-motion: reduce)").matches
        }
        static supportsHover() {
            return window.matchMedia("(pointer: fine)").matches
        }
    }
      , P = class extends a6 {
        constructor() {
            if (super(),
            1 === this.childElementCount)
                return;
            this.addEventListener("flickity:ready", this._preloadNextImage.bind(this)),
            this.addEventListener("flickity:slide-changed", this._preloadNextImage.bind(this)),
            this._createFlickity()
        }
        async disconnectedCallback() {
            if (this.flickity) {
                let a = await this.flickity;
                a.destroy()
            }
        }
        get flickityConfig() {
            return JSON.parse(this.getAttribute("flickity-config"))
        }
        get flickityInstance() {
            return this.flickity
        }
        async next() {
            (await this.flickityInstance).next()
        }
        async previous() {
            (await this.flickityInstance).previous()
        }
        async select(a) {
            (await this.flickityInstance).selectCell(a)
        }
        async setDraggable(b) {
            let a = await this.flickity;
            a.options.draggable = b,
            a.updateDraggable()
        }
        async reload() {
            let a = await this.flickity;
            a.destroy(),
            this.flickityConfig.cellSelector && Array.from(this.children).sort((a,b)=>parseInt(a.getAttribute("data-original-position")) > parseInt(b.getAttribute("data-original-position")) ? 1 : -1).forEach(a=>this.appendChild(a)),
            this._createFlickity()
        }
        async _createFlickity() {
            if (this.flickity = new Promise(async a=>{
                await J.load("flickity"),
                await this.untilVisible({
                    rootMargin: "400px",
                    threshold: 0
                });
                let b = new window.ThemeFlickity(this,{
                    ...this.flickityConfig,
                    rightToLeft: "rtl" === window.themeVariables.settings.direction,
                    accessibility: bI.supportsHover(),
                    on: {
                        ready: a=>a4(this, "flickity:ready", a),
                        change: a=>a4(this, "flickity:slide-changed", a),
                        settle: a=>a4(this, "flickity:slide-settled", a)
                    }
                });
                a(b)
            }
            ),
            this.hasAttribute("click-nav")) {
                let a = await this.flickityInstance;
                a.on("staticClick", this._onStaticClick.bind(this)),
                this.addEventListener("mousemove", this._onMouseMove.bind(this))
            }
        }
        async _onStaticClick(h, c, d) {
            if (h.target.closest && h.target.closest("button, a"))
                return;
            let a = await this.flickityInstance
              , e = a.selectedElement.hasAttribute("data-media-type") && ["video", "external_video", "model"].includes(a.selectedElement.getAttribute("data-media-type"));
            if (!d || e || window.matchMedia(window.themeVariables.breakpoints.phone).matches)
                return;
            let f = a.viewport
              , b = f.getBoundingClientRect()
              , g = Math.floor(b.right - b.width / 2);
            c.clientX > g ? a.next() : a.previous()
        }
        async _onMouseMove(a) {
            let b = await this.flickityInstance
              , c = b.selectedElement.hasAttribute("data-media-type") && ["video", "external_video", "model"].includes(b.selectedElement.getAttribute("data-media-type"));
            this.classList.toggle("is-hovering-right", a.offsetX > this.clientWidth / 2 && !c),
            this.classList.toggle("is-hovering-left", a.offsetX <= this.clientWidth / 2 && !c)
        }
        async _preloadNextImage() {
            var a;
            let b = await this.flickity;
            b.selectedElement.nextElementSibling && (null == (a = b.selectedElement.nextElementSibling.querySelector("img")) || a.setAttribute("loading", "eager"))
        }
    }
    ;
    function bJ(d, b, e=!1) {
        let c = []
          , a = d;
        for (; a = a.previousElementSibling; )
            (!b || a.matches(b)) && c.push(a);
        for (e && c.push(d),
        a = d; a = a.nextElementSibling; )
            (!b || a.matches(b)) && c.push(a);
        return c
    }
    async function bK(a) {
        let b = [];
        for (let c of (null != a && "function" == typeof a[Symbol.iterator] || (a = [a]),
        a))
            if ("function" == typeof c[Symbol.asyncIterator])
                for await(let d of c)
                    b.push(d);
            else
                b.push(c);
        return b
    }
    window.customElements.define("flickity-carousel", P);
    var Q = class extends a6 {
        async connectedCallback() {
            this.flickityCarousel.addEventListener("flickity:ready", this._onSlideChanged.bind(this, !1)),
            this.flickityCarousel.addEventListener("flickity:slide-changed", this._onSlideChanged.bind(this, !0)),
            this.delegate.on("click", '[data-action="prev"]', ()=>this.flickityCarousel.previous()),
            this.delegate.on("click", '[data-action="next"]', ()=>this.flickityCarousel.next()),
            this.delegate.on("click", '[data-action="select"]', (b,a)=>this.flickityCarousel.select(`#${a.getAttribute("aria-controls")}`))
        }
        get flickityCarousel() {
            return this._flickityCarousel = this._flickityCarousel || document.getElementById(this.getAttribute("controls"))
        }
        async _onSlideChanged(b=!0) {
            let a = await this.flickityCarousel.flickityInstance;
            Array.from(this.querySelectorAll(`[aria-controls="${a.selectedElement.id}"]`)).forEach(a=>{
                a.setAttribute("aria-current", "true"),
                bJ(a).forEach(a=>a.removeAttribute("aria-current")),
                requestAnimationFrame(()=>{
                    if (a.offsetParent && a.offsetParent !== this) {
                        let c = a.offsetParent.clientHeight / 2
                          , d = a.offsetParent.clientWidth / 2;
                        a.offsetParent.scrollTo({
                            behavior: b ? "smooth" : "auto",
                            top: a.offsetTop - c + a.clientHeight / 2,
                            left: a.offsetLeft - d + a.clientWidth / 2
                        })
                    }
                }
                )
            }
            )
        }
    }
    ;
    window.customElements.define("flickity-controls", Q);
    var R = class extends a6 {
        constructor() {
            super(),
            this.hasLoaded = !1,
            (async()=>{
                this.autoPlay ? (await this.untilVisible({
                    rootMargin: "300px",
                    threshold: 0
                }),
                this.play()) : this.addEventListener("click", this.play.bind(this), {
                    once: !0
                })
            }
            )()
        }
        get autoPlay() {
            return this.hasAttribute("autoplay")
        }
        get provider() {
            return this.getAttribute("provider")
        }
        async play() {
            this.hasLoaded || await this._setupPlayer(),
            "youtube" === this.provider ? this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "playVideo",
                args: ""
            }), "*") : "vimeo" === this.provider && this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({
                method: "play"
            }), "*")
        }
        pause() {
            this.hasLoaded && ("youtube" === this.provider ? this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "pauseVideo",
                args: ""
            }), "*") : "vimeo" === this.provider && this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({
                method: "pause"
            }), "*"))
        }
        _setupPlayer() {
            return this._setupPromise ? this._setupPromise : this._setupPromise = new Promise(c=>{
                let b = this.querySelector("template")
                  , a = b.content.firstElementChild.cloneNode(!0);
                a.onload = ()=>{
                    this.hasLoaded = !0,
                    c()
                }
                ,
                this.autoPlay ? b.replaceWith(a) : (this.innerHTML = "",
                this.appendChild(a))
            }
            )
        }
    }
    ;
    window.customElements.define("external-video", R);
    var S = class {
        static load(a) {
            return a ? (this.loadedProducts[a] || (this.loadedProducts[a] = new Promise(async b=>{
                let c = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/products/${a}.js`)
                  , d = await c.json();
                b(d)
            }
            )),
            this.loadedProducts[a]) : void 0
        }
    }
    ;
    b(S, "loadedProducts", {});
    var T = class extends HTMLElement {
        constructor() {
            super(),
            J.load("modelViewerUiStyles"),
            window.Shopify.loadFeatures([{
                name: "shopify-xr",
                version: "1.0",
                onLoad: this._setupShopifyXr.bind(this)
            }, {
                name: "model-viewer-ui",
                version: "1.0",
                onLoad: ()=>{
                    this.modelUi = new window.Shopify.ModelViewerUI(this.firstElementChild,{
                        focusOnPlay: !1
                    });
                    let a = this.querySelector("model-viewer");
                    a.addEventListener("shopify_model_viewer_ui_toggle_play", ()=>{
                        a.dispatchEvent(new CustomEvent("model:played",{
                            bubbles: !0
                        }))
                    }
                    ),
                    a.addEventListener("shopify_model_viewer_ui_toggle_pause", ()=>{
                        a.dispatchEvent(new CustomEvent("model:paused",{
                            bubbles: !0
                        }))
                    }
                    )
                }
            }])
        }
        disconnectedCallback() {
            var a;
            null == (a = this.modelUi) || a.destroy()
        }
        play() {
            this.modelUi && this.modelUi.play()
        }
        pause() {
            this.modelUi && this.modelUi.pause()
        }
        async _setupShopifyXr() {
            if (window.ShopifyXR) {
                let a = await S.load(this.getAttribute("product-handle"))
                  , b = a.media.filter(a=>"model" === a.media_type);
                window.ShopifyXR.addModels(b),
                window.ShopifyXR.setupXRElements()
            } else
                document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this))
        }
    }
    ;
    window.customElements.define("model-media", T);
    var U = class extends HTMLElement {
        constructor() {
            super(),
            this.hasLoaded = !1,
            this.autoPlay ? this.play() : this.addEventListener("click", this.play.bind(this), {
                once: !0
            })
        }
        get autoPlay() {
            return this.hasAttribute("autoplay")
        }
        play() {
            this.hasLoaded || this._replaceContent(),
            this.querySelector("video").play()
        }
        pause() {
            this.hasLoaded && this.querySelector("video").pause()
        }
        _replaceContent() {
            let a = this.querySelector("template").content.firstElementChild.cloneNode(!0);
            this.innerHTML = "",
            this.appendChild(a),
            this.firstElementChild.addEventListener("play", ()=>{
                this.dispatchEvent(new CustomEvent("video:played",{
                    bubbles: !0
                }))
            }
            ),
            this.firstElementChild.addEventListener("pause", ()=>{
                this.dispatchEvent(new CustomEvent("video:paused",{
                    bubbles: !0
                }))
            }
            ),
            this.hasLoaded = !0
        }
    }
    ;
    window.customElements.define("native-video", U);
    var V = class extends o {
        connectedCallback() {
            if (super.connectedCallback(),
            this.options = Array.from(this.querySelectorAll('[role="option"]')),
            this.delegate.on("click", '[role="option"]', this._onValueClicked.bind(this)),
            this.delegate.on("keydown", '[role="listbox"]', this._onKeyDown.bind(this)),
            this.delegate.on("change", "select", this._onValueChanged.bind(this)),
            this.delegate.on("click", ".combo-box__overlay", ()=>this.open = !1),
            this.hasAttribute("fit-toggle")) {
                let b = Math.max(...this.options.map(a=>a.clientWidth))
                  , a = document.querySelector(`[aria-controls="${this.id}"]`);
                a && a.style.setProperty("--largest-option-width", `${b + 2}px`)
            }
        }
        get nativeSelect() {
            return this.querySelector("select")
        }
        set selectedValue(a) {
            this.options.forEach(b=>{
                b.setAttribute("aria-selected", b.getAttribute("value") === a ? "true" : "false")
            }
            )
        }
        attributeChangedCallback(a, b, c) {
            if (super.attributeChangedCallback(a, b, c),
            "open" === a) {
                if (this.open) {
                    let d = this.getBoundingClientRect();
                    this.classList.toggle("combo-box--top", d.top >= window.innerHeight / 2 * 1.5),
                    setTimeout(()=>this.focusTrap.activate(), 150)
                } else
                    this.focusTrap.deactivate(),
                    setTimeout(()=>this.classList.remove("combo-box--top"), 200);
                document.documentElement.classList.toggle("lock-mobile", this.open)
            }
        }
        _onValueClicked(b, a) {
            this.selectedValue = a.value,
            this.nativeSelect.value = a.value,
            this.nativeSelect.dispatchEvent(new Event("change",{
                bubbles: !0
            })),
            this.open = !1
        }
        _onValueChanged(b, a) {
            Array.from(this.nativeSelect.options).forEach(b=>b.toggleAttribute("selected", a.value === b.value)),
            this.selectedValue = a.value
        }
        _onKeyDown(a) {
            var b, c;
            ("ArrowDown" === a.key || "ArrowUp" === a.key) && (a.preventDefault(),
            "ArrowDown" === a.key ? null == (b = document.activeElement.nextElementSibling) || b.focus() : null == (c = document.activeElement.previousElementSibling) || c.focus())
        }
    }
    ;
    window.customElements.define("combo-box", V);
    var W = class extends a6 {
        connectedCallback() {
            this.inputElement = this.querySelector("input"),
            this.delegate.on("click", "button:first-child", ()=>this.inputElement.quantity = this.inputElement.quantity - 1),
            this.delegate.on("click", "button:last-child", ()=>this.inputElement.quantity = this.inputElement.quantity + 1)
        }
    }
    ;
    window.customElements.define("quantity-selector", W);
    var X = class extends HTMLInputElement {
        connectedCallback() {
            this.addEventListener("input", this._onValueInput.bind(this)),
            this.addEventListener("change", this._onValueChanged.bind(this)),
            this.addEventListener("keydown", this._onKeyDown.bind(this))
        }
        get quantity() {
            return parseInt(this.value)
        }
        set quantity(a) {
            let b = ("number" == typeof a || "string" == typeof a && "" !== a.trim()) && !isNaN(a);
            "" !== a && ((!b || a < 0) && (a = parseInt(a) || 1),
            this.value = Math.max(this.min || 1, Math.min(a, this.max || Number.MAX_VALUE)).toString(),
            this.size = Math.max(this.value.length + 1, 2))
        }
        _onValueInput() {
            this.quantity = this.value
        }
        _onValueChanged() {
            "" === this.value && (this.quantity = 1)
        }
        _onKeyDown(a) {
            a.stopPropagation(),
            "ArrowUp" === a.key ? this.quantity = this.quantity + 1 : "ArrowDown" === a.key && (this.quantity = this.quantity - 1)
        }
    }
    ;
    window.customElements.define("input-number", X, {
        extends: "input"
    });
    var Y = class extends a6 {
        async connectedCallback() {
            await customElements.whenDefined("announcement-bar-item"),
            this.items = Array.from(this.querySelectorAll("announcement-bar-item")),
            this.hasPendingTransition = !1,
            this.delegate.on("click", '[data-action="prev"]', this.previous.bind(this)),
            this.delegate.on("click", '[data-action="next"]', this.next.bind(this)),
            this.autoPlay && (this.delegate.on("announcement-bar:content:open", this._pausePlayer.bind(this)),
            this.delegate.on("announcement-bar:content:close", this._startPlayer.bind(this))),
            window.ResizeObserver && (this.resizeObserver = new ResizeObserver(this._updateCustomProperties.bind(this)),
            this.resizeObserver.observe(this)),
            this.autoPlay && this._startPlayer(),
            Shopify.designMode && this.delegate.on("shopify:block:select", a=>this.select(a.target.index, !1))
        }
        get autoPlay() {
            return this.hasAttribute("auto-play")
        }
        get selectedIndex() {
            return this.items.findIndex(a=>a.selected)
        }
        previous() {
            this.select((this.selectedIndex - 1 + this.items.length) % this.items.length)
        }
        next() {
            this.select((this.selectedIndex + 1 + this.items.length) % this.items.length)
        }
        async select(a, b=!0) {
            this.selectedIndex !== a && !this.hasPendingTransition && (this.autoPlay && this._pausePlayer(),
            this.hasPendingTransition = !0,
            await this.items[this.selectedIndex].deselect(b),
            await this.items[a].select(b),
            this.hasPendingTransition = !1,
            this.autoPlay && this._startPlayer())
        }
        _pausePlayer() {
            clearInterval(this._interval)
        }
        _startPlayer() {
            this._interval = setInterval(this.next.bind(this), 1e3 * parseInt(this.getAttribute("cycle-speed")))
        }
        _updateCustomProperties(a) {
            a.forEach(a=>{
                if (a.target === this) {
                    let b = a.borderBoxSize ? a.borderBoxSize.length > 0 ? a.borderBoxSize[0].blockSize : a.borderBoxSize.blockSize : a.target.clientHeight;
                    document.documentElement.style.setProperty("--announcement-bar-height", `${b}px`)
                }
            }
            )
        }
    }
    ;
    window.customElements.define("announcement-bar", Y);
    var Z = class extends a6 {
        connectedCallback() {
            this.hasContent && (this.contentElement = this.querySelector(".announcement-bar__content"),
            this.delegate.on("click", '[data-action="open-content"]', this.openContent.bind(this)),
            this.delegate.on("click", '[data-action="close-content"]', this.closeContent.bind(this)),
            Shopify.designMode && (this.addEventListener("shopify:block:select", this.openContent.bind(this)),
            this.addEventListener("shopify:block:deselect", this.closeContent.bind(this))))
        }
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get hasContent() {
            return this.hasAttribute("has-content")
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        get focusTrap() {
            return this._trapFocus = this._trapFocus || bC(this.contentElement.querySelector(".announcement-bar__content-inner"), {
                fallbackFocus: this,
                clickOutsideDeactivates: a=>"BUTTON" !== a.target.tagName,
                allowOutsideClick: a=>"BUTTON" === a.target.tagName,
                onDeactivate: this.closeContent.bind(this),
                preventScroll: !0
            })
        }
        async select(a=!0) {
            this.removeAttribute("hidden"),
            await new Promise(b=>{
                this.animate({
                    transform: ["translateY(8px)", "translateY(0)"],
                    opacity: [0, 1]
                }, {
                    duration: a ? 150 : 0,
                    easing: "ease-in-out"
                }).onfinish = b
            }
            )
        }
        async deselect(a=!0) {
            await this.closeContent(),
            await new Promise(b=>{
                this.animate({
                    transform: ["translateY(0)", "translateY(-8px)"],
                    opacity: [1, 0]
                }, {
                    duration: a ? 150 : 0,
                    easing: "ease-in-out"
                }).onfinish = b
            }
            ),
            this.setAttribute("hidden", "")
        }
        async openContent() {
            this.hasContent && (this.contentElement.addEventListener("transitionend", ()=>this.focusTrap.activate(), {
                once: !0
            }),
            this.contentElement.removeAttribute("hidden"),
            document.documentElement.classList.add("lock-all"),
            this.dispatchEvent(new CustomEvent("announcement-bar:content:open",{
                bubbles: !0
            })))
        }
        async closeContent() {
            if (!this.hasContent || this.contentElement.hasAttribute("hidden"))
                return Promise.resolve();
            await new Promise(a=>{
                this.contentElement.addEventListener("transitionend", ()=>a(), {
                    once: !0
                }),
                this.contentElement.setAttribute("hidden", ""),
                this.focusTrap.deactivate(),
                document.documentElement.classList.remove("lock-all"),
                this.dispatchEvent(new CustomEvent("announcement-bar:content:close",{
                    bubbles: !0
                }))
            }
            )
        }
    }
    ;
    window.customElements.define("announcement-bar-item", Z);
    var $ = class extends HTMLElement {
        connectedCallback() {
            this.facetToolbar = document.getElementById("mobile-facet-toolbar"),
            this.tabsNav = document.getElementById("search-tabs-nav"),
            this.tabsNav && this.tabsNav.addEventListener("tabs-nav:changed", this._onCategoryChanged.bind(this)),
            this._completeSearch()
        }
        get terms() {
            return this.getAttribute("terms")
        }
        get completeFor() {
            const attrValue = this.getAttribute("complete-for");
            if (!attrValue) return [];
            try {
                // Try parsing as JSON first
                return JSON.parse(attrValue).filter(a=>"" !== a);
            } catch (e) {
                // If not valid JSON, treat as comma-separated string
                return attrValue.split(',').map(s => s.trim()).filter(a=>"" !== a);
            }
        }
        async _completeSearch() {
            let a = [];
            this.completeFor.forEach(b=>{
                a.push(fetch(`${window.themeVariables.routes.searchUrl}?section_id=${this.getAttribute("section-id")}&q=${this.terms}&type=${b}&options[prefix]=last&options[unavailable_products]=${window.themeVariables.settings.searchUnavailableProducts}`))
            }
            );
            let b = await Promise.all(a);
            await Promise.all(b.map(async c=>{
                let a = document.createElement("div");
                a.innerHTML = await c.text();
                let b = a.querySelector(".main-search__category-result")
                  , d = a.querySelector("#search-tabs-nav .tabs-nav__item");
                b && (b.setAttribute("hidden", ""),
                this.insertAdjacentElement("beforeend", b),
                this.tabsNav && this.tabsNav.addButton(d))
            }
            ))
        }
        _onCategoryChanged(a) {
            let b = a.detail.button;
            this.facetToolbar.classList.toggle("is-collapsed", "product" !== b.getAttribute("data-type"))
        }
    }
    ;
    window.customElements.define("search-page", $);
    var _ = class extends a6 {
        connectedCallback() {
            window.Shopify && window.Shopify.designMode && (this.rootDelegate.on("shopify:section:select", a=>bD(a, this, ()=>this.open = !0)),
            this.rootDelegate.on("shopify:section:deselect", a=>bD(a, this, ()=>this.open = !1))),
            this.delegate.on("click", '[data-action~="accept-policy"]', this._acceptPolicy.bind(this)),
            this.delegate.on("click", '[data-action~="decline-policy"]', this._declinePolicy.bind(this)),
            window.Shopify.loadFeatures([{
                name: "consent-tracking-api",
                version: "0.1",
                onLoad: this._onCookieBarSetup.bind(this)
            }])
        }
        set open(a) {
            this.toggleAttribute("hidden", !a)
        }
        _onCookieBarSetup() {
            window.Shopify.customerPrivacy.shouldShowGDPRBanner() && (this.open = !0)
        }
        _acceptPolicy() {
            window.Shopify.customerPrivacy.setTrackingConsent(!0, ()=>this.open = !1)
        }
        _declinePolicy() {
            window.Shopify.customerPrivacy.setTrackingConsent(!1, ()=>this.open = !1)
        }
    }
    ;
    window.customElements.define("cookie-bar", _);
    var aa = class extends HTMLElement {
        async connectedCallback() {
            if (!this.hasAttribute("use-automatic-recommendations"))
                return;
            let c = await fetch(`${window.themeVariables.routes.productRecommendationsUrl}?product_id=${this.productId}&limit=${this.recommendationsCount}&section_id=${this.sectionId}`)
              , a = document.createElement("div");
            a.innerHTML = await c.text();
            let b = a.querySelector("product-recommendations");
            b.hasChildNodes() && (this.innerHTML = b.innerHTML)
        }
        get productId() {
            return this.getAttribute("product-id")
        }
        get sectionId() {
            return this.getAttribute("section-id")
        }
        get recommendationsCount() {
            return parseInt(this.getAttribute("recommendations-count") || 4)
        }
    }
    ;
    window.customElements.define("product-recommendations", aa);
    var ab = class extends HTMLElement {
        async connectedCallback() {
            if ("" === this.searchQueryString)
                return;
            let c = await fetch(`${window.themeVariables.routes.searchUrl}?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`)
              , a = document.createElement("div");
            a.innerHTML = await c.text();
            let b = a.querySelector("recently-viewed-products");
            b.hasChildNodes() && (this.innerHTML = b.innerHTML)
        }
        get searchQueryString() {
            let a = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
            return this.hasAttribute("exclude-product-id") && a.includes(parseInt(this.getAttribute("exclude-product-id"))) && a.splice(a.indexOf(parseInt(this.getAttribute("exclude-product-id"))), 1),
            a.map(a=>"id:" + a).slice(0, this.productsCount).join(" OR ")
        }
        get sectionId() {
            return this.getAttribute("section-id")
        }
        get productsCount() {
            return this.getAttribute("products-count") || 4
        }
    }
    ;
    function bL(a, c) {
        let b = "string" == typeof a ? a : a.preview_image ? a.preview_image.src : a.url;
        if (null === c)
            return b;
        if ("master" === c)
            return b.replace(/http(s)?:/, "");
        let d = b.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
        if (!d)
            return null;
        {
            let e = b.split(d[0])
              , f = d[0];
            return (e[0] + "_" + c + f).replace(/http(s)?:/, "")
        }
    }
    function bM(a, b) {
        let c = [];
        return ("string" == typeof a ? b : bN(a, b)).forEach(b=>{
            c.push(`${bL(a, b + "x")} ${b}w`)
        }
        ),
        c.join(",")
    }
    function bN(a, b) {
        let c = []
          , d = a.preview_image.width;
        return b.forEach(a=>{
            d >= a && c.push(a)
        }
        ),
        c
    }
    function bO(a) {
        return new Promise(b=>{
            !a || "IMG" !== a.tagName || a.complete ? b() : a.onload = ()=>b()
        }
        )
    }
    window.customElements.define("recently-viewed-products", ab);
    var bP = class {
        constructor(a) {
            this._effect = a,
            this._playState = "idle",
            this._finished = Promise.resolve()
        }
        get finished() {
            return this._finished
        }
        get animationEffects() {
            return this._effect instanceof bQ ? [this._effect] : this._effect.animationEffects
        }
        cancel() {
            this.animationEffects.forEach(a=>a.cancel())
        }
        finish() {
            this.animationEffects.forEach(a=>a.finish())
        }
        play() {
            this._playState = "running",
            this._effect.play(),
            this._finished = this._effect.finished,
            this._finished.then(()=>{
                this._playState = "finished"
            }
            , a=>{
                this._playState = "idle"
            }
            )
        }
    }
      , bQ = class {
        constructor(a, c, b={}) {
            if (!a)
                return;
            "Animation"in window ? this._animation = new Animation(new KeyframeEffect(a,c,b)) : (b.fill = "forwards",
            this._animation = a.animate(c, b),
            this._animation.pause()),
            this._animation.addEventListener("finish", ()=>{
                a.style.opacity = c.hasOwnProperty("opacity") ? c.opacity[c.opacity.length - 1] : null,
                a.style.visibility = c.hasOwnProperty("visibility") ? c.visibility[c.visibility.length - 1] : null
            }
            )
        }
        get finished() {
            return this._animation ? this._animation.finished ? this._animation.finished : new Promise(a=>this._animation.onfinish = a) : Promise.resolve()
        }
        play() {
            this._animation && (this._animation.startTime = null,
            this._animation.play())
        }
        cancel() {
            this._animation && this._animation.cancel()
        }
        finish() {
            this._animation && this._animation.finish()
        }
    }
      , bR = class {
        constructor(a) {
            this._childrenEffects = a,
            this._finished = Promise.resolve()
        }
        get finished() {
            return this._finished
        }
        get animationEffects() {
            return this._childrenEffects.flatMap(a=>a instanceof bQ ? a : a.animationEffects)
        }
    }
      , bS = class extends bR {
        play() {
            let a = [];
            for (let b of this._childrenEffects)
                b.play(),
                a.push(b.finished);
            this._finished = Promise.all(a)
        }
    }
      , bT = class extends bR {
        play() {
            this._finished = new Promise(async(b,c)=>{
                try {
                    for (let a of this._childrenEffects)
                        a.play(),
                        await a.finished;
                    b()
                } catch (d) {
                    c()
                }
            }
            )
        }
    }
      , ac = class extends HTMLElement {
        async connectedCallback() {
            this._pendingAnimations = [],
            this.addEventListener("split-lines:re-split", a=>{
                Array.from(a.target.children).forEach(a=>a.style.visibility = this.selected ? "visible" : "hidden")
            }
            ),
            bI.prefersReducedMotion() && (this.setAttribute("reveal-visibility", ""),
            Array.from(this.querySelectorAll("[reveal], [reveal-visibility]")).forEach(a=>{
                a.removeAttribute("reveal"),
                a.removeAttribute("reveal-visibility")
            }
            ))
        }
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        async transitionToLeave(b, d=!0) {
            "reveal" !== b && this.setAttribute("hidden", ""),
            this._pendingAnimations.forEach(a=>a.cancel()),
            this._pendingAnimations = [];
            let a = null
              , c = await bK(this.querySelectorAll("split-lines, .button-group, .button-wrapper"))
              , e = Array.from(this.querySelectorAll(".slideshow__image-wrapper"));
            switch (b) {
            case "sweep":
                a = new bP(new bT([new bQ(this,{
                    visibility: ["visible", "hidden"]
                },{
                    duration: 500
                }), new bS(c.map(a=>new bQ(a,{
                    opacity: [1, 0],
                    visibility: ["visible", "hidden"]
                })))]));
                break;
            case "fade":
                a = new bP(new bQ(this,{
                    opacity: [1, 0],
                    visibility: ["visible", "hidden"]
                },{
                    duration: 250,
                    easing: "ease-in-out"
                }));
                break;
            case "reveal":
                a = new bP(new bT([new bS(c.reverse().map(a=>new bQ(a,{
                    opacity: [1, 0],
                    visibility: ["visible", "hidden"]
                },{
                    duration: 250,
                    easing: "ease-in-out"
                }))), new bS(e.map(a=>a.classList.contains("slideshow__image-wrapper--secondary") ? new bQ(a,{
                    visibility: ["visible", "hidden"],
                    clipPath: ["inset(0 0 0 0)", "inset(100% 0 0 0)"]
                },{
                    duration: 450,
                    easing: "cubic-bezier(0.99, 0.01, 0.50, 0.94)"
                }) : new bQ(a,{
                    visibility: ["visible", "hidden"],
                    clipPath: ["inset(0 0 0 0)", "inset(0 0 100% 0)"]
                },{
                    duration: 450,
                    easing: "cubic-bezier(0.99, 0.01, 0.50, 0.94)"
                })))]))
            }
            await this._executeAnimation(a, d),
            "reveal" === b && this.setAttribute("hidden", "")
        }
        async transitionToEnter(c, d=!0, e=!1) {
            this.removeAttribute("hidden"),
            await this._untilReady();
            let a = null
              , b = await bK(this.querySelectorAll("split-lines, .button-group, .button-wrapper"))
              , f = Array.from(this.querySelectorAll(".slideshow__image-wrapper"));
            switch (c) {
            case "sweep":
                a = new bP(new bT([new bQ(this,{
                    visibility: ["hidden", "visible"],
                    clipPath: e ? ["inset(0 100% 0 0)", "inset(0 0 0 0)"] : ["inset(0 0 0 100%)", "inset(0 0 0 0)"]
                },{
                    duration: 500,
                    easing: "cubic-bezier(1, 0, 0, 1)"
                }), new bS(b.map((a,b)=>new bQ(a,{
                    opacity: [0, 1],
                    visibility: ["hidden", "visible"],
                    clipPath: ["inset(0 0 100% 0)", "inset(0 0 0 0)"],
                    transform: ["translateY(100%)", "translateY(0)"]
                },{
                    duration: 450,
                    delay: 100 * b,
                    easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
                })))]));
                break;
            case "fade":
                a = new bP(new bQ(this,{
                    opacity: [0, 1],
                    visibility: ["hidden", "visible"]
                },{
                    duration: 250,
                    easing: "ease-in-out"
                }));
                break;
            case "reveal":
                a = new bP(new bT([new bS(f.map(a=>a.classList.contains("slideshow__image-wrapper--secondary") ? new bQ(a,{
                    visibility: ["hidden", "visible"],
                    clipPath: ["inset(100% 0 0 0)", "inset(0 0 0 0)"]
                },{
                    duration: 450,
                    delay: 100,
                    easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
                }) : new bQ(a,{
                    visibility: ["hidden", "visible"],
                    clipPath: ["inset(0 0 100% 0)", "inset(0 0 0 0)"]
                },{
                    duration: 450,
                    delay: 100,
                    easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
                }))), new bS(b.map((a,b)=>new bQ(a,{
                    opacity: [0, 1],
                    visibility: ["hidden", "visible"],
                    clipPath: ["inset(0 0 100% 0)", "inset(0 0 0 0)"],
                    transform: ["translateY(100%)", "translateY(0)"]
                },{
                    duration: 450,
                    delay: 100 * b,
                    easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
                })))]))
            }
            return this._executeAnimation(a, d)
        }
        async _executeAnimation(a, b) {
            return this._pendingAnimations.push(a),
            b ? a.play() : a.finish(),
            a.finished
        }
        async _untilReady() {
            return Promise.all(this._getVisibleImages().map(a=>bO(a)))
        }
        _preloadImages() {
            this._getVisibleImages().forEach(a=>{
                a.setAttribute("loading", "eager")
            }
            )
        }
        _getVisibleImages() {
            return Array.from(this.querySelectorAll("img")).filter(a=>"none" !== getComputedStyle(a.parentElement).display)
        }
    }
    ;
    window.customElements.define("slide-show-item", ac);
    var c = {
        _blockVerticalScroll(a=18) {
            this.addEventListener("touchstart", a=>{
                this.firstTouchClientX = a.touches[0].clientX
            }
            ),
            this.addEventListener("touchmove", b=>{
                let c = b.touches[0].clientX - this.firstTouchClientX;
                Math.abs(c) > a && b.preventDefault()
            }
            , {
                passive: !1
            })
        }
    }
      , h = class extends a6 {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll("slide-show-item")),
            this.pageDots = this.querySelector("page-dots"),
            this.isTransitioning = !1,
            this.items.length > 1 && (Shopify.designMode && (this.addEventListener("shopify:block:deselect", this.startPlayer.bind(this)),
            this.addEventListener("shopify:block:select", a=>{
                this.pausePlayer(),
                this.intersectionObserver.disconnect(),
                !a.detail.load && a.target.selected || this.select(a.target.index, !a.detail.load)
            }
            )),
            this.addEventListener("swiperight", this.previous.bind(this)),
            this.addEventListener("swipeleft", this.next.bind(this)),
            this.addEventListener("page-dots:changed", a=>this.select(a.detail.index)),
            this._blockVerticalScroll()),
            this._setupVisibility()
        }
        get selectedIndex() {
            return this.items.findIndex(a=>a.selected)
        }
        get transitionType() {
            return bI.prefersReducedMotion() ? "fade" : this.getAttribute("transition-type")
        }
        async _setupVisibility() {
            await this.untilVisible(),
            await this.items[this.selectedIndex].transitionToEnter(this.transitionType).catch(a=>{}
            ),
            this.startPlayer()
        }
        previous() {
            this.select((this.selectedIndex - 1 + this.items.length) % this.items.length, !0, !0)
        }
        next() {
            this.select((this.selectedIndex + 1 + this.items.length) % this.items.length, !0, !1)
        }
        async select(d, c=!0, e=!1) {
            if ("reveal" === this.transitionType && this.isTransitioning)
                return;
            this.isTransitioning = !0;
            let a = this.items[this.selectedIndex]
              , b = this.items[d];
            this.items[(b.index + 1) % this.items.length]._preloadImages(),
            a && a !== b && ("reveal" !== this.transitionType ? a.transitionToLeave(this.transitionType, c) : await a.transitionToLeave(this.transitionType, c)),
            this.pageDots && (this.pageDots.selectedIndex = b.index),
            await b.transitionToEnter(this.transitionType, c, e).catch(a=>{}
            ),
            this.isTransitioning = !1
        }
        pausePlayer() {
            this.style.setProperty("--section-animation-play-state", "paused")
        }
        startPlayer() {
            this.hasAttribute("auto-play") && this.style.setProperty("--section-animation-play-state", "running")
        }
    }
    ;
    Object.assign(h.prototype, c),
    window.customElements.define("slide-show", h);
    var ad = class extends HTMLElement {
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        get hasAttachedImage() {
            return this.hasAttribute("attached-image")
        }
        async transitionToEnter(b=!0) {
            this.removeAttribute("hidden");
            let c = this.querySelector(".image-with-text__text-wrapper")
              , d = await bK(this.querySelectorAll(".image-with-text__content split-lines"))
              , a = new bP(new bT([new bS(d.map((a,b)=>new bQ(a,{
                opacity: [0, .2, 1],
                transform: ["translateY(100%)", "translateY(0)"],
                clipPath: ["inset(0 0 100% 0)", "inset(0 0 0 0)"]
            },{
                duration: 350,
                delay: 120 * b,
                easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
            }))), new bQ(c,{
                opacity: [0, 1]
            },{
                duration: 300
            })]));
            return b ? a.play() : a.finish(),
            a.finished
        }
        async transitionToLeave(b=!0) {
            let c = await bK(this.querySelectorAll(".image-with-text__text-wrapper, .image-with-text__content split-lines"))
              , a = new bP(new bS(c.map(a=>new bQ(a,{
                opacity: [1, 0]
            },{
                duration: 200
            }))));
            b ? a.play() : a.finish(),
            await a.finished,
            this.setAttribute("hidden", "")
        }
    }
    ;
    window.customElements.define("image-with-text-item", ad);
    var ae = class extends a6 {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll("image-with-text-item")),
            this.imageItems = Array.from(this.querySelectorAll(".image-with-text__image")),
            this.pageDots = this.querySelector("page-dots"),
            this.hasPendingTransition = !1,
            this.items.length > 1 && (this.addEventListener("page-dots:changed", a=>this.select(a.detail.index)),
            Shopify.designMode && (this.addEventListener("shopify:block:deselect", this.startPlayer.bind(this)),
            this.addEventListener("shopify:block:select", a=>{
                this.intersectionObserver.disconnect(),
                this.pausePlayer(),
                this.select(a.target.index, !a.detail.load)
            }
            ))),
            this._setupVisibility()
        }
        async _setupVisibility() {
            await this.untilVisible(),
            this.hasAttribute("reveal-on-scroll") && (await this.transitionImage(this.selectedIndex),
            this.select(this.selectedIndex)),
            this.startPlayer()
        }
        get selectedIndex() {
            return this.items.findIndex(a=>a.selected)
        }
        async select(a, b=!0) {
            this.hasPendingTransition || (this.hasPendingTransition = !0,
            (this.items[a].hasAttachedImage || !b) && await this.transitionImage(a, b),
            this.selectedIndex !== a && await this.items[this.selectedIndex].transitionToLeave(b),
            this.pageDots && (this.pageDots.selectedIndex = a),
            await this.items[a].transitionToEnter(b),
            this.hasPendingTransition = !1)
        }
        async transitionImage(e, d=!0) {
            let b = this.imageItems.find(a=>!a.hasAttribute("hidden"))
              , a = this.imageItems.find(a=>a.id === this.items[e].getAttribute("attached-image")) || b;
            b.setAttribute("hidden", ""),
            a.removeAttribute("hidden"),
            await bO(a);
            let c = new bP(new bQ(a,{
                visibility: ["hidden", "visible"],
                clipPath: ["inset(0 0 0 100%)", "inset(0 0 0 0)"]
            },{
                duration: 600,
                easing: "cubic-bezier(1, 0, 0, 1)"
            }));
            d ? c.play() : c.finish()
        }
        pausePlayer() {
            this.style.setProperty("--section-animation-play-state", "paused")
        }
        startPlayer() {
            this.style.setProperty("--section-animation-play-state", "running")
        }
    }
    ;
    window.customElements.define("image-with-text", ae);
    var af = class extends a6 {
        connectedCallback() {
            this.addEventListener("split-lines:re-split", a=>{
                Array.from(a.target.children).forEach(a=>a.style.visibility = this.selected ? "visible" : "hidden")
            }
            )
        }
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        async transitionToLeave(b=!0) {
            let c = await bK(this.querySelectorAll("split-lines, .testimonial__author"))
              , a = new bP(new bS(c.reverse().map((a,b)=>new bQ(a,{
                visibility: ["visible", "hidden"],
                clipPath: ["inset(0 0 0 0)", "inset(0 0 100% 0)"],
                transform: ["translateY(0)", "translateY(100%)"]
            },{
                duration: 350,
                delay: 60 * b,
                easing: "cubic-bezier(0.68, 0.00, 0.77, 0.00)"
            }))));
            b ? a.play() : a.finish(),
            await a.finished,
            this.setAttribute("hidden", "")
        }
        async transitionToEnter(b=!0) {
            let c = await bK(this.querySelectorAll("split-lines, .testimonial__author"))
              , a = new bP(new bS(c.map((a,b)=>new bQ(a,{
                visibility: ["hidden", "visible"],
                clipPath: ["inset(0 0 100% 0)", "inset(0 0 0px 0)"],
                transform: ["translateY(100%)", "translateY(0)"]
            },{
                duration: 550,
                delay: 120 * b,
                easing: "cubic-bezier(0.23, 1, 0.32, 1)"
            }))));
            return this.removeAttribute("hidden"),
            b ? a.play() : a.finish(),
            a.finished
        }
    }
    ;
    window.customElements.define("testimonial-item", af);
    var i = class extends a6 {
        connectedCallback() {
            this.items = Array.from(this.querySelectorAll("testimonial-item")),
            this.pageDots = this.querySelector("page-dots"),
            this.hasPendingTransition = !1,
            this.items.length > 1 && (this.addEventListener("swiperight", this.previous.bind(this)),
            this.addEventListener("swipeleft", this.next.bind(this)),
            this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            this.addEventListener("page-dots:changed", a=>this.select(a.detail.index)),
            Shopify.designMode && this.addEventListener("shopify:block:select", a=>{
                var b;
                null == (b = this.intersectionObserver) || b.disconnect(),
                (a.detail.load || !a.target.selected) && this.select(a.target.index, !a.detail.load)
            }
            ),
            this._blockVerticalScroll()),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }
        get selectedIndex() {
            return this.items.findIndex(a=>a.selected)
        }
        async _setupVisibility() {
            await this.untilVisible(),
            this.items[this.selectedIndex].transitionToEnter()
        }
        previous() {
            this.select((this.selectedIndex - 1 + this.items.length) % this.items.length)
        }
        next() {
            this.select((this.selectedIndex + 1 + this.items.length) % this.items.length)
        }
        async select(a, b=!0) {
            this.hasPendingTransition || (this.hasPendingTransition = !0,
            await this.items[this.selectedIndex].transitionToLeave(b),
            this.pageDots && (this.pageDots.selectedIndex = a),
            await this.items[a].transitionToEnter(b),
            this.hasPendingTransition = !1)
        }
    }
    ;
    Object.assign(i.prototype, c),
    window.customElements.define("testimonial-list", i);
    var ag = class extends HTMLElement {
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        async transitionToLeave(b=!0) {
            this.setAttribute("hidden", "");
            let a = new bP(new bQ(this,{
                visibility: ["visible", "hidden"]
            },{
                duration: 500
            }));
            return b ? a.play() : a.finish(),
            a.finished
        }
        async transitionToEnter(d=!0) {
            this.removeAttribute("hidden");
            let b = Array.from(this.querySelectorAll(".shop-the-look__dot"));
            b.forEach(a=>a.style.opacity = 0);
            let a = new bP(new bT([new bS(Array.from(this.querySelectorAll(".shop-the-look__image")).map(a=>new bQ(a,{
                opacity: [1, 1]
            },{
                duration: 0
            }))), new bQ(this,{
                visibility: ["hidden", "visible"],
                zIndex: [0, 1],
                clipPath: ["inset(0 0 0 100%)", "inset(0 0 0 0)"]
            },{
                duration: 500,
                easing: "cubic-bezier(1, 0, 0, 1)"
            }), new bS(b.map((a,b)=>new bQ(a,{
                opacity: [0, 1],
                transform: ["scale(0)", "scale(1)"]
            },{
                duration: 120,
                delay: 75 * b,
                easing: "ease-in-out"
            })))]));
            if (d ? a.play() : a.finish(),
            await a.finished,
            window.matchMedia(window.themeVariables.breakpoints.tabletAndUp).matches) {
                let c = this.querySelector(".shop-the-look__product-wrapper .shop-the-look__dot");
                null == c || c.setAttribute("aria-expanded", "true")
            }
        }
    }
    ;
    window.customElements.define("shop-the-look-item", ag);
    var ah = class extends a6 {
        connectedCallback() {
            this.shopTheLook = this.closest("shop-the-look"),
            this.inTransition = !1,
            this.pendingTransition = !1,
            this.pendingTransitionTo = null,
            this.delegate.on("click", '[data-action="prev"]', ()=>this.shopTheLook.previous()),
            this.delegate.on("click", '[data-action="next"]', ()=>this.shopTheLook.next())
        }
        transitionToIndex(e, a, b=!0) {
            let c = Array.from(this.querySelectorAll(".shop-the-look__counter-page-transition"))
              , f = c[e]
              , d = c[a];
            if (this.inTransition) {
                this.pendingTransition = !0,
                this.pendingTransitionTo = a;
                return
            }
            this.inTransition = !0,
            f.animate({
                transform: ["translateY(0)", "translateY(-100%)"]
            }, {
                duration: b ? 1e3 : 0,
                easing: "cubic-bezier(1, 0, 0, 1)"
            }).onfinish = ()=>{
                f.setAttribute("hidden", ""),
                this.inTransition = !1,
                this.pendingTransition && this.pendingTransitionTo !== a && (this.pendingTransition = !1,
                this.transitionToIndex(a, this.pendingTransitionTo, b),
                this.pendingTransitionTo = null)
            }
            ,
            d.removeAttribute("hidden"),
            d.animate({
                transform: ["translateY(100%)", "translateY(0)"]
            }, {
                duration: b ? 1e3 : 0,
                easing: "cubic-bezier(1, 0, 0, 1)"
            })
        }
    }
    ;
    window.customElements.define("shop-the-look-nav", ah);
    var ai = class extends a6 {
        connectedCallback() {
            this.lookItems = Array.from(this.querySelectorAll("shop-the-look-item")),
            this.nav = this.querySelector("shop-the-look-nav"),
            this.hasPendingTransition = !1,
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility(),
            this.lookItems.length > 1 && Shopify.designMode && this.addEventListener("shopify:block:select", async a=>{
                this.intersectionObserver.disconnect(),
                await this.select(a.target.index, !a.detail.load),
                this.nav.animate({
                    opacity: [0, 1],
                    transform: ["translateY(30px)", "translateY(0)"]
                }, {
                    duration: 0,
                    fill: "forwards",
                    easing: "ease-in-out"
                })
            }
            )
        }
        get selectedIndex() {
            return this.lookItems.findIndex(a=>a.selected)
        }
        async _setupVisibility() {
            await this.untilVisible();
            let b = Array.from(this.lookItems[this.selectedIndex].querySelectorAll(".shop-the-look__image"));
            for (let a of b)
                null !== a.offsetParent && await bO(a);
            await this.lookItems[this.selectedIndex].transitionToEnter(),
            this.nav && this.nav.animate({
                opacity: [0, 1],
                transform: ["translateY(30px)", "translateY(0)"]
            }, {
                duration: 150,
                fill: "forwards",
                easing: "ease-in-out"
            })
        }
        previous() {
            this.select((this.selectedIndex - 1 + this.lookItems.length) % this.lookItems.length)
        }
        next() {
            this.select((this.selectedIndex + 1 + this.lookItems.length) % this.lookItems.length)
        }
        async select(a, b=!0) {
            let c = this.lookItems[this.selectedIndex]
              , d = this.lookItems[a];
            this.hasPendingTransition || (this.hasPendingTransition = !0,
            c !== d && (this.nav.transitionToIndex(this.selectedIndex, a, b),
            c.transitionToLeave()),
            d.transitionToEnter(b),
            this.hasPendingTransition = !1)
        }
    }
    ;
    window.customElements.define("shop-the-look", ai);
    var aj = class extends a6 {
        async connectedCallback() {
            this.items = Array.from(this.querySelectorAll(".list-collections__item")),
            this.hasAttribute("scrollable") && (this.scroller = this.querySelector(".list-collections__scroller"),
            this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            this.addEventListener("shopify:block:select", a=>a.target.scrollIntoView({
                block: "nearest",
                inline: "center",
                behavior: a.detail.load ? "auto" : "smooth"
            }))),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }
        async _setupVisibility() {
            await this.untilVisible();
            let b = bI.prefersReducedMotion()
              , a = new bP(new bS(this.items.map((a,c)=>new bT([new bQ(a.querySelector(".list-collections__item-image"),{
                opacity: [0, 1],
                transform: [`scale(${b ? 1 : 1.1})`, "scale(1)"]
            },{
                duration: 250,
                delay: b ? 0 : 150 * c,
                easing: "cubic-bezier(0.65, 0, 0.35, 1)"
            }), new bS(Array.from(a.querySelectorAll(".list-collections__item-info [reveal]")).map((a,d)=>new bQ(a,{
                opacity: [0, 1],
                clipPath: [`inset(${b ? "0 0 0 0" : "0 0 100% 0"})`, "inset(0 0 0 0)"],
                transform: [`translateY(${b ? 0 : "100%"})`, "translateY(0)"]
            },{
                duration: 200,
                delay: b ? 0 : 150 * c + 150 * d,
                easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
            })))]))));
            this._hasSectionReloaded ? a.finish() : a.play()
        }
        previous() {
            let a = "ltr" === window.themeVariables.settings.direction ? 1 : -1;
            this.scroller.scrollBy({
                left: -this.items[0].clientWidth * a,
                behavior: "smooth"
            })
        }
        next() {
            let a = "ltr" === window.themeVariables.settings.direction ? 1 : -1;
            this.scroller.scrollBy({
                left: this.items[0].clientWidth * a,
                behavior: "smooth"
            })
        }
    }
    ;
    window.customElements.define("collection-list", aj);
    var j = class extends a6 {
        constructor() {
            super(),
            this.productListInner = this.querySelector(".product-list__inner"),
            this.productItems = Array.from(this.querySelectorAll("product-item"))
        }
        connectedCallback() {
            this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            !this.hidden && this.staggerApparition && this._staggerProductsApparition()
        }
        get staggerApparition() {
            return this.hasAttribute("stagger-apparition")
        }
        get apparitionAnimation() {
            return this._animation = this._animation || new bP(new bS(this.productItems.map((b,a)=>new bQ(b,{
                opacity: [0, 1],
                transform: [`translateY(${bI.prefersReducedMotion() ? 0 : window.innerWidth < 1e3 ? 35 : 60}px)`, "translateY(0)"]
            },{
                duration: 600,
                delay: bI.prefersReducedMotion() ? 0 : 100 * a - Math.min(3 * a * a, 100 * a),
                easing: "ease"
            }))))
        }
        previous(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1
              , c = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));
            a.target.nextElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.productListInner.scrollLeft * b - (this.productListInner.clientWidth + c) <= 0),
            this.productListInner.scrollBy({
                left: -(this.productListInner.clientWidth + c) * b,
                behavior: "smooth"
            })
        }
        next(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1
              , c = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));
            a.target.previousElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.productListInner.scrollLeft * b + (this.productListInner.clientWidth + c) * 2 >= this.productListInner.scrollWidth),
            this.productListInner.scrollBy({
                left: (this.productListInner.clientWidth + c) * b,
                behavior: "smooth"
            })
        }
        attributeChangedCallback(c) {
            var a, b;
            this.staggerApparition && "hidden" === c && (this.hidden ? this.apparitionAnimation.finish() : (this.productListInner.scrollLeft = 0,
            this.productListInner.parentElement.scrollLeft = 0,
            null == (a = this.querySelector(".prev-next-button--prev")) || a.setAttribute("disabled", ""),
            null == (b = this.querySelector(".prev-next-button--next")) || b.removeAttribute("disabled"),
            this._staggerProductsApparition()))
        }
        async _staggerProductsApparition() {
            this.productItems.forEach(a=>a.style.opacity = 0),
            await this.untilVisible({
                threshold: this.clientHeight > 0 ? Math.min(50 / this.clientHeight, 1) : 0
            }),
            this.apparitionAnimation.play()
        }
    }
    ;
    b(j, "observedAttributes", ["hidden"]),
    window.customElements.define("product-list", j);
    var ak = class extends a6 {
        async connectedCallback() {
            this.items = Array.from(this.querySelectorAll(".logo-list__item")),
            this.logoListScrollable = this.querySelector(".logo-list__list"),
            this.items.length > 1 && (this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this))),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }
        async _setupVisibility() {
            await this.untilVisible({
                rootMargin: "50px 0px",
                threshold: 0
            });
            let a = new bP(new bS(this.items.map((a,b)=>new bQ(a,{
                opacity: [0, 1],
                transform: [`translateY(${bI.prefersReducedMotion() ? 0 : "30px"})`, "translateY(0)"]
            },{
                duration: 300,
                delay: bI.prefersReducedMotion() ? 0 : 100 * b,
                easing: "ease"
            }))));
            this._hasSectionReloaded ? a.finish() : a.play()
        }
        previous(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1;
            a.target.nextElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.logoListScrollable.scrollLeft * b - (this.logoListScrollable.clientWidth + 24) <= 0),
            this.logoListScrollable.scrollBy({
                left: -(this.logoListScrollable.clientWidth + 24) * b,
                behavior: "smooth"
            })
        }
        next(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1;
            a.target.previousElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.logoListScrollable.scrollLeft * b + (this.logoListScrollable.clientWidth + 24) * 2 >= this.logoListScrollable.scrollWidth),
            this.logoListScrollable.scrollBy({
                left: (this.logoListScrollable.clientWidth + 24) * b,
                behavior: "smooth"
            })
        }
    }
    ;
    window.customElements.define("logo-list", ak);
    var al = class extends HTMLElement {
        connectedCallback() {
            window.addEventListener("scroll", bF(this._updateProgressBar.bind(this), 15))
        }
        
        get hasNextArticle() {
            return this.hasAttribute("has-next-article")
        }

        _updateProgressBar() {
            let c = bE()
              , b = window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? 40 : 80
              , e = this.getBoundingClientRect()
              , a = this.parentElement.getBoundingClientRect()
              , f = a.bottom - (e.bottom - b)
              , d = Math.max(-1 * (f / (a.height + b) - 1), 0);
            this.classList.toggle("is-visible", a.top < c && a.bottom > c + this.clientHeight - b),
            this.hasNextArticle && (d > .8 ? this.classList.add("article__nav--show-next") : this.classList.remove("article__nav--show-next")),
            this.style.setProperty("--transform", `${d}`)
        }
    }
    ;
    window.customElements.define("blog-post-navigation", al);
    var am = class extends a6 {
        connectedCallback() {
            !this.hasAttribute("stack") && (this.multiColumnInner = this.querySelector(".multi-column__inner"),
            this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            Shopify.designMode && this.addEventListener("shopify:block:select", a=>{
                a.target.scrollIntoView({
                    inline: "center",
                    block: "nearest",
                    behavior: a.detail.load ? "auto" : "smooth"
                })
            }
            )),
            this.hasAttribute("stagger-apparition") && this._setupVisibility()
        }

        async _setupVisibility() {
            await this.untilVisible({
                threshold: Math.min(50 / this.clientHeight, 1)
            });
            let b = bI.prefersReducedMotion()
              , a = new bP(new bS(Array.from(this.querySelectorAll(".multi-column__item")).map((a,c)=>new bQ(a,{
                opacity: [0, 1],
                transform: [`translateY(${bI.prefersReducedMotion() ? 0 : window.innerWidth < 1e3 ? 35 : 60}px)`, "translateY(0)"]
            },{
                duration: 600,
                delay: b ? 0 : 100 * c,
                easing: "ease"
            }))));
            this._hasSectionReloaded ? a.finish() : a.play()
        }

        previous(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1
              , c = parseInt(getComputedStyle(this).getPropertyValue("--multi-column-column-gap"));
            a.target.nextElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.multiColumnInner.scrollLeft * b - (this.multiColumnInner.clientWidth + c) <= 0),
            this.multiColumnInner.scrollBy({
                left: -(this.multiColumnInner.clientWidth + c) * b,
                behavior: "smooth"
            })
        }

        next(a) {
            let b = "ltr" === window.themeVariables.settings.direction ? 1 : -1
              , c = parseInt(getComputedStyle(this).getPropertyValue("--multi-column-column-gap"));
            a.target.previousElementSibling.removeAttribute("disabled"),
            a.target.toggleAttribute("disabled", this.multiColumnInner.scrollLeft * b + (this.multiColumnInner.clientWidth + c) * 2 >= this.multiColumnInner.scrollWidth),
            this.multiColumnInner.scrollBy({
                left: (this.multiColumnInner.clientWidth + c) * b,
                behavior: "smooth"
            })
        }
    }
    ;
    window.customElements.define("multi-column", am);
    var an = class extends HTMLElement {
        connectedCallback() {
            this.listItems = Array.from(this.querySelectorAll("gallery-item")),
            this.scrollBarElement = this.querySelector(".gallery__progress-bar"),
            this.listWrapperElement = this.querySelector(".gallery__list-wrapper"),
            this.listItems.length > 1 && (this.addEventListener("scrollable-content:progress", this._updateProgressBar.bind(this)),
            this.addEventListener("prev-next:prev", this.previous.bind(this)),
            this.addEventListener("prev-next:next", this.next.bind(this)),
            Shopify.designMode && this.addEventListener("shopify:block:select", a=>this.select(a.target.index, !a.detail.load)))
        }

        previous() {
            this.select([...this.listItems].reverse().find(a=>a.isOnLeftHalfPartOfScreen).index)
        }

        next() {
            this.select(this.listItems.findIndex(a=>a.isOnRightHalfPartOfScreen))
        }

        select(b, c=!0) {
            let a = this.listItems[b].getBoundingClientRect();
            this.listWrapperElement.scrollBy({
                behavior: c ? "smooth" : "auto",
                left: Math.floor(a.left - window.innerWidth / 2 + a.width / 2)
            })
        }

        _updateProgressBar(b) {
            var a;
            null == (a = this.scrollBarElement) || a.style.setProperty("--transform", `${b.detail.progress}%`)
        }
    }
    ;
    window.customElements.define("gallery-list", an);
    var ao = class extends HTMLElement {
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }

        get isOnRightHalfPartOfScreen() {
            return "ltr" === window.themeVariables.settings.direction ? this.getBoundingClientRect().left > window.innerWidth / 2 : this.getBoundingClientRect().right < window.innerWidth / 2
        }

        get isOnLeftHalfPartOfScreen() {
            return "ltr" === window.themeVariables.settings.direction ? this.getBoundingClientRect().right < window.innerWidth / 2 : this.getBoundingClientRect().left > window.innerWidth / 2
        }
    }
    ;
    window.customElements.define("gallery-item", ao);
    var ap = class extends a6 {
        connectedCallback() {
            this.hasAttribute("parallax") && !bI.prefersReducedMotion() && (this._hasPendingRaF = !1,
            this._onScrollListener = this._onScroll.bind(this),
            window.addEventListener("scroll", this._onScrollListener)),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }

        disconnectedCallback() {
            super.disconnectedCallback(),
            this._onScrollListener && window.removeEventListener("scroll", this._onScrollListener)
        }

        async _setupVisibility() {
            await this.untilVisible();
            let a = this.querySelector(".image-overlay__image")
              , e = await bK(this.querySelectorAll("split-lines"))
              , b = bI.prefersReducedMotion();
            await bO(a);
            let c = [new bQ(a,{
                opacity: [0, 1],
                transform: [`scale(${b ? 1 : 1.1})`, "scale(1)"]
            },{
                duration: 500,
                easing: "cubic-bezier(0.65, 0, 0.35, 1)"
            }), new bS(e.map((a,c)=>new bQ(a,{
                opacity: [0, .2, 1],
                transform: [`translateY(${b ? 0 : "100%"})`, "translateY(0)"],
                clipPath: [`inset(${b ? "0 0 0 0" : "0 0 100% 0"})`, "inset(0 0 0 0)"]
            },{
                duration: 300,
                delay: b ? 0 : 120 * c,
                easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
            }))), new bQ(this.querySelector(".image-overlay__text-container"),{
                opacity: [0, 1]
            },{
                duration: 300
            })]
              , d = new bP(b ? new bS(c) : new bT(c));
            this._hasSectionReloaded ? d.finish() : d.play()
        }

        _onScroll() {
            this._hasPendingRaF || (this._hasPendingRaF = !0,
            requestAnimationFrame(()=>{
                let a = this.getBoundingClientRect()
                  , b = this.querySelector(".image-overlay__content-wrapper")
                  , c = this.querySelector(".image-overlay__image")
                  , d = a.bottom
                  , e = a.height
                  , f = bE();
                b && (b.style.opacity = Math.max(1 - 3 * (1 - Math.min(d / e, 1)), 0).toString()),
                c && (c.style.transform = `translateY(${100 - 100 * Math.max(1 - (1 - Math.min(d / (e + f), 1)), 0)}px)`),
                this._hasPendingRaF = !1
            }
            ))
        }
    }
    ;
    window.customElements.define("image-with-text-overlay", ap);
    var aq = class extends a6 {
        async connectedCallback() {
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }

        async _setupVisibility() {
            await this.untilVisible();
            let a = Array.from(this.querySelectorAll(".image-with-text-block__image[reveal]"))
              , f = await bK(this.querySelectorAll("split-lines"))
              , b = bI.prefersReducedMotion();
            for (let c of a)
                null !== c.offsetParent && await bO(c);
            let d = [new bS(a.map(a=>new bQ(a,{
                opacity: [0, 1],
                transform: [`scale(${b ? 1 : 1.1})`, "scale(1)"]
            },{
                duration: 500,
                easing: "cubic-bezier(0.65, 0, 0.35, 1)"
            }))), new bQ(this.querySelector(".image-with-text-block__content"),{
                opacity: [0, 1],
                transform: [`translateY(${b ? 0 : "60px"})`, "translateY(0)"]
            },{
                duration: 150,
                easing: "ease-in-out"
            }), new bS(f.map((a,c)=>new bQ(a,{
                opacity: [0, .2, 1],
                transform: [`translateY(${b ? 0 : "100%"})`, "translateY(0)"],
                clipPath: [`inset(${b ? "0 0 0 0" : "0 0 100% 0"})`, "inset(0 0 0 0)"]
            },{
                duration: 300,
                delay: b ? 0 : 120 * c,
                easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)"
            }))), new bQ(this.querySelector(".image-with-text-block__text-container"),{
                opacity: [0, 1]
            },{
                duration: 300
            })]
              , e = new bP(b ? new bS(d) : new bT(d));
            this._hasSectionReloaded ? e.finish() : e.play()
        }
    }
    ;
    window.customElements.define("image-with-text-block", aq);
    var ar = class extends a6 {
        async connectedCallback() {
            if (this.articleItems = Array.from(this.querySelectorAll(".article-item")),
            this.staggerApparition) {
                await this.untilVisible({
                    threshold: this.clientHeight > 0 ? Math.min(50 / this.clientHeight, 1) : 0
                });
                let a = new bP(new bS(this.articleItems.map((b,a)=>new bQ(b,{
                    opacity: [0, 1],
                    transform: [`translateY(${bI.prefersReducedMotion() ? 0 : window.innerWidth < 1e3 ? 35 : 60}px)`, "translateY(0)"]
                },{
                    duration: 600,
                    delay: bI.prefersReducedMotion() ? 0 : 100 * a - Math.min(3 * a * a, 100 * a),
                    easing: "ease"
                }))));
                this._hasSectionReloaded ? a.finish() : a.play()
            }
        }

        get staggerApparition() {
            return this.hasAttribute("stagger-apparition")
        }
    }
    ;
    window.customElements.define("article-list", ar);
    var as = class extends HTMLElement {
        async connectedCallback() {
            let a = this.querySelector(".article__image");
            bI.prefersReducedMotion() ? a.removeAttribute("reveal") : (await bO(a),
            a.animate({
                opacity: [0, 1],
                transform: ["scale(1.1)", "scale(1)"]
            }, {
                duration: 500,
                fill: "forwards",
                easing: "cubic-bezier(0.65, 0, 0.35, 1)"
            }))
        }
    }
    ;
    window.customElements.define("blog-post-header", as);
    var at = class extends HTMLInputElement {
        connectedCallback() {
            this.addEventListener("click", ()=>document.getElementById(this.getAttribute("aria-controls")).open = !0)
        }
    }
    ;
    window.customElements.define("predictive-search-input", at, {
        extends: "input"
    });
    var au = class extends o {
        connectedCallback() {
            if (super.connectedCallback(),
            this.hasAttribute("reverse-breakpoint")) {
                this.originalDirection = this.classList.contains("drawer--from-left") ? "left" : "right";
                let a = window.matchMedia(this.getAttribute("reverse-breakpoint"));
                a.addListener(this._checkReverseOpeningDirection.bind(this)),
                this._checkReverseOpeningDirection(a)
            }
            this.delegate.on("click", ".drawer__overlay", ()=>this.open = !1)
        }

        attributeChangedCallback(a, b, c) {
            super.attributeChangedCallback(a, b, c),
            "open" === a && document.documentElement.classList.toggle("lock-all", this.open)
        }

        _checkReverseOpeningDirection(a) {
            this.classList.remove("drawer--from-left"),
            ("left" === this.originalDirection && !a.matches || "left" !== this.originalDirection && a.matches) && this.classList.add("drawer--from-left")
        }
    }
    ;
    window.customElements.define("drawer-content", au);
    var av = class extends au {
        connectedCallback() {
            super.connectedCallback(),
            this.inputElement = this.querySelector('[name="q"]'),
            this.drawerContentElement = this.querySelector(".drawer__content"),
            this.drawerFooterElement = this.querySelector(".drawer__footer"),
            this.loadingStateElement = this.querySelector(".predictive-search__loading-state"),
            this.resultsElement = this.querySelector(".predictive-search__results"),
            this.menuListElement = this.querySelector(".ss_predictive-search__menu-list"),
            this.delegate.on("input", '[name="q"]', this._debounce(this._onSearch.bind(this), 200)),
            this.delegate.on("click", '[data-action="reset-search"]', this._startNewSearch.bind(this))
        }

        // SEARCH SPRING INTEGRATION (TESTING)

        async _onSearch(event, inputElement) {
            if (event.key === "Enter") return;
        
            if (this.abortController) {
                this.abortController.abort();
            }
        
            const searchTerm = inputElement.value.trim();
        
            if (searchTerm === "") {
                this._showEmptyState();
                return;
            }
        
            this._showLoadingState();
            this.abortController = new AbortController();
        
            if (window.themeVariables?.settings?.searchSpringEnabled) {
                try {
                    // Query the Suggest API first so we can autocomplete against its
                    // top suggested term rather than the raw partial characters the customer has typed.
                    const suggestionsData = await getSuggestions(getSiteId(), searchTerm, {
                        suggestionCount: 5
                    });

                    const autocompleteTerm = suggestionsData?.suggested?.text || searchTerm;

                    const searchResults = await getAutocompleteSuggestions(getSiteId(), autocompleteTerm, {
                        q: autocompleteTerm,
                        resultsFormat: 'json',
                        resultsPerPage: 10
                    });

                    const processedResults = await this._processSearchSpringResults(searchResults, searchTerm);
                    const suggestionsHTML = this._buildSuggestionsHTML(suggestionsData);
                    const finalHTML = suggestionsHTML + processedResults.html;
                    
                    this._showResultsState({
                        ...processedResults,
                        html: finalHTML,
                        hasSuggestions: !!suggestionsHTML
                    });

                    if (processedResults.hasResults) {
                        this.resultsElement.dispatchEvent(new CustomEvent('searchspring:rendered', {
                            bubbles: true,
                            detail: { responseId: processedResults.responseId, contextType: 'autocomplete' }
                        }));
                    }

                } catch (error) {
                    console.error(error);
                    if (error.name === "AbortError") return;
                    this._showErrorState();
                }
            } else {
                try {
                    const results = await this._doLiquidSearch(searchTerm);
                    this._showResultsState(results);
                } catch (error) {
                    console.error(error);
                    if (error.name === "AbortError") return;
                    this._showErrorState();
                }
            }
        }

        // HELPER FUNCTIONS
        _showEmptyState() {
            this.loadingStateElement.hidden = true;
            this.resultsElement.hidden = true;
            this.drawerContentElement.classList.remove("drawer__content--center");
            this.drawerFooterElement.hidden = true;
            if (this.menuListElement) {
                this.menuListElement.hidden = false;
            }
        }

        _showLoadingState() {
            this.drawerContentElement.classList.add("drawer__content--center");
            this.loadingStateElement.hidden = false;
            this.resultsElement.hidden = true;
            this.drawerFooterElement.hidden = true;
            if (this.menuListElement) {
                this.menuListElement.hidden = true;
            }
        }

        _showResultsState(results) {
            this.loadingStateElement.hidden = true;
            this.resultsElement.hidden = false;
            
            if (results.hasResults) {
                this.drawerFooterElement.hidden = false;
                this.drawerContentElement.classList.remove("drawer__content--center");
            } else if (results.hasSuggestions) {
                // Keep suggestions at top near search bar, don't center
                this.drawerContentElement.classList.remove("drawer__content--center");
            } else {
                this.drawerContentElement.classList.add("drawer__content--center");
            }
            
            if (this.menuListElement) {
                this.menuListElement.hidden = true;
            }
            
            this.resultsElement.innerHTML = results.html;
        }

        _showErrorState() {
            this.loadingStateElement.hidden = true;
            this.resultsElement.hidden = false;
            this.drawerContentElement.classList.add("drawer__content--center");
            
            this.resultsElement.innerHTML = `
                <div class="search-error">
                    <p>Search temporarily unavailable. Please try again.</p>
                </div>
            `;
        }

        async _processSearchSpringResults(searchResults, searchTerm) {
            // Check for SearchSpring response structure
            if (!searchResults || !searchResults.results || searchResults.results.length === 0) {
                const t = window.themeVariables?.strings || {};
                return {
                    hasResults: false,
                    html: `
                        <div id="shopify-section-predictive-search" class="shopify-section shopify-section--predictive-search" style="padding-top: 24px" >
                            <p class="text--large">${t.searchNoResults || 'No results could be found.'}</p>
                            <div class="button-wrapper">
                                <button type="button" data-action="reset-search" class="button button--primary">${t.searchNewSearch || 'New search'}</button>
                            </div>
                        </div>
                    `
                };
            }

            // Process SearchSpring results into the original structure
            const marketPrefixMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
            const marketPrefix = marketPrefixMatch ? marketPrefixMatch[0] : '';
            const handles = searchResults.results.map((r) => r.handle).filter(Boolean);
            const shopifyPrices = await SearchSpringCommon.fetchShopifyPricesForHandles(handles);

            const products = searchResults.results.map((item, index) => {
                const ss = item;
                const shopifyPrice = shopifyPrices.get(ss.handle);
                let productUrl = item.url || '';
                if (marketPrefix && productUrl && !productUrl.startsWith('http') && !productUrl.startsWith(marketPrefix)) {
                    productUrl = marketPrefix + (productUrl.startsWith('/') ? productUrl : '/' + productUrl);
                }

                //Grab language and market settings, and determine country code for rendering
                const searchLanguage = window.themeVariables?.settings?.currentLanguage;
                const searchMarket = window.themeVariables?.settings?.currentMarket;
                const searchMarketCodeMap = { 'gb': 'gb', 'ie': 'ie', 'nether': 'nl', 'spain': 'es', 'france': 'fr', 'italy': 'it', 'portugal': 'pt', 'germany': 'de', 'austria': 'at', 'denmark': 'dk', 'belgium':'be', 'finland': 'fi', 'isle-of-man': 'im', 'poland': 'pl' };

                // Default to 'gb' if market code is not recognized
                const renderCountryCode = searchMarketCodeMap[searchMarket];

                // Assign displayTitle as the default title from SS, preferring a localized title if available
                let displayTitle = SearchSpringCommon.getLocalizedTitle(ss, ss.title || '', searchLanguage);

                // Decode HTML entities/encoding issues and strip colours from the title if configured
                displayTitle = SearchSpringCommon.getDisplayTitle(displayTitle);

                // Price: prefer Shopify (Global-e controlled), fallback to SearchSpring
                let price, compare_at_price;
                if (shopifyPrice) {
                    price = shopifyPrice.price;
                    compare_at_price = shopifyPrice.compare_at_price || shopifyPrice.compare_at_price_min || 0;
                } else {
                    price = ss.price || 0;
                    if (renderCountryCode !== 'en' && ss[`${renderCountryCode}_price`]) {
                        price = ss[`${renderCountryCode}_price`];
                    }
                    compare_at_price = ss.msrp || 0;
                    if (renderCountryCode !== 'en' && ss[`${renderCountryCode}_compare_at_price`]) {
                        compare_at_price = ss[`${renderCountryCode}_compare_at_price`];
                    }
                }

                const formattedPrice = SearchSpringCommon.formatPrice(price, !!shopifyPrice);
                const formattedComparePrice = compare_at_price > price ? SearchSpringCommon.formatPrice(compare_at_price, !!shopifyPrice) : '';
                const isOnSale = compare_at_price > price;

                return `
                    <li class="predictive-search__product-item line-item line-item--centered" data-product-id="${ss.uid || ss.id}" data-parent-id="${ss.uid || ss.id}" data-uid="${ss.ss_id || ss.uid || ss.id}" data-sku="${ss.sku || ''}">
                        <a href="${productUrl}?_pos=${index + 1}&_psq=${encodeURIComponent(searchTerm)}&_ss=e&_v=1.0" class="line-item__content-wrapper" data-instant data-ss-beacon="product-link">
                            <span class="line-item__image-wrapper">
                                ${SearchSpringCommon.buildResponsiveImageHTML({
                                    src: SearchSpringCommon.getFullImageUrl(item),
                                    alt: displayTitle,
                                    className: 'line-item__image',
                                    lazyload: index > 0,
                                    sizes: '80px',
                                    widths: SearchSpringCommon.PREDICTIVE_IMAGE_WIDTHS
                                })}
                            </span>
                            <div class="line-item__info">
                                <div class="product-item-meta">
                                    <span class="product-item-meta__title text--small">${displayTitle}</span>
                                    <div class="product-item-meta__price-list-container text--small">
                                        <div class="price-list">
                                            <span class="price ${isOnSale ? "price--highlight" : ""}">
                                                ${formattedPrice}
                                            </span>
                                            ${isOnSale ? `
                                                <span class="price price--compare">
                                                    ${formattedComparePrice}
                                                </span>
                                            ` : ""}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <svg focusable="false" width="17" height="14" class="icon icon--nav-arrow-right icon--direction-aware" viewBox="0 0 17 14">
                                <path d="M0 7h15M9 1l6 6-6 6" stroke="currentColor" stroke-width="1.7" fill="none"></path>
                            </svg>
                        </a>
                    </li>
                `;
            }).join('');

            // Build the complete HTML structure matching the original
            const html = `
                <div id="shopify-section-predictive-search" class="shopify-section shopify-section--predictive-search">
                    <tabs-nav class="tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border">
                        <scrollable-content class="tabs-nav__scroller hide-scrollbar">
                            <div class="tabs-nav__scroller-inner">
                                <div class="tabs-nav__item-list">
                                    <button hello type="button" class="tabs-nav__item heading heading--small" aria-expanded="true" aria-controls="predictive-search-products">${window.themeVariables.strings.searchProducts}</button>
                                </div>
                            </div>
                        </scrollable-content>
                    </tabs-nav>
                    <div class="predictive-search__results-categories">
                        <div class="predictive-search__results-categories-item" id="predictive-search-products">
                            <input type="hidden" form="predictive-search-form" name="type" value="product">
                            <ul class="predictive-search__product-list list--unstyled" role="list" data-type="products">
                                ${products}
                            </ul>
                        </div>
                    </div>
                </div>
            `;

            return {
                hasResults: true,
                html: html,
                totalResults: searchResults.pagination.totalResults,
                products: searchResults.results,
                responseId: searchResults.responseId || ''
            };
        }

        _formatPrice(price) {
            if (!price) return '';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: window.themeVariables.settings.cartCurrency || 'USD'
            }).format(price);
        }

        _buildSuggestionsHTML(suggestionsData) {
            if (!suggestionsData || (!suggestionsData.suggested && (!suggestionsData.alternatives || suggestionsData.alternatives.length === 0))) {
                return '';
            }

            const marketPrefixMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
            const marketPrefix = marketPrefixMatch ? marketPrefixMatch[0] : '';
            
            let html = `
                <div class="predictive-search__suggestions">
                    <tabs-nav class="tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border">
                        <scrollable-content class="tabs-nav__scroller hide-scrollbar">
                            <div class="tabs-nav__scroller-inner">
                                <div class="tabs-nav__item-list">
                                    <button type="button" class="tabs-nav__item heading heading--small" aria-expanded="true" aria-controls="predictive-search-suggestions">
                                        ${window.searchSpringTranslations.suggestions || 'Suggestions'}
                                    </button>
                                </div>
                            </div>
                        </scrollable-content>
                    </tabs-nav>

                    <ul class="predictive-search__suggestions-list list--unstyled" role="list">
            `;
            
            // Add main suggestion
            if (suggestionsData.suggested) {
                html += `
                    <li class="predictive-search__suggestion-item">
                        <a href="${marketPrefix}/search?q=${encodeURIComponent(suggestionsData.suggested.text)}" class="predictive-search__suggestion-link">
                            <strong>${suggestionsData.suggested.text}</strong>
                        </a>
                    </li>
                `;
            }
            
            // Add alternatives if any
            if (suggestionsData.alternatives && suggestionsData.alternatives.length > 0) {
                suggestionsData.alternatives.forEach(alt => {
                    html += `
                        <li class="predictive-search__suggestion-item">
                            <a href="${marketPrefix}/search?q=${encodeURIComponent(alt.text)}" class="predictive-search__suggestion-link">
                                ${alt.text}
                            </a>
                        </li>
                    `;
                });
            }
            
            html += `
                    </ul>
                </div>
            `;
            
            return html;
        }

        async _doPredictiveSearch(searchTerm) {
            // Initialize SearchSpring cookies if not already done
            if (!SearchSpringCommon.getCookie('ssUserId')) {
                initializeSearchSpringCookies();
            }
            
            try {
                // Use SearchSpring autocomplete API
                const searchResults = await getAutocompleteSuggestions(getSiteId(), searchTerm, {
                    q: searchTerm,
                    resultsFormat: 'json',
                    limit: 10
                });
                
                // Process SearchSpring results using the original HTML structure
                const processedResults = await this._processSearchSpringResults(searchResults, searchTerm);
                
                return {
                    hasResults: processedResults.hasResults,
                    html: processedResults.html
                };
                
            } catch (error) {}

            // Return original error structure
            return {
                hasResults: false,
                html: `
                        <div id="shopify-section-predictive-search" class="shopify-section shopify-section--predictive-search">
                            <p class="text--large">Search temporarily unavailable. Please try again.</p>
                            <div class="button-wrapper">
                                <button type="button" data-action="reset-search" class="button button--primary">New search</button>
                            </div>
                        </div>
                    `
            };
        }

        // END OF SEARCH SPRING INTEGRATION

        async _doLiquidSearch(o) {
            let i = []
              , e = window.themeVariables.settings.searchMode.split(",").filter(a=>"collection" !== a);
            e.forEach(a=>{
                i.push(fetch(`${window.themeVariables.routes.searchUrl}?section_id=predictive-search-compatibility&q=${o}&type=${a}&options[unavailable_products]=${window.themeVariables.settings.searchUnavailableProducts}&options[prefix]=last`, {
                    signal: this.abortController.signal
                }))
            }
            );
            let j = await Promise.all(i)
              , c = {};
            for (let[k,l] of j.entries()) {
                let m = await l.text()
                  , b = document.createElement("div");
                b.innerHTML = m,
                b.innerHTML = b.firstElementChild.innerHTML,
                b.childElementCount > 0 && (c[e[k]] = b.innerHTML)
            }
            if (!(Object.keys(c).length > 0))
                return {
                    hasResults: !1,
                    html: `
        <p class="text--large">${window.themeVariables.strings.searchNoResults}</p>
          <div class="button-wrapper">
            <button type="button" data-action="reset-search" class="button button--primary">${window.themeVariables.strings.searchNewSearch}</button>
          </div>
        `
                };
            {
                let f = Object.entries(c)
                  , g = Object.keys(c)
                  , a = `
        <tabs-nav class="tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border">
          <scrollable-content class="tabs-nav__scroller hide-scrollbar">
            <div class="tabs-nav__scroller-inner">
              <div class="tabs-nav__item-list">
      `;
                for (let[d,p] of f)
                    a += `
          <button type="button" class="tabs-nav__item heading heading--small" aria-expanded="${d === g[0] ? "true" : "false"}" aria-controls="predictive-search-${d}">
            ${window.themeVariables.strings["search" + d.charAt(0).toUpperCase() + d.slice(1) + "s"]}
          </button>
        `;
                for (let[h,n] of (a += `
              </div>
            </div>
          </scrollable-content>
        </tabs-nav>
      `,
                a += '<div class="predictive-search__results-categories">',
                f))
                    a += `
          <div class="predictive-search__results-categories-item" ${h !== g[0] ? "hidden" : ""} id="predictive-search-${h}">
            ${n}
          </div>
        `;
                return a += "</div>",
                {
                    hasResults: !0,
                    html: a
                }
            }
        }

        _startNewSearch() {
            this.inputElement.value = "",
            this.inputElement.focus();
            let a = new Event("input",{
                bubbles: !0,
                cancelable: !0
            });
            this.inputElement.dispatchEvent(a)
        }

        _supportPredictiveApi() {
            let a = JSON.parse(document.getElementById("shopify-features").innerHTML);
            return a.predictiveSearch
        }

        _debounce(a, b) {
            let c = null;
            return (...d)=>{
                clearTimeout(c),
                c = setTimeout(()=>{
                    a.apply(this, d)
                }
                , b)
            }
        }
    }
    ;
    window.customElements.define("predictive-search-drawer", av);
    var aw = class extends HTMLElement {
        connectedCallback() {
            if (this.prevNextButtons = this.querySelector("prev-next-buttons"),
            this.pageDots = this.querySelector("page-dots"),
            this.scrollBarElement = this.querySelector(".timeline__progress-bar"),
            this.listWrapperElement = this.querySelector(".timeline__list-wrapper"),
            this.listItemElements = Array.from(this.querySelectorAll(".timeline__item")),
            this.isScrolling = !1,
            this.listItemElements.length > 1) {
                this.addEventListener("prev-next:prev", this.previous.bind(this)),
                this.addEventListener("prev-next:next", this.next.bind(this)),
                this.addEventListener("page-dots:changed", a=>this.select(a.detail.index)),
                Shopify.designMode && this.addEventListener("shopify:block:select", a=>{
                    this.select([...a.target.parentNode.children].indexOf(a.target), !a.detail.load)
                }
                ),
                this.itemIntersectionObserver = new IntersectionObserver(this._onItemObserved.bind(this),{
                    threshold: .4
                });
                let a = window.matchMedia(window.themeVariables.breakpoints.pocket);
                a.addListener(this._onMediaChanged.bind(this)),
                this._onMediaChanged(a)
            }
        }
        get selectedIndex() {
            return this.listItemElements.findIndex(a=>!a.hasAttribute("hidden"))
        }
        previous() {
            this.select(Math.max(0, this.selectedIndex - 1))
        }
        next() {
            this.select(Math.min(this.selectedIndex + 1, this.listItemElements.length - 1))
        }
        select(a, b=!0) {
            let d = this.listItemElements[a]
              , c = d.getBoundingClientRect();
            b && (this.isScrolling = !0,
            setTimeout(()=>this.isScrolling = !1, 800)),
            window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? this.listWrapperElement.scrollTo({
                behavior: b ? "smooth" : "auto",
                left: this.listItemElements[0].clientWidth * a
            }) : this.listWrapperElement.scrollBy({
                behavior: b ? "smooth" : "auto",
                left: Math.floor(c.left - window.innerWidth / 2 + c.width / 2)
            }),
            this._onItemSelected(a)
        }
        _onItemSelected(a) {
            var b;
            let c = this.listItemElements[a];
            c.removeAttribute("hidden", "false"),
            bJ(c).forEach(a=>a.setAttribute("hidden", "")),
            this.prevNextButtons.isPrevDisabled = 0 === a,
            this.prevNextButtons.isNextDisabled = a === this.listItemElements.length - 1,
            this.pageDots.selectedIndex = a,
            null == (b = this.scrollBarElement) || b.style.setProperty("--transform", `${100 / (this.listItemElements.length - 1) * a}%`)
        }
        _onItemObserved(a) {
            this.isScrolling || a.forEach(a=>{
                a.isIntersecting && this._onItemSelected([...a.target.parentNode.children].indexOf(a.target))
            }
            )
        }
        _onMediaChanged(a) {
            a.matches ? this.listItemElements.forEach(a=>this.itemIntersectionObserver.observe(a)) : this.listItemElements.forEach(a=>this.itemIntersectionObserver.unobserve(a))
        }
    }
    ;
    window.customElements.define("time-line", aw);
    var k = class extends a6 {
        connectedCallback() {
            this.pressItemsWrapper = this.querySelector(".press-list__wrapper"),
            this.pressItems = Array.from(this.querySelectorAll("press-item")),
            this.pageDots = this.querySelector("page-dots"),
            this.pressItems.length > 1 && (Shopify.designMode && this.addEventListener("shopify:block:select", a=>{
                var b;
                null == (b = this.intersectionObserver) || b.disconnect(),
                (a.detail.load || !a.target.selected) && this.select(a.target.index, !a.detail.load)
            }
            ),
            this.pressItemsWrapper.addEventListener("swiperight", this.previous.bind(this)),
            this.pressItemsWrapper.addEventListener("swipeleft", this.next.bind(this)),
            this.addEventListener("page-dots:changed", a=>this.select(a.detail.index)),
            this._blockVerticalScroll()),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility()
        }
        async _setupVisibility() {
            await this.untilVisible(),
            this.pressItems[this.selectedIndex].transitionToEnter()
        }
        get selectedIndex() {
            return this.pressItems.findIndex(a=>a.selected)
        }
        previous() {
            this.select((this.selectedIndex - 1 + this.pressItems.length) % this.pressItems.length)
        }
        next() {
            this.select((this.selectedIndex + 1 + this.pressItems.length) % this.pressItems.length)
        }
        async select(a, b=!0) {
            let c = this.pressItems[this.selectedIndex]
              , d = this.pressItems[a];
            await c.transitionToLeave(b),
            this.pageDots.selectedIndex = a,
            await d.transitionToEnter(b)
        }
    }
    ;
    Object.assign(k.prototype, c),
    window.customElements.define("press-list", k);
    var ax = class extends HTMLElement {
        connectedCallback() {
            this.addEventListener("split-lines:re-split", a=>{
                Array.from(a.target.children).forEach(a=>a.style.visibility = this.selected ? "visible" : "hidden")
            }
            )
        }
        get index() {
            return [...this.parentNode.children].indexOf(this)
        }
        get selected() {
            return !this.hasAttribute("hidden")
        }
        async transitionToLeave(b=!0) {
            let c = await bK(this.querySelectorAll("split-lines"))
              , a = new bP(new bS(c.reverse().map((a,b)=>new bQ(a,{
                visibility: ["visible", "hidden"],
                clipPath: ["inset(0 0 0 0)", "inset(0 0 100% 0)"],
                transform: ["translateY(0)", "translateY(100%)"]
            },{
                duration: 350,
                delay: 60 * b,
                easing: "cubic-bezier(0.68, 0.00, 0.77, 0.00)"
            }))));
            b ? a.play() : a.finish(),
            await a.finished,
            this.setAttribute("hidden", "")
        }
        async transitionToEnter(b=!0) {
            this.removeAttribute("hidden");
            let c = await bK(this.querySelectorAll("split-lines, .testimonial__author"))
              , a = new bP(new bS(c.map((a,b)=>new bQ(a,{
                visibility: ["hidden", "visible"],
                clipPath: ["inset(0 0 100% 0)", "inset(0 0 0px 0)"],
                transform: ["translateY(100%)", "translateY(0)"]
            },{
                duration: 550,
                delay: 120 * b,
                easing: "cubic-bezier(0.23, 1, 0.32, 1)"
            }))));
            return b ? a.play() : a.finish(),
            a.finished
        }
    }
    ;
    window.customElements.define("press-item", ax);
    var ay = class extends a6 {
        connectedCallback() {
            this.openingTimeout = null,
            this.currentMegaMenu = null,
            this.delegate.on("mouseenter", ".has-dropdown", (a,b)=>{
                a.target === b && null !== a.relatedTarget && this.openDropdown(b)
            }
            , !0),
            this.delegate.on("click", ".header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]", (b,a)=>{
                window.matchMedia("(hover: hover)").matches || "true" === a.getAttribute("aria-expanded") || (b.preventDefault(),
                this.openDropdown(a.parentElement))
            }
            ),
            this.delegate.on("shopify:block:select", a=>this.openDropdown(a.target.parentElement)),
            this.delegate.on("shopify:block:deselect", a=>this.closeDropdown(a.target.parentElement))
        }
        openDropdown(a) {
            let c = a.querySelector("[aria-controls]")
              , b = a.querySelector(`#${c.getAttribute("aria-controls")}`);
            this.currentMegaMenu = b.classList.contains("mega-menu") ? b : null;
            let d = setTimeout(()=>{
                if ("true" === c.getAttribute("aria-expanded"))
                    return;
                if (c.setAttribute("aria-expanded", "true"),
                b.removeAttribute("hidden"),
                b.classList.contains("mega-menu") && !bI.prefersReducedMotion()) {
                    let e = Array.from(b.querySelectorAll(".mega-menu__column, .mega-menu__image-push"));
                    e.forEach(a=>{
                        a.getAnimations().forEach(a=>a.cancel()),
                        a.style.opacity = 0
                    }
                    );
                    let f = new bP(new bS(e.map((a,b)=>new bQ(a,{
                        opacity: [0, 1],
                        transform: ["translateY(20px)", "translateY(0)"]
                    },{
                        duration: 250,
                        delay: 100 + 60 * b,
                        easing: "cubic-bezier(0.65, 0, 0.35, 1)"
                    }))));
                    f.play()
                }
                let g = b=>{
                    null !== b.relatedTarget && (this.closeDropdown(a),
                    a.removeEventListener("mouseleave", g))
                }
                ;
                a.addEventListener("mouseleave", g),
                d = null,
                this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open",{
                    bubbles: !0
                }))
            }
            , 100);
            a.addEventListener("mouseleave", ()=>{
                d && clearTimeout(d)
            }
            , {
                once: !0
            })
        }
        closeDropdown(a) {
            let b = a.querySelector("[aria-controls]")
              , c = a.querySelector(`#${b.getAttribute("aria-controls")}`);
            requestAnimationFrame(()=>{
                c.classList.add("is-closing"),
                b.setAttribute("aria-expanded", "false"),
                setTimeout(()=>{
                    c.setAttribute("hidden", ""),
                    clearTimeout(this.openingTimeout),
                    c.classList.remove("is-closing")
                }
                , c.classList.contains("mega-menu") && this.currentMegaMenu !== c ? 250 : 0),
                this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close",{
                    bubbles: !0
                }))
            }
            )
        }
    }
    ;
    window.customElements.define("desktop-navigation", ay);
    var az = class extends au {
        get apparitionAnimation() {
            if (this._apparitionAnimation)
                return this._apparitionAnimation;
            if (!bI.prefersReducedMotion()) {
                let a = Array.from(this.querySelectorAll('.mobile-nav__item[data-level="1"]'))
                  , b = [];
                b.push(new bS(a.map((b,a)=>new bQ(b,{
                    opacity: [0, 1],
                    transform: ["translateX(-40px)", "translateX(0)"]
                },{
                    duration: 300,
                    delay: 300 + 120 * a - Math.min(2 * a * a, 120 * a),
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)"
                }))));
                let c = this.querySelector(".drawer__footer");
                return c && b.push(new bQ(c,{
                    opacity: [0, 1],
                    transform: ["translateY(100%)", "translateY(0)"]
                },{
                    duration: 300,
                    delay: 500 + Math.max(125 * a.length - 25 * a.length, 25),
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)"
                })),
                this._apparitionAnimation = new bP(new bS(b))
            }
        }
        attributeChangedCallback(a, b, c) {
            super.attributeChangedCallback(a, b, c),
            "open" === a && (this.open && this.apparitionAnimation && (Array.from(this.querySelectorAll('.mobile-nav__item[data-level="1"], .drawer__footer')).forEach(a=>a.style.opacity = 0),
            this.apparitionAnimation.play()),
            a4(this, this.open ? "mobile-nav:open" : "mobile-nav:close"))
        }
    }
    ;
    window.customElements.define("mobile-navigation", az);
    var aA = class extends a6 {
        connectedCallback() {
            window.ResizeObserver && (this.resizeObserver = new ResizeObserver(this._updateCustomProperties.bind(this)),
            this.resizeObserver.observe(this),
            this.resizeObserver.observe(this.querySelector(".header__wrapper"))),
            this.isTransparent && (this.isTransparencyDetectionLocked = !1,
            this.delegate.on("desktop-nav:dropdown:open", ()=>this.lockTransparency = !0),
            this.delegate.on("desktop-nav:dropdown:close", ()=>this.lockTransparency = !1),
            this.rootDelegate.on("mobile-nav:open", ()=>this.lockTransparency = !0),
            this.rootDelegate.on("mobile-nav:close", ()=>this.lockTransparency = !1),
            this.delegate.on("mouseenter", this._checkTransparentHeader.bind(this), !0),
            this.delegate.on("mouseleave", this._checkTransparentHeader.bind(this)),
            this.isSticky && (this._checkTransparentHeader(),
            this._onWindowScrollListener = bF(this._checkTransparentHeader.bind(this), 100),
            window.addEventListener("scroll", this._onWindowScrollListener)))
        }
        disconnectedCallback() {
            super.disconnectedCallback(),
            window.ResizeObserver && this.resizeObserver.disconnect(),
            this.isTransparent && this.isSticky && window.removeEventListener("scroll", this._onWindowScrollListener)
        }
        get isSticky() {
            return this.hasAttribute("sticky")
        }
        get isTransparent() {
            return this.hasAttribute("transparent")
        }
        get transparentHeaderThreshold() {
            return 25
        }
        set lockTransparency(a) {
            this.isTransparencyDetectionLocked = a,
            this._checkTransparentHeader()
        }
        _updateCustomProperties(a) {
            a.forEach(a=>{
                if (a.target === this) {
                    let b = a.borderBoxSize ? a.borderBoxSize.length > 0 ? a.borderBoxSize[0].blockSize : a.borderBoxSize.blockSize : a.target.clientHeight;
                    document.documentElement.style.setProperty("--header-height", `${b}px`)
                }
                if (a.target.classList.contains("header__wrapper")) {
                    let c = a.borderBoxSize ? a.borderBoxSize.length > 0 ? a.borderBoxSize[0].blockSize : a.borderBoxSize.blockSize : a.target.clientHeight;
                    document.documentElement.style.setProperty("--header-height-without-bottom-nav", `${c}px`)
                }
            }
            )
        }
        _checkTransparentHeader(a) {
            this.isTransparencyDetectionLocked || window.scrollY > this.transparentHeaderThreshold || a && "mouseenter" === a.type ? this.classList.remove("header--transparent") : this.classList.add("header--transparent")
        }
    }
    ;
    window.customElements.define("store-header", aA);
    var bU = class {
        constructor(a) {
            this.photoSwipeInstance = a,
            this.delegate = new m(this.photoSwipeInstance.scrollWrap),
            this.maxSpreadZoom = window.themeVariables.settings.mobileZoomFactor || 2,
            this.pswpUi = this.photoSwipeInstance.scrollWrap.querySelector(".pswp__ui"),
            this.delegate.on("click", '[data-action="pswp-close"]', this._close.bind(this)),
            this.delegate.on("click", '[data-action="pswp-prev"]', this._goToPrev.bind(this)),
            this.delegate.on("click", '[data-action="pswp-next"]', this._goToNext.bind(this)),
            this.delegate.on("click", '[data-action="pswp-move-to"]', this._moveTo.bind(this)),
            this.photoSwipeInstance.listen("close", this._onPswpClosed.bind(this)),
            this.photoSwipeInstance.listen("doubleTap", this._onPswpDoubleTap.bind(this)),
            this.photoSwipeInstance.listen("beforeChange", this._onPswpBeforeChange.bind(this)),
            this.photoSwipeInstance.listen("initialZoomInEnd", this._onPswpInitialZoomInEnd.bind(this)),
            this.photoSwipeInstance.listen("initialZoomOut", this._onPswpInitialZoomOut.bind(this)),
            this.photoSwipeInstance.listen("parseVerticalMargin", this._onPswpParseVerticalMargin.bind(this)),
            this.delegate.on("pswpTap", ".pswp__img", this._onPswpTap.bind(this))
        }
        init() {
            let b = this.pswpUi.querySelector(".pswp__prev-next-buttons")
              , a = this.pswpUi.querySelector(".pswp__dots-nav-wrapper");
            if (this.photoSwipeInstance.items.length <= 1) {
                b.style.display = "none",
                a.style.display = "none";
                return
            }
            b.style.display = "",
            a.style.display = "";
            let c = "";
            this.photoSwipeInstance.items.forEach((b,a)=>{
                c += `
        <button class="dots-nav__item tap-area" ${0 === a ? 'aria-current="true"' : ""} data-action="pswp-move-to">
          <span class="visually-hidden">Go to slide ${a}</span>
        </button>
      `
            }
            ),
            a.querySelector(".pswp__dots-nav-wrapper .dots-nav").innerHTML = c
        }
        _close() {
            this.photoSwipeInstance.close()
        }
        _goToPrev() {
            this.photoSwipeInstance.prev()
        }
        _goToNext() {
            this.photoSwipeInstance.next()
        }
        _moveTo(b, a) {
            this.photoSwipeInstance.goTo([...a.parentNode.children].indexOf(a))
        }
        _onPswpClosed() {
            this.delegate.off("pswpTap")
        }
        _onPswpDoubleTap(b) {
            let a = this.photoSwipeInstance.currItem.initialZoomLevel;
            this.photoSwipeInstance.getZoomLevel() !== a ? this.photoSwipeInstance.zoomTo(a, b, 333) : this.photoSwipeInstance.zoomTo(a < .7 ? 1 : this.maxSpreadZoom, b, 333)
        }
        _onPswpTap(a) {
            "mouse" === a.detail.pointerType && this.photoSwipeInstance.toggleDesktopZoom(a.detail.releasePoint)
        }
        _onPswpBeforeChange() {
            if (this.photoSwipeInstance.items.length <= 1)
                return;
            let a = this.photoSwipeInstance.scrollWrap.querySelector(`.dots-nav__item:nth-child(${this.photoSwipeInstance.getCurrentIndex() + 1})`);
            a.setAttribute("aria-current", "true"),
            bJ(a).forEach(a=>a.removeAttribute("aria-current"))
        }
        _onPswpInitialZoomInEnd() {
            var a;
            null == (a = this.pswpUi) || a.classList.remove("pswp__ui--hidden")
        }
        _onPswpInitialZoomOut() {
            var a;
            null == (a = this.pswpUi) || a.classList.add("pswp__ui--hidden")
        }
        _onPswpParseVerticalMargin(a) {
            a.vGap.bottom = this.photoSwipeInstance.items.length <= 1 || window.matchMedia(window.themeVariables.breakpoints.lapAndUp).matches ? 0 : 60
        }
    }
      , aB = class extends o {
        connectedCallback() {
            super.connectedCallback(),
            this.mediaElement = this.closest(".product__media"),
            this.maxSpreadZoom = window.themeVariables.settings.mobileZoomFactor || 2,
            J.load("photoswipe")
        }
        disconnectedCallback() {
            var a;
            super.disconnectedCallback(),
            null == (a = this.photoSwipeInstance) || a.destroy()
        }
        async attributeChangedCallback(a, b, c) {
            super.attributeChangedCallback(a, b, c),
            "open" === a && this.open && (await J.load("photoswipe"),
            this._openPhotoSwipe())
        }
        async _openPhotoSwipe() {
            let a = await this._buildItems();
            this.photoSwipeInstance = new window.ThemePhotoSwipe(this,bU,a,{
                index: a.findIndex(a=>a.selected),
                maxSpreadZoom: this.maxSpreadZoom,
                loop: !1,
                allowPanToNext: !1,
                closeOnScroll: !1,
                closeOnVerticalDrag: bI.supportsHover(),
                showHideOpacity: !0,
                arrowKeys: !0,
                history: !1,
                getThumbBoundsFn: ()=>{
                    let b = this.mediaElement.querySelector(".product__media-item.is-selected")
                      , c = window.pageYOffset || document.documentElement.scrollTop
                      , a = b.getBoundingClientRect();
                    return {
                        x: a.left,
                        y: a.top + c,
                        w: a.width
                    }
                }
                ,
                getDoubleTapZoom: (b,a)=>b ? a.w > a.h ? 1.6 : 1 : a.initialZoomLevel < .7 ? 1 : 1.33
            });
            let b = null;
            this.photoSwipeInstance.updateSize = new Proxy(this.photoSwipeInstance.updateSize,{
                apply(a, c, d) {
                    b !== window.innerWidth && (a(arguments),
                    b = window.innerWidth)
                }
            }),
            this.photoSwipeInstance.listen("close", ()=>{
                this.open = !1
            }
            ),
            this.photoSwipeInstance.init()
        }
        async _buildItems() {
            let a = Array.from(this.mediaElement.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)'))
              , b = await S.load(this.getAttribute("product-handle"));
            return Promise.resolve(a.map(d=>{
                let a = b.media.find(a=>a.id === parseInt(d.getAttribute("data-media-id")))
                  , e = bN(a, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800, 2e3, 2200, 2400, 2600, 2800, 3e3])
                  , c = Math.min(e[e.length - 1], window.innerWidth);
                return {
                    selected: d.classList.contains("is-selected"),
                    src: bL(a, `${Math.ceil(Math.min(c * window.devicePixelRatio * this.maxSpreadZoom, 3e3))}x`),
                    msrc: d.firstElementChild.currentSrc,
                    originalMedia: a,
                    w: c,
                    h: parseInt(c / a.aspect_ratio)
                }
            }
            ))
        }
    }
    ;
    window.customElements.define("product-image-zoom", aB);
    var aC = class extends HTMLElement {
        connectedCallback() {
            var a;
            let b = this.querySelector("script");
            b && (this.inventories = JSON.parse(b.innerHTML),
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this)))
        }
        _onVariantChanged(a) {
            var b;
            null == (b = this.querySelector("span")) || b.remove(),
            a.detail.variant && "" !== this.inventories[a.detail.variant.id] ? (this.hidden = !1,
            this.insertAdjacentHTML("afterbegin", this.inventories[a.detail.variant.id])) : this.hidden = !0
        }
    }
    ;
    window.customElements.define("product-inventory", aC);
    var aD = class extends HTMLElement {
        connectedCallback() {
            var a;
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this)),
            Shopify.designMode && Shopify.PaymentButton && Shopify.PaymentButton.init()
        }
        _onVariantChanged(a) {
            this._updateAddToCartButton(a.detail.variant),
            this._updateDynamicCheckoutButton(a.detail.variant)
        }
        _updateAddToCartButton(c) {
            let a = this.querySelector("[data-product-add-to-cart-button]");
            if (!a)
                return;
            let b = "";
            a.classList.remove("button--primary", "button--secondary", "button--ternary"),
            c ? c.available ? (a.removeAttribute("disabled"),
            a.classList.add(a.hasAttribute("data-use-primary") ? "button--primary" : "button--secondary"),
            b = a.getAttribute("data-button-content")) : (a.setAttribute("disabled", "disabled"),
            a.classList.add("button--ternary"),
            b = window.themeVariables.strings.productFormSoldOut) : (a.setAttribute("disabled", "disabled"),
            a.classList.add("button--ternary"),
            b = window.themeVariables.strings.productFormUnavailable),
            "loader-button" === a.getAttribute("is") ? a.firstElementChild.innerHTML = b : a.innerHTML = b
        }
        _updateDynamicCheckoutButton(a) {
            let b = this.querySelector(".shopify-payment-button");
            b && (b.style.display = a && a.available ? "block" : "none")
        }
    }
    ;
    window.customElements.define("product-payment-container", aD);
    var aE = class extends a6 {
        connectedCallback() {
            var a;
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this))
        }
        _onVariantChanged(c) {
            let a = c.detail.variant;
            if (a) {
                let b = this.querySelector('[name="id"]');
                b.value = a.id,
                b.dispatchEvent(new Event("change",{
                    bubbles: !0
                }))
            }
        }
    }
    ;
    window.customElements.define("product-payment-terms", aE);
    var aF = class extends HTMLFormElement {
        connectedCallback() {
            this.id.disabled = !1,
            "page" !== window.themeVariables.settings.cartType && "cart" !== window.themeVariables.settings.pageType && (this._boundOnSubmit || (this._boundOnSubmit = this._onSubmit.bind(this)), this.removeEventListener("submit", this._boundOnSubmit), this.addEventListener("submit", this._boundOnSubmit))
        }
        disconnectedCallback() {
            this._boundOnSubmit && this.removeEventListener("submit", this._boundOnSubmit)
        }
        async _onSubmit(e) {
            if (e.preventDefault(),
            !this.checkValidity()) {
                this.reportValidity();
                return
            }
            let d = Array.from(this.elements).filter(a=>"submit" === a.type);
            d.forEach(a=>{
                a.setAttribute("disabled", "disabled"),
                a.setAttribute("aria-busy", "true")
            }
            );
            let a = new FormData(this);

            if(themeVariables.settings.xoEnabled) {
                const payload = {
                action: "add-to-cart",
                target: {
                    product: a.get("product-id"),
                },
                metadata: {
                    ...(typeof metadata != "undefined" ? metadata : {}),
                    quantity: Number(a.get("quantity")),
                    locale: "en_GB",
                },
                };
                xo.activity.send(payload);
            }

            a.append("sections", [document.getElementById("mini-cart")?.getAttribute("section") || "mini-cart"]),
            a.delete("option1"),
            a.delete("option2"),
            a.delete("option3");
            let c = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
                body: a,
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            });
            d.forEach(a=>{
                a.removeAttribute("disabled"),
                a.removeAttribute("aria-busy")
            }
            );
            let b = await c.json();
            await Shopify.getCart();
            c.ok && (this.dispatchEvent(new CustomEvent("variant:added",{
                bubbles: !0,
                detail: {
                    variant: b.hasOwnProperty("items") ? b.items[0] : b
                }
            })),
            fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async c=>{
                let a = await c.json();
                document.documentElement.dispatchEvent(new CustomEvent("cart:updated",{
                    bubbles: !0,
                    detail: {
                        cart: a
                    }
                })),
                a.sections = b.sections,
                document.documentElement.dispatchEvent(new CustomEvent("cart:refresh",{
                    bubbles: !0,
                    detail: {
                        cart: a,
                        openMiniCart: "drawer" === window.themeVariables.settings.cartType && null === this.closest(".drawer")
                    }
                }))
            }
            )),
            this.dispatchEvent(new CustomEvent("cart-notification:show",{
                bubbles: !0,
                cancelable: !0,
                detail: {
                    status: c.ok ? "success" : "error",
                    error: b.description || ""
                }
            }))
        }
    }
    ;
    window.customElements.define("product-form", aF, {
        extends: "form"
    });
    var aG = class extends a6 {
        async connectedCallback() {
            var a;
            this.mainCarousel = this.querySelector("flickity-carousel"),
            this.hasAttribute("reveal-on-scroll") && this._setupVisibility(),
            1 !== this.mainCarousel.childElementCount && (this.selectedVariantMediaId = null,
            this.viewInSpaceElement = this.querySelector("[data-shopify-model3d-id]"),
            this.zoomButton = this.querySelector(".product__zoom-button"),
            this.product = await S.load(this.getAttribute("product-handle")),
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this)),
            this.mainCarousel.addEventListener("model:played", ()=>this.mainCarousel.setDraggable(!1)),
            this.mainCarousel.addEventListener("model:paused", ()=>this.mainCarousel.setDraggable(!0)),
            this.mainCarousel.addEventListener("flickity:ready", this._onFlickityReady.bind(this)),
            this.mainCarousel.addEventListener("flickity:slide-changed", this._onFlickityChanged.bind(this)),
            this.mainCarousel.addEventListener("flickity:slide-settled", this._onFlickitySettled.bind(this)),
            this._onFlickityReady())
        }
        get thumbnailsPosition() {
            return window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? "bottom" : this.getAttribute("thumbnails-position")
        }
        async _setupVisibility() {
            await this.untilVisible();
            let a = await this.mainCarousel.flickityInstance
              , b = a ? a.selectedElement.querySelector("img") : this.querySelector(".product__media-image-wrapper img")
              , d = bI.prefersReducedMotion();
            await bO(b);
            let c = new bP(new bS([new bQ(b,{
                opacity: [0, 1],
                transform: [`scale(${d ? 1 : 1.1})`, "scale(1)"]
            },{
                duration: 500,
                easing: "cubic-bezier(0.65, 0, 0.35, 1)"
            }), new bS(Array.from(this.querySelectorAll(".product__thumbnail-item:not(.is-filtered)")).map((a,b)=>new bQ(a,{
                opacity: [0, 1],
                transform: "left" === this.thumbnailsPosition ? [`translateY(${d ? 0 : "40px"})`, "translateY(0)"] : [`translateX(${d ? 0 : "50px"})`, "translateX(0)"]
            },{
                duration: 250,
                delay: d ? 0 : 100 * b,
                easing: "cubic-bezier(0.75, 0, 0.175, 1)"
            })))]));
            this._hasSectionReloaded ? c.finish() : c.play()
        }
        async _onVariantChanged(a) {
            let b = a.detail.variant
              , d = []
              , e = !1;
            this.product.media.forEach(a=>{
                var c;
                let f = b.featured_media && a.id === b.featured_media.id;
                if ((null == (c = a.alt) ? void 0 : c.includes("#")) && (e = !0,
                !f)) {
                    let g = a.alt.split("#")
                      , h = g.pop().split("_");
                    this.product.options.forEach(c=>{
                        c.name.toLowerCase() === h[0].toLowerCase() && b.options[c.position - 1].toLowerCase() !== h[1].trim().toLowerCase() && d.push(a.id)
                    }
                    )
                }
            }
            );
            let c = [...new Set(Array.from(this.querySelectorAll(".is-filtered[data-media-id]")).map(a=>parseInt(a.getAttribute("data-media-id"))))];
            if (c.some(a=>!d.includes(a))) {
                let f = b.featured_media ? b.featured_media.id : this.product.media.map(a=>a.id).filter(a=>!d.includes(a))[0];
                Array.from(this.querySelectorAll("[data-media-id]")).forEach(a=>{
                    a.classList.toggle("is-filtered", d.includes(parseInt(a.getAttribute("data-media-id")))),
                    a.classList.toggle("is-selected", f === parseInt(a.getAttribute("data-media-id"))),
                    a.classList.toggle("is-initial-selected", f === parseInt(a.getAttribute("data-media-id")))
                }
                ),
                this.mainCarousel.reload()
            } else {
                if (!a.detail.variant.featured_media || this.selectedVariantMediaId === a.detail.variant.featured_media.id)
                    return;
                this.mainCarousel.select(`[data-media-id="${a.detail.variant.featured_media.id}"]`)
            }
            this.selectedVariantMediaId = a.detail.variant.featured_media ? a.detail.variant.featured_media.id : null
        }
        async _onFlickityReady() {
            let a = await this.mainCarousel.flickityInstance;
            if (["video", "external_video"].includes(a.selectedElement.getAttribute("data-media-type")) && this.hasAttribute("autoplay-video")) {
                let b = a.selectedElement.querySelector("native-video, external-video");
                b && "function" == typeof b.play && b.play()
            }
        }
        async _onFlickityChanged() {
            let a = await this.mainCarousel.flickityInstance;
            a.cells.forEach(a=>{
                if (["external_video", "video", "model"].includes(a.element.getAttribute("data-media-type"))) {
                    let b = a.element.querySelector("native-video, external-video, model-media");
                    b && "function" == typeof b.pause && b.pause()
                }
            }
            )
        }
        async _onFlickitySettled() {
            let b = await this.mainCarousel.flickityInstance
              , a = b.selectedElement;
            switch (this.zoomButton && (this.zoomButton.hidden = "image" !== a.getAttribute("data-media-type")),
            this.viewInSpaceElement && this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id")),
            a.getAttribute("data-media-type")) {
            case "model":
                this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", a.getAttribute("data-media-id"));
                let c = a.querySelector("model-media");
                c && "function" == typeof c.play && c.play();
                break;
            case "external_video":
            case "video":
                if (this.hasAttribute("autoplay-video") && "true" !== a.dataset.userPaused) {
                    let d = a.querySelector("native-video, external-video");
                    d && "function" == typeof d.play && d.play()
                }
            }
        }
    }
    ;
    function bV(a, f="") {
        "string" == typeof a && (a = a.replace(".", ""));
        let e = /\{\{\s*(\w+)\s*\}\}/
          , d = f || window.themeVariables.settings.moneyFormat;
        function g(a, b) {
            return null == a || a != a ? b : a
        }
        function c(a, b, c, d) {
            if (b = g(b, 2),
            c = g(c, ","),
            d = g(d, "."),
            isNaN(a) || null == a)
                return 0;
            let e = (a = (a / 100).toFixed(b)).split(".")
              , f = e[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + c)
              , h = e[1] ? d + e[1] : "";
            return f + h
        }
        let b = "";
        switch (d.match(e)[1]) {
        case "amount":
            b = c(a, 2);
            break;
        case "amount_no_decimals":
            b = c(a, 0);
            break;
        case "amount_with_space_separator":
            b = c(a, 2, " ", ".");
            break;
        case "amount_with_comma_separator":
            b = c(a, 2, ".", ",");
            break;
        case "amount_with_apostrophe_separator":
            b = c(a, 2, "'", ".");
            break;
        case "amount_no_decimals_with_comma_separator":
            b = c(a, 0, ".", ",");
            break;
        case "amount_no_decimals_with_space_separator":
            b = c(a, 0, " ");
            break;
        case "amount_no_decimals_with_apostrophe_separator":
            b = c(a, 0, "'")
        }
        return d.indexOf("with_comma_separator"),
        d.replace(e, b)
    }
    window.formatMoney = bV;
    window.customElements.define("product-media", aG);
    var aH = class extends HTMLElement {
        connectedCallback() {
            var a;
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this))
        }
        get priceClass() {
            return this.getAttribute("price-class") || ""
        }
        get unitPriceClass() {
            return this.getAttribute("unit-price-class") || ""
        }
        _onVariantChanged(a) {
            this._updateLabels(a.detail.variant),
            this._updatePrices(a.detail.variant),
            this._updateSku(a.detail.variant)
        }
        _updateLabels(a) {
            let b = this.querySelector("[data-product-label-list]");
            if (b) {
                if (a) {
                    if (b.innerHTML = "",
                    a.available) {
                        if (a.compare_at_price > a.price) {
                            let c = "";
                            c = "percentage" === window.themeVariables.settings.discountMode ? `${Math.round((a.compare_at_price - a.price) * 100 / a.compare_at_price)}%` : bV(a.compare_at_price - a.price),
                            b.innerHTML = `<span class="label label--highlight">${window.themeVariables.strings.collectionDiscount.replace("@savings@", c)}</span>`
                        }
                    } else
                        b.innerHTML = `<span class="label label--subdued">${window.themeVariables.strings.collectionSoldOut}</span>`
                } else
                    b.innerHTML = ""
            }
        }
        _updatePrices(a) {
            let b = this.querySelector("[data-product-price-list]")
              , c = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
            if (b) {
                if (a) {
                    if (b.innerHTML = "",
                    a.compare_at_price > a.price ? (b.innerHTML += `<span class="price price--highlight ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${bV(a.price, c)}</span>`,
                    b.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${bV(a.compare_at_price, c)}</span>`) : b.innerHTML += `<span class="price ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${bV(a.price, c)}</span>`,
                    a.unit_price_measurement) {
                        let d = "";
                        1 !== a.unit_price_measurement.reference_value && (d = `<span class="unit-price-measurement__reference-value">${a.unit_price_measurement.reference_value}</span>`),
                        b.innerHTML += `
          <div class="price text--subdued ${this.unitPriceClass}">
            <div class="unit-price-measurement">
              <span class="unit-price-measurement__price">${bV(a.unit_price)}</span>
              <span class="unit-price-measurement__separator">/</span>
              ${d}
              <span class="unit-price-measurement__reference-unit">${a.unit_price_measurement.reference_unit}</span>
            </div>
          </div>
        `
                    }
                    b.style.display = ""
                } else
                    b.style.display = "none"
            }
        }
        _updateSku(b) {
            let a = this.querySelector("[data-product-sku-container]");
            if (!a)
                return;
            let c = a.querySelector("[data-product-sku-number]");
            b && b.sku ? (c.innerHTML = b.sku,
            a.style.display = "") : a.style.display = "none"
        }
    }
    ;
    window.customElements.define("product-meta", aH);
    var aI = class extends au {
        connectedCallback() {
            super.connectedCallback(),
            this.delegate.on("variant:changed", this._onVariantChanged.bind(this))
        }
        async _load() {
            await super._load(),
            this.imageElement = this.querySelector(".quick-buy-product__image"),
            window.Shopify && window.Shopify.PaymentButton && window.Shopify.PaymentButton.init()
        }
        _onVariantChanged(c) {
            let b = c.detail.variant;
            if (b && Array.from(this.querySelectorAll('[href*="/products"]')).forEach(a=>{
                let c = new URL(a.href);
                c.searchParams.set("variant", b.id),
                a.setAttribute("href", c.toString())
            }
            ),
            !this.imageElement || !b || !b.featured_media)
                return;
            let a = b.featured_media;
            a.alt && this.imageElement.setAttribute("alt", a.alt),
            this.imageElement.setAttribute("width", a.preview_image.width),
            this.imageElement.setAttribute("height", a.preview_image.height),
            this.imageElement.setAttribute("src", bL(a, "342x")),
            this.imageElement.setAttribute("srcset", bM(a, [114, 228, 342]))
        }
    }
    ;
    window.customElements.define("quick-buy-drawer", aI);
    var aJ = class extends H {
        connectedCallback() {
            super.connectedCallback(),
            this.delegate.on("variant:changed", this._onVariantChanged.bind(this)),
            this.delegate.on("variant:added", ()=>this.open = !1)
        }
        async _load() {
            await super._load(),
            this.imageElement = this.querySelector(".quick-buy-product__image")
        }
        _onVariantChanged(c) {
            let b = c.detail.variant;
            if (b && Array.from(this.querySelectorAll('[href*="/products"]')).forEach(a=>{
                let c = new URL(a.href);
                c.searchParams.set("variant", b.id),
                a.setAttribute("href", c.toString())
            }
            ),
            !this.imageElement || !b || !b.featured_media)
                return;
            let a = b.featured_media;
            a.alt && this.imageElement.setAttribute("alt", a.alt),
            this.imageElement.setAttribute("width", a.preview_image.width),
            this.imageElement.setAttribute("height", a.preview_image.height),
            this.imageElement.setAttribute("src", bL(a, "195x")),
            this.imageElement.setAttribute("srcset", bM(a, [65, 130, 195]))
        }
    }
    ;
    window.customElements.define("quick-buy-popover", aJ);
    var aK = class extends HTMLElement {
        connectedCallback() {
            var a;
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this))
        }
        _onVariantChanged(a) {
            a.detail.variant ? this._renderForVariant(a.detail.variant.id) : this.innerHTML = ""
        }
        async _renderForVariant(b) {
            let c = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/variants/${b}?section_id=store-availability`)
              , a = document.createElement("div");
            a.innerHTML = await c.text(),
            this.innerHTML = a.firstElementChild.innerHTML.trim()
        }
    }
    ;
    window.customElements.define("store-pickup", aK);
    var aL = class extends a6 {
        async connectedCallback() {
            if (this.masterSelector = document.getElementById(this.getAttribute("form-id")).id,
            this.optionSelectors = Array.from(this.querySelectorAll("[data-selector-type]")),
            !this.masterSelector) {
                return
            }
            this.product = await S.load(this.productHandle),
            this.delegate.on("change", '[name^="option"]', this._onOptionChanged.bind(this)),
            this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this)),
            this._updateDisableSelectors(),
            this.selectVariant(this.selectedVariant.id)
        }
        get selectedVariant() {
            return this._getVariantById(parseInt(this.masterSelector.value))
        }
        get productHandle() {
            return this.getAttribute("handle")
        }
        get hideSoldOutVariants() {
            return this.hasAttribute("hide-sold-out-variants")
        }
        get updateUrl() {
            return this.hasAttribute("update-url")
        }
        selectVariant(a) {
            var b;
            this._isVariantSelectable(this._getVariantById(a)) || (a = this._getFirstMatchingAvailableOrSelectableVariant().id),
            (null == (b = this.selectedVariant) ? void 0 : b.id) !== a && (this.masterSelector.value = a,
            this.masterSelector.dispatchEvent(new Event("change",{
                bubbles: !0
            })),
            this.updateUrl && history.replaceState,
            this._updateDisableSelectors(),
            a4(this.masterSelector.form, "variant:changed", {
                variant: this.selectedVariant
            }))
        }
        _onOptionChanged() {
            var a;
            this.selectVariant(null == (a = this._getVariantFromOptions()) ? void 0 : a.id)
        }
        _onMasterSelectorChanged() {
            var a;
            let b = (null == (a = this.selectedVariant) ? void 0 : a.options) || [];
            b.forEach((b,d)=>{
                let a = this.querySelector(`input[name="option${d + 1}"][value="${CSS.escape(b)}"], select[name="option${d + 1}"]`)
                  , c = !1;
                "SELECT" === a.tagName ? (c = a.value !== b,
                a.value = b) : "INPUT" === a.tagName && (c = !a.checked && a.value === b,
                a.checked = a.value === b),
                c && a.dispatchEvent(new Event("change",{
                    bubbles: !0
                }))
            }
            )
        }
        _getVariantById(a) {
            return this.product.variants.find(b=>b.id === a)
        }
        _getVariantFromOptions() {
            let a = this._getSelectedOptionValues();
            return this.product.variants.find(b=>b.options.every((b,c)=>b === a[c]))
        }
        _isVariantSelectable(a) {
            return !!a && (a.available || !this.hideSoldOutVariants && !a.available)
        }
        _getFirstMatchingAvailableOrSelectableVariant() {
            let b = this._getSelectedOptionValues()
              , a = null
              , c = 0;
            do
                b.pop(),
                c += 1,
                a = this.product.variants.find(a=>this.hideSoldOutVariants ? a.available && a.options.slice(0, a.options.length - c).every((a,c)=>a === b[c]) : a.options.slice(0, a.options.length - c).every((a,c)=>a === b[c]));
            while (!a && b.length > 0)return a
        }
        _getSelectedOptionValues() {
            let a = [];
            return Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach(b=>a.push(b.value)),
            a
        }
        _updateDisableSelectors() {
            let a = this.selectedVariant;
            if (!a)
                return;
            let b = (b,c,d,e)=>{
                let f = b.getAttribute("data-selector-type")
                  , a = "";
                switch (f) {
                case "color":
                    a = `.color-swatch:nth-child(${c + 1})`;
                    break;
                case "variant-image":
                    a = `.variant-swatch:nth-child(${c + 1})`;
                    break;
                case "block":
                    a = `.block-swatch:nth-child(${c + 1})`;
                    break;
                case "dropdown":
                    a = `.combo-box__option-item:nth-child(${c + 1})`
                }
                b.querySelector(a).toggleAttribute("hidden", !e),
                this.hideSoldOutVariants ? b.querySelector(a).toggleAttribute("hidden", !d) : b.querySelector(a).classList.toggle("is-disabled", !d)
            }
            ;
            this.optionSelectors && this.optionSelectors[0] && this.product.options[0].values.forEach((f,c)=>{
                let d = this.product.variants.some(a=>a.option1 === f && a)
                  , e = this.product.variants.some(a=>a.option1 === f && a.available);
                b(this.optionSelectors[0], c, e, d),
                this.optionSelectors[1] && this.product.options[1].values.forEach((f,c)=>{
                    let d = this.product.variants.some(b=>b.option2 === f && b.option1 === a.option1 && b)
                      , e = this.product.variants.some(b=>b.option2 === f && b.option1 === a.option1 && b.available);
                    b(this.optionSelectors[1], c, e, d),
                    this.optionSelectors[2] && this.product.options[2].values.forEach((f,c)=>{
                        let d = this.product.variants.some(b=>b.option3 === f && b.option1 === a.option1 && b.option2 === a.option2 && b)
                          , e = this.product.variants.some(b=>b.option3 === f && b.option1 === a.option1 && b.option2 === a.option2 && b.available);
                        b(this.optionSelectors[2], c, e, d)
                    }
                    )
                }
                )
            }
            )
        }
    }
    ;
    window.customElements.define("product-variants", aL);
    var aM = class extends a6 {
        connectedCallback() {
            this.primaryImageList = Array.from(this.querySelectorAll(".product-item__primary-image")),
            this.delegate.on("change", ".product-item-meta__swatch-list .color-swatch__radio", this._onColorSwatchChanged.bind(this)),
            this.delegate.on("mouseenter", ".product-item-meta__swatch-list .color-swatch__item", this._onColorSwatchHovered.bind(this), !0)
        }
        async _onColorSwatchChanged(e, c) {
            if (Array.from(this.querySelectorAll('[href*="/products"]')).forEach(a=>{
                let b;
                (b = "A" === a.tagName ? new URL(a.href) : new URL(a.getAttribute("href"),`https://${window.themeVariables.routes.host}`)).searchParams.set("variant", c.getAttribute("data-variant-id")),
                a.setAttribute("href", b.toString())
            }
            ),
            c.hasAttribute("data-variant-featured-media")) {
                let a = this.primaryImageList.find(a=>a.getAttribute("data-media-id") === c.getAttribute("data-variant-featured-media"));
                a.setAttribute("loading", "eager");
                let d = a.complete ? Promise.resolve() : new Promise(b=>a.onload = b);
                await d,
                a.removeAttribute("hidden");
                let b = {};
                b = Array.from(a.parentElement.classList).some(a=>["aspect-ratio--short", "aspect-ratio--tall", "aspect-ratio--square"].includes(a)) ? [{
                    clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)",
                    transform: "translate(calc(-50% - 20px), -50%)",
                    zIndex: 1,
                    offset: 0
                }, {
                    clipPath: "polygon(0 0, 20% 0, 5% 100%, 0 100%)",
                    transform: "translate(calc(-50% - 20px), -50%)",
                    zIndex: 1,
                    offset: .3
                }, {
                    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                    offset: 1
                }] : [{
                    clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)",
                    transform: "translateX(-20px)",
                    zIndex: 1,
                    offset: 0
                }, {
                    clipPath: "polygon(0 0, 20% 0, 5% 100%, 0 100%)",
                    transform: "translateX(-20px)",
                    zIndex: 1,
                    offset: .3
                }, {
                    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
                    transform: "translateX(0px)",
                    zIndex: 1,
                    offset: 1
                }],
                await a.animate(b, {
                    duration: 500,
                    easing: "ease-in-out"
                }).finished,
                this.primaryImageList.filter(b=>b.classList.contains("product-item__primary-image") && b !== a).forEach(a=>a.setAttribute("hidden", ""))
            }
        }
        _onColorSwatchHovered(d, a) {
            let b = a.previousElementSibling;
            if (b.hasAttribute("data-variant-featured-media")) {
                let c = this.primaryImageList.find(a=>a.getAttribute("data-media-id") === b.getAttribute("data-variant-featured-media"));
                c.setAttribute("loading", "eager")
            }
        }
    }
    ;
    window.customElements.define("product-item", aM);
    var aN = class extends a6 {
        connectedCallback() {
            this.delegate.on("pagination:page-changed", this._rerender.bind(this)),
            this.delegate.on("facet:criteria-changed", this._rerender.bind(this)),
            this.delegate.on("facet:abort-loading", this._abort.bind(this))
        }
        async _rerender(e) {
            history.replaceState({}, "", e.detail.url );
            if (this.classList.contains("searchspring")) return;
            this._abort(),
            this.showLoadingBar();
            let b = new URL(window.location);
            b.searchParams.set("section_id", this.getAttribute("section-id"));
            try {
                this.abortController = new AbortController;
                let f = await fetch(b.toString(), {
                    signal: this.abortController.signal
                })
                  , g = await f.text()
                  , a = document.createElement("div");
                a.innerHTML = g,
                this.querySelector("#facet-main").innerHTML = a.querySelector("#facet-main").innerHTML;
                let h = Array.from(a.querySelectorAll(".product-facet__active-list:not(:empty)"))
                  , c = document.querySelector(".mobile-toolbar__item--filters");
                c && c.classList.toggle("has-filters", h.length > 0);
                let d = a.querySelector("#facet-filters");
                if (d) {
                    let i = this.querySelector("#facet-filters .drawer__content").scrollTop;
                    Array.from(this.querySelectorAll("#facet-filters-form .collapsible-toggle[aria-controls]")).forEach(c=>{
                        let a = d.querySelector(`[aria-controls="${c.getAttribute("aria-controls")}"]`)
                          , b = "true" === c.getAttribute("aria-expanded");
                        a.setAttribute("aria-expanded", b ? "true" : "false"),
                        a.nextElementSibling.toggleAttribute("open", b),
                        a.nextElementSibling.style.overflow = b ? "visible" : ""
                    }
                    ),
                    this.querySelector("#facet-filters").innerHTML = d.innerHTML,
                    this.querySelector("#facet-filters .drawer__content").scrollTop = i
                }
                let k = this.querySelector(".product-facet__meta-bar") || this.querySelector(".product-facet__product-list") || this.querySelector(".product-facet__main");
                requestAnimationFrame(()=>{
                    k.scrollIntoView({
                        block: "start",
                        behavior: "smooth"
                    })
                }
                ),
                this.hideLoadingBar();

            } catch (j) {
                if ("AbortError" === j.name)
                    return
            }
        }
        _abort() {
            this.abortController && this.abortController.abort()
        }
    }
    ;
    window.customElements.define("product-facet", aN);
    var aO = class extends au {
        connectedCallback() {
            super.connectedCallback(),
            this.delegate.on("change", '[name^="filter."]:not([name="filter.price.low"]):not([name="filter.price.high"])', this._onFilterChanged.bind(this)),
            this.rootDelegate.on("click", '[data-action="clear-filters"]', this._onFiltersCleared.bind(this)),
            this.alwaysVisible && (this.matchMedia = window.matchMedia(window.themeVariables.breakpoints.pocket),
            this.matchMedia.addListener(this._adjustDrawer.bind(this)),
            this._adjustDrawer(this.matchMedia));
        }
        get alwaysVisible() {
            return this.hasAttribute("always-visible")
        }
        _onFiltersCleared(a, b) {
            a.preventDefault(),
            a4(this, "facet:criteria-changed", {
                url: b.href
            })
        }
        _onFilterChanged() {
            let a = new FormData(this.querySelector("#facet-filters-form"))
              , b = new URLSearchParams(a).toString();
            a4(this, "facet:criteria-changed", {
                url: `${window.location.pathname}?${b}`
            })
        }
        _adjustDrawer(a) {
            this.classList.toggle("drawer", a.matches),
            this.classList.toggle("drawer--from-left", a.matches)
        }
    }
    ;
    window.customElements.define("facet-filters", aO);
    var aP = class extends H {
        connectedCallback() {
            super.connectedCallback(),
            this.delegate.on("change", '[name="sort_by"]', this._onSortChanged.bind(this))
        }
        _onSortChanged(c, b) {
            let a = new URL(location.href);
            a.searchParams.set("sort_by", b.value),
            a.searchParams.delete("page"),
            this.open = !1,
            this.dispatchEvent(new CustomEvent("facet:criteria-changed",{
                bubbles: !0,
                detail: {
                    url: a.toString()
                }
            }))
        }
    }
    ;
    window.customElements.define("sort-by-popover", aP);
    var aQ = class extends a6 {
        connectedCallback() {
            this.rootDelegate.on("cart:updated", a=>this.innerText = a.detail.cart.item_count),
            this.rootDelegate.on("cart:refresh", a=>this.innerText = a.detail.cart.item_count)
        }
    }
    ;
    window.customElements.define("cart-count", aQ);
    var aR = class extends au {
        connectedCallback() {
            super.connectedCallback(),
            this.nextReplacementDelay = 0,
            this.rootDelegate.on("cart:refresh", this._rerenderCart.bind(this)),
            this.addEventListener("variant:added", ()=>this.nextReplacementDelay = 600)
        }
        async _rerenderCart(a) {
            var c;
            let d = null
              , b = "";
            if (a.detail && a.detail.cart && a.detail.cart.sections)
                d = a.detail.cart,
                b = a.detail.cart.sections[this.getAttribute("section")] || a.detail.cart.sections["mini-cart"];
            else {
                let e = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${this.getAttribute("section")}`);
                b = await e.text()
            }
            let f = document.createElement("div");
            f.innerHTML = b,
            setTimeout(async()=>{
                var b;
                let e = this.querySelector(".drawer__content").scrollTop;
                if (!!e && d && 0 === d.item_count) {
                    let c = new bP(new bS(Array.from(this.querySelectorAll(".drawer__content, .drawer__footer")).map(a=>new bQ(a,{
                        opacity: [1, 0]
                    },{
                        duration: 250,
                        easing: "ease-in"
                    }))));
                    c.play(),
                    await c.finished
                }
                const cartDrawerElement = f.querySelector("cart-drawer");
                if (cartDrawerElement && cartDrawerElement.innerHTML) {
                    this.innerHTML = cartDrawerElement.innerHTML;
                }
                d && 0 === d.item_count ? this.querySelector(".drawer__content").animate({
                    opacity: [0, 1],
                    transform: ["translateY(40px)", "translateY(0)"]
                }, {
                    duration: 450,
                    easing: "cubic-bezier(0.33, 1, 0.68, 1)"
                }) : this.querySelector(".drawer__content").scrollTop = e,
                (null == (b = null == a ? void 0 : a.detail) ? void 0 : b.openMiniCart) && (this.clientWidth,
                this.open = !0)
            }
            , (null == (c = null == a ? void 0 : a.detail) ? void 0 : c.replacementDelay) || this.nextReplacementDelay),
            this.nextReplacementDelay = 0
        }
        async attributeChangedCallback(e, f, g) {
            if (super.attributeChangedCallback(e, f, g),
            "open" === e && this.open && (this.querySelector(".drawer__content").scrollTop = 0,
            !bI.prefersReducedMotion())) {
                let b = Array.from(this.querySelectorAll(".line-item"))
                  , a = this.querySelector(".mini-cart__recommendations-inner")
                  , c = this.querySelector(".drawer__footer")
                  , d = [];
                a && window.matchMedia(window.themeVariables.breakpoints.pocket).matches && b.push(a),
                b.forEach(a=>a.style.opacity = 0),
                a && (a.style.opacity = 0),
                c && (c.style.opacity = 0),
                d.push(new bS(b.map((b,a)=>new bQ(b,{
                    opacity: [0, 1],
                    transform: ["translateX(40px)", "translateX(0)"]
                },{
                    duration: 400,
                    delay: 400 + 120 * a - Math.min(2 * a * a, 120 * a),
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)"
                })))),
                c && d.push(new bQ(c,{
                    opacity: [0, 1],
                    transform: ["translateY(100%)", "translateY(0)"]
                },{
                    duration: 300,
                    delay: 400,
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)"
                })),
                a && !window.matchMedia(window.themeVariables.breakpoints.pocket).matches && d.push(new bQ(a,{
                    opacity: [0, 1],
                    transform: ["translateX(100%)", "translateX(0)"]
                },{
                    duration: 250,
                    delay: 400 + Math.max(120 * b.length - 25 * b.length, 25),
                    easing: "cubic-bezier(0.25, 1, 0.5, 1)"
                })),
                new bP(new bS(d)).play()
            }
        }
    }
    ;
    window.customElements.define("cart-drawer", aR);
    var aS = class extends HTMLElement {
        async connectedCallback() {
            if(!themeVariables.settings.xoEnabled){
                aS.recommendationsCache[this.productId] || (aS.recommendationsCache[this.productId] = fetch(`${window.themeVariables.routes.productRecommendationsUrl}?product_id=${this.productId}&limit=10&section_id=${this.sectionId}`));
                let c = await aS.recommendationsCache[this.productId]
                  , b = document.createElement("div");
                b.innerHTML = await c.clone().text();
                let a = b.querySelector("cart-drawer-recommendations");
                a && a.hasChildNodes() ? this.innerHTML = a.innerHTML : this.hidden = !0
            }
        }
        get productId() {
            return this.getAttribute("product-id")
        }
        get sectionId() {
            return this.getAttribute("section-id")
        }
    }
      , l = aS;
    b(l, "recommendationsCache", {}),
    window.customElements.define("cart-drawer-recommendations", l);
    var aT = class extends HTMLTextAreaElement {
        connectedCallback() {
            this.addEventListener("change", this._onNoteChanged.bind(this))
        }
        get ownedToggle() {
            return this.hasAttribute("aria-owns") ? document.getElementById(this.getAttribute("aria-owns")) : null
        }
        async _onNoteChanged() {
            this.ownedToggle && (this.ownedToggle.innerHTML = "" === this.value ? window.themeVariables.strings.cartAddOrderNote : window.themeVariables.strings.cartEditOrderNote);
            let a = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
                body: JSON.stringify({
                    note: this.value
                }),
                credentials: "same-origin",
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                }
            })
              , b = await a.json();
            document.documentElement.dispatchEvent(new CustomEvent("cart:updated",{
                bubbles: !0,
                detail: {
                    cart: b
                }
            }))
        }
    }
    ;
    window.customElements.define("cart-note", aT, {
        extends: "textarea"
    });
    var aU = class extends HTMLElement {
        connectedCallback() {
            document.documentElement.addEventListener("cart:updated", this._onCartUpdated.bind(this))
        }
        get threshold() {
            return parseFloat(this.getAttribute("threshold"))
        }
        _onCartUpdated(a) {
            this.style.setProperty("--progress", Math.min(parseFloat(a.detail.cart.total_price) / this.threshold, 1))
        }
    }
    ;
    window.customElements.define("free-shipping-bar", aU);
    var aV = class extends a6 {
        connectedCallback() {
            this.delegate.on("click", "a", this._onQuantityLinkClicked.bind(this)),
            this.delegate.on("change", "input", this._onQuantityChanged.bind(this))
        }
        _onQuantityLinkClicked(a, b) {
            a.preventDefault(),
            this._updateFromLink(b.href)
        }
        _onQuantityChanged(b, a) {
            this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${a.value}&line=${a.getAttribute("data-line")}`)
        }
        async _updateFromLink(d) {
            if ("cart" === window.themeVariables.settings.pageType) {
                window.location.href = d;
                return
            }
            let e = new URL(d,`https://${window.themeVariables.routes.host}`)
              , b = e.searchParams
              , f = b.get("line")
              , g = b.get("id")
              , a = parseInt(b.get("quantity"));
            this.dispatchEvent(new CustomEvent("line-item-quantity:change:start",{
                bubbles: !0,
                detail: {
                    newLineQuantity: a
                }
            }));
            let h = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
                body: JSON.stringify({
                    line: f,
                    id: g,
                    quantity: a,
                    sections: [document.getElementById("mini-cart")?.getAttribute("section") || "mini-cart"]
                }),
                credentials: "same-origin",
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                }
            })
              , c = await h.json();
                  await Shopify.getCart();
            this.dispatchEvent(new CustomEvent("line-item-quantity:change:end",{
                bubbles: !0,
                detail: {
                    cart: c,
                    newLineQuantity: a
                }
            })),
            document.documentElement.dispatchEvent(new CustomEvent("cart:updated",{
                bubbles: !0,
                detail: {
                    cart: c
                }
            })),
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh",{
                bubbles: !0,
                detail: {
                    cart: c,
                    replacementDelay: 0 === a ? 600 : 750
                }
            }))
        }
    }
    ;
    window.customElements.define("line-item-quantity", aV);
    var aW = class extends HTMLElement {
        connectedCallback() {
            this.lineItemLoader = this.querySelector(".line-item__loader"),
            this.addEventListener("line-item-quantity:change:start", this._onQuantityStart.bind(this)),
            this.addEventListener("line-item-quantity:change:end", this._onQuantityEnd.bind(this))
        }
        _onQuantityStart() {
            this.lineItemLoader && (this.lineItemLoader.hidden = !1,
            this.lineItemLoader.firstElementChild.hidden = !1,
            this.lineItemLoader.lastElementChild.hidden = !0)
        }
        async _onQuantityEnd(a) {
            0 !== a.detail.cart.item_count && this.lineItemLoader && (await this.lineItemLoader.firstElementChild.animate({
                opacity: [1, 0],
                transform: ["translateY(0)", "translateY(-10px)"]
            }, 75).finished,
            this.lineItemLoader.firstElementChild.hidden = !0,
            0 === a.detail.newLineQuantity ? (await this.animate({
                opacity: [1, 0],
                height: [`${this.clientHeight}px`, 0]
            }, {
                duration: 300,
                easing: "ease"
            }).finished,
            this.remove()) : (this.lineItemLoader.lastElementChild.hidden = !1,
            await this.lineItemLoader.lastElementChild.animate({
                opacity: [0, 1],
                transform: ["translateY(10px)", "translateY(0)"]
            }, {
                duration: 75,
                endDelay: 300
            }).finished,
            this.lineItemLoader.hidden = !0))
        }
    }
    ;
    window.customElements.define("line-item", aW);
    var aX = class extends a6 {
        connectedCallback() {
            this.rootDelegate.on("cart-notification:show", this._onShow.bind(this), !this.hasAttribute("global")),
            this.delegate.on("click", '[data-action="close"]', a=>{
                a.stopPropagation(),
                this.hidden = !0
            }
            ),
            this.addEventListener("mouseenter", this.stopTimer.bind(this)),
            this.addEventListener("mouseleave", this.startTimer.bind(this)),
            window.addEventListener("pagehide", ()=>this.hidden = !0)
        }
        set hidden(a) {
            a ? this.stopTimer() : this.startTimer(),
            this.toggleAttribute("hidden", a)
        }
        get isInsideDrawer() {
            return this.classList.contains("cart-notification--drawer")
        }
        stopTimer() {
            clearTimeout(this._timeout)
        }
        startTimer() {
            this._timeout = setTimeout(()=>this.hidden = !0, 3e3)
        }
        _onShow(a) {
            if (this.isInsideDrawer && !this.closest(".drawer").open || this.hasAttribute("global") && "success" === a.detail.status && "drawer" === window.themeVariables.settings.cartType)
                return;
            a.stopPropagation();
            let b = "";
            this.isInsideDrawer || (b = `
        <button class="cart-notification__close tap-area hidden-phone" data-action="close">
          <span class="visually-hidden">${window.themeVariables.strings.accessibilityClose}</span>
          <svg focusable="false" width="14" height="14" class="icon icon--close icon--inline" viewBox="0 0 14 14">
            <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
          </svg>
        </button>
      `),
            "success" === a.detail.status ? (this.classList.remove("cart-notification--error"),
            this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M6 10L9 13L14 7" fill="none" stroke="rgb(var(--success-color))" stroke-width="2"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading hidden-phone">${window.themeVariables.strings.cartItemAdded}</span>
                <span class="cart-notification__heading heading hidden-tablet-and-up">${window.themeVariables.strings.cartItemAddedShort}</span>
                <a href="${window.themeVariables.routes.cartUrl}" class="cart-notification__view-cart link">${window.themeVariables.strings.cartViewCart}</a>
              </div>
              
              ${b}
            </div>
          </div>
        </div>
      `) : (this.classList.add("cart-notification--error"),
            this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M9.6748 13.2798C9.90332 13.0555 10.1763 12.9434 10.4937 12.9434C10.811 12.9434 11.0819 13.0555 11.3062 13.2798C11.5347 13.5041 11.6489 13.7749 11.6489 14.0923C11.6489 14.4097 11.5347 14.6847 11.3062 14.9175C11.0819 15.146 10.811 15.2603 10.4937 15.2603C10.1763 15.2603 9.90332 15.146 9.6748 14.9175C9.45052 14.6847 9.33838 14.4097 9.33838 14.0923C9.33838 13.7749 9.45052 13.5041 9.6748 13.2798ZM9.56689 12.1816V5.19922H11.4141V12.1816H9.56689Z" fill="rgb(var(--error-color))"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading">${a.detail.error}</span>
              </div>
              
              ${b}
            </div>
          </div>
        </div>
      `),
            this.clientHeight,
            this.hidden = !1
        }
    }
    ;
    window.customElements.define("cart-notification", aX);
    var aY = class extends HTMLElement {
        connectedCallback() {
            this.submitButton = this.querySelector('[type="button"]'),
            this.submitButton.addEventListener("click", this._estimateShipping.bind(this))
        }
        async _estimateShipping() {
            let a = this.querySelector('[name="shipping-estimator[zip]"]').value
              , b = this.querySelector('[name="shipping-estimator[country]"]').value
              , c = this.querySelector('[name="shipping-estimator[province]"]').value;
            this.submitButton.setAttribute("aria-busy", "true");
            let d = await fetch(`${window.themeVariables.routes.cartUrl}/prepare_shipping_rates.json?shipping_address[zip]=${a}&shipping_address[country]=${b}&shipping_address[province]=${c}`, {
                method: "POST"
            });
            if (d.ok) {
                let e = await this._getAsyncShippingRates(a, b, c);
                this._formatShippingRates(e)
            } else {
                let f = await d.json();
                this._formatError(f)
            }
            this.submitButton.removeAttribute("aria-busy")
        }
        async _getAsyncShippingRates(a, b, c) {
            let e = await fetch(`${window.themeVariables.routes.cartUrl}/async_shipping_rates.json?shipping_address[zip]=${a}&shipping_address[country]=${b}&shipping_address[province]=${c}`)
              , d = await e.text();
            return "null" === d ? this._getAsyncShippingRates(a, b, c) : JSON.parse(d).shipping_rates
        }
        _formatShippingRates(a) {
            var b;
            null == (b = this.querySelector(".shipping-estimator__results")) || b.remove();
            let c = "";
            a.forEach(a=>{
                c += `<li>${a.presentment_name}: ${bV(100 * parseFloat(a.price))}</li>`
            }
            );
            let d = `
      <div class="shipping-estimator__results">
        <p>${0 === a.length ? window.themeVariables.strings.shippingEstimatorNoResults : 1 === a.length ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${"" === c ? "" : `<ul class="unordered-list">${c}</ul>`}
      </div>
    `;
            this.insertAdjacentHTML("beforeend", d)
        }
        _formatError(b) {
            var a;
            null == (a = this.querySelector(".shipping-estimator__results")) || a.remove();
            let c = "";
            Object.keys(b).forEach(a=>{
                c += `<li>${a} ${b[a]}</li>`
            }
            );
            let d = `
      <div class="shipping-estimator__results">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="unordered-list">${c}</ul>
      </div>
    `;
            this.insertAdjacentHTML("beforeend", d)
        }
    }
    ;
    window.customElements.define("shipping-estimator", aY);
    var aZ = class extends HTMLAnchorElement {
        constructor() {
            super(),
            this.addEventListener("click", this._onClick.bind(this))
        }
        _onClick() {
            let a = document.getElementById("shopify-product-reviews");
            a && (window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? a.closest("collapsible-content").open = !0 : document.querySelector(`[aria-controls="${a.closest(".product-tabs__tab-item-wrapper").id}"]`).click())
        }
    }
    ;
    window.customElements.define("review-link", aZ, {
        extends: "a"
    });
    var a$ = class extends HTMLElement {
        connectedCallback() {
            var a;
            null == (a = document.getElementById(this.getAttribute("form-id"))) || a.addEventListener("variant:changed", this._onVariantChanged.bind(this)),
            this.imageElement = this.querySelector(".product-sticky-form__image"),
            this.priceElement = this.querySelector(".product-sticky-form__price"),
            this.unitPriceElement = this.querySelector(".product-sticky-form__unit-price"),
            this._setupVisibilityObservers()
        }
        disconnectedCallback() {
            this.intersectionObserver.disconnect()
        }
        set hidden(a) {
            this.toggleAttribute("hidden", a),
            a ? document.documentElement.style.removeProperty("--cart-notification-offset") : document.documentElement.style.setProperty("--cart-notification-offset", `${this.clientHeight}px`)
        }
        _onVariantChanged(d) {
            let a = d.detail.variant
              , e = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
            if (!a)
                return;
            if (this.priceElement && (this.priceElement.innerHTML = bV(a.price, e)),
            this.unitPriceElement && (this.unitPriceElement.style.display = a.unit_price_measurement ? "block" : "none",
            a.unit_price_measurement)) {
                let c = "";
                1 !== a.unit_price_measurement.reference_value && (c = `<span class="unit-price-measurement__reference-value">${a.unit_price_measurement.reference_value}</span>`),
                this.unitPriceElement.innerHTML = `
          <div class="unit-price-measurement">
            <span class="unit-price-measurement__price">${bV(a.unit_price)}</span>
            <span class="unit-price-measurement__separator">/</span>
            ${c}
            <span class="unit-price-measurement__reference-unit">${a.unit_price_measurement.reference_unit}</span>
          </div>
        `
            }
            if (!this.imageElement || !a || !a.featured_media)
                return;
            let b = a.featured_media;
            b.alt && this.imageElement.setAttribute("alt", b.alt),
            this.imageElement.setAttribute("width", b.preview_image.width),
            this.imageElement.setAttribute("height", b.preview_image.height),
            this.imageElement.setAttribute("src", bL(b, "165x")),
            this.imageElement.setAttribute("srcset", bM(b, [55, 110, 165]))
        }
        _setupVisibilityObservers() {
            let a = document.getElementById("MainPaymentContainer")
              , b = document.querySelector(".shopify-section--footer")
              , c = bE();
            this._isFooterVisible = this._isPaymentContainerPassed = !1,
            this.intersectionObserver = new IntersectionObserver(d=>{
                d.forEach(d=>{
                    if (d.target === b && (this._isFooterVisible = d.intersectionRatio > 0),
                    d.target === a) {
                        let e = a.getBoundingClientRect();
                        this._isPaymentContainerPassed = 0 === d.intersectionRatio && e.top + e.height <= c
                    }
                }
                ),
                window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? this.hidden = !this._isPaymentContainerPassed || this._isFooterVisible : this.hidden = !this._isPaymentContainerPassed
            }
            ,{
                rootMargin: `-${c}px 0px 0px 0px`
            }),
            this.intersectionObserver.observe(a),
            this.intersectionObserver.observe(b)
        }
    }
    ;
    window.customElements.define("product-sticky-form", a$),
    new n,
    Shopify.designMode && document.addEventListener("shopify:section:load", ()=>{
        window.SPR && (window.SPR.initDomEls(),
        window.SPR.loadProducts())
    }
    ),
    window.SPRCallbacks = {
        onFormSuccess(b, a) {
            document.getElementById(`form_${a.id}`).classList.add("spr-form--success")
        }
    },
    (()=>{
        let b = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth
          , a = ()=>{
            let a = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth
              , c = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;
            a !== b && requestAnimationFrame(()=>{
                document.documentElement.style.setProperty("--window-height", c + "px"),
                b = a
            }
            )
        }
        ;
        a(),
        window.visualViewport ? window.visualViewport.addEventListener("resize", a) : window.addEventListener("resize", a)
    }
    )(),
    (()=>{
        let a = new m(document.body);
        a.on("keyup", 'input:not([type="checkbox"]):not([type="radio"]), textarea', (b,a)=>{
            a.classList.toggle("is-filled", "" !== a.value)
        }
        ),
        a.on("change", "select", (b,a)=>{
            a.parentNode.classList.toggle("is-filled", "" !== a.value)
        }
        )
    }
    )(),
    document.querySelectorAll(".rte table").forEach(a=>{
        a.outerHTML = '<div class="table-wrapper">' + a.outerHTML + "</div>"
    }
    ),
    document.querySelectorAll(".rte iframe").forEach(a=>{
        (-1 !== a.src.indexOf("youtube") || -1 !== a.src.indexOf("youtu.be") || -1 !== a.src.indexOf("vimeo")) && (a.outerHTML = '<div class="video-wrapper">' + a.outerHTML + "</div>")
    }
    ),
    new m(document.documentElement).on("click", "[data-smooth-scroll]", (b,c)=>{
        let a = document.querySelector(c.getAttribute("href"));
        a && (b.preventDefault(),
        a.scrollIntoView({
            behavior: "smooth",
            block: "start"
        }))
    }
    ),
    document.addEventListener("keyup", function(a) {
        "Tab" === a.key && (document.body.classList.remove("no-focus-outline"),
        document.body.classList.add("focus-outline"))
    })
}
)();
/*!
* focus-trap 6.7.1
* @license MIT, https://github.com/focus-trap/focus-trap/blob/master/LICENSE
*/
/*!
* tabbable 5.2.1
* @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
*/
